/**
 * term.js - an xterm emulator
 * Copyright (c) 2012-2013, Christopher Jeffrey (MIT License)
 * https://github.com/chjj/term.js
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
 * THE SOFTWARE.
 *
 * Originally forked from (with the author's permission):
 *   Fabrice Bellard's javascript vt100 for jslinux:
 *   http://bellard.org/jslinux/
 *   Copyright (c) 2011 Fabrice Bellard
 *   The original design remains. The terminal itself
 *   has been extended to include xterm CSI codes, among
 *   other features.
 *   
 * Modified by Peter Schellenbach, Zumasys, Inc. for use 
 * in AccuTerm Mobile project. Added several missing
 * VTxxx features, re-worked scrollback, override
 * several functions in subclass module.
 * Modifications Copyright (c) 2015 Zumasys, Inc.
 *   
 */

////////////////////////////////////////////////////////////
// Import Dependencies!
////////////////////////////////////////////////////////////

import { TermUtil } from './termutil.js';
import { DEBUG } from './globals.js';

var TerminalExport;

;(function() {

/**
 * Terminal Emulation References:
 *   http://vt100.net/
 *   http://invisible-island.net/xterm/ctlseqs/ctlseqs.txt
 *   http://invisible-island.net/xterm/ctlseqs/ctlseqs.html
 *   http://invisible-island.net/vttest/
 *   http://www.inwap.com/pdp10/ansicode.txt
 *   http://linux.die.net/man/4/console_codes
 *   http://linux.die.net/man/7/urxvt
 */

'use strict';

/**
 * Shared
 */

var window = this
  , document = this.document;

/**
 * EventEmitter
 */

function EventEmitter() {
  this._events = this._events || {};
}

EventEmitter.prototype.addListener = function(type, listener) {
  this._events[type] = this._events[type] || [];
  this._events[type].push(listener);
};

EventEmitter.prototype.on = EventEmitter.prototype.addListener;

EventEmitter.prototype.removeListener = function(type, listener) {
  if (!this._events[type]) return;

  var obj = this._events[type]
    , i = obj.length;

  while (i--) {
    if (obj[i] === listener || obj[i].listener === listener) {
      obj.splice(i, 1);
      return;
    }
  }
};

EventEmitter.prototype.off = EventEmitter.prototype.removeListener;

EventEmitter.prototype.removeAllListeners = function(type) {
  if (this._events[type]) delete this._events[type];
};

EventEmitter.prototype.once = function(type, listener) {
  function on() {
    var args = Array.prototype.slice.call(arguments);
    this.removeListener(type, on);
    return listener.apply(this, args);
  }
  on.listener = listener;
  return this.on(type, on);
};

EventEmitter.prototype.emit = function(type) {
  if (!this._events[type]) return;

  var args = Array.prototype.slice.call(arguments, 1)
    , obj = this._events[type]
    , l = obj.length
    , i = 0;

  for (; i < l; i++) {
    obj[i].apply(this, args);
  }
};

EventEmitter.prototype.listeners = function(type) {
  return this._events[type] = this._events[type] || [];
};

/**
 * States
 */

var normal = 0
  , escaped = 1
  , csi = 2
  , osc = 3
  , charset = 4
  , dcs = 5
  , ignore = 6
  , decdsc = 7
  , esc_sp;

/**
 * Terminal
 */

function Terminal(options) {
  var self = this;
    
  if (!(this instanceof Terminal)) {
    return new Terminal(arguments[0], arguments[1], arguments[2]);
  }

  EventEmitter.call(this);

  if (typeof options === 'number') {
    options = {
      cols: arguments[0],
      rows: arguments[1],
      handler: arguments[2]
    };
  }

  options = options || {};

  each(keys(Terminal.defaults), function(key) {
    if (options[key] == null) {
      options[key] = Terminal.options[key];
      // Legacy:
      if (Terminal[key] !== Terminal.defaults[key]) {
        options[key] = Terminal[key];
      }
    }
    self[key] = options[key];
  });

  if (options.colors.length === 8) {
    options.colors = options.colors.concat(Terminal._colors.slice(8));
  } else if (options.colors.length === 16) {
    options.colors = options.colors.concat(Terminal._colors.slice(16));
  } else if (options.colors.length === 10) {
    options.colors = options.colors.slice(0, -2).concat(
      Terminal._colors.slice(8, -2), options.colors.slice(-2));
  } else if (options.colors.length === 18) {
    options.colors = options.colors.slice(0, -2).concat(
      Terminal._colors.slice(16, -2), options.colors.slice(-2));
  }
  this.colors = options.colors;

  this.options = options;

  // this.context = options.context || window;
  // this.document = options.document || document;
  this.parent = options.body || options.parent
    || (document ? document.getElementsByTagName('body')[0] : null);

  //PJS: use scrMode to select initial screen geometry, stash normal & extended geometry properties
  this.scrMode = options.scrMode || 0; //0 = 80 column mode, 1 = 132 column mode
  this.normCols = options.normCols || options.cols || options.geometry[0] || 80;
  this.normRows = options.normRows || options.rows || options.geometry[1] || 24;
  this.extCols = options.extCols || 132;
  this.extRows = options.extRows || this.normRows;
  if (this.scrMode) {
	this.cols = this.extCols;
	this.rows = this.extRows;
  } else {
	this.cols = this.normCols;
	this.rows = this.normRows;
  }
  
  if (options.handler) {
    this.on('data', options.handler);
  }

  this.ybase = 0;   // index of live screen row 0 in lines[]
  this.ydisp = 0;   // index of first visible row in lines[] (modified by scrolling display <div>)
  this.x = 0;       // current cursor X position
  this.y = 0;       // current cursor Y position
  this.cursorState = 0; // PJS - this used to be the blink state, but now we can use it to hide cursor if we lose focus, etc. (not currently implemented)
  this.cursorStyle = options.cursorStyle || 0; // PJS - blinking block
  this.cursorHidden = false;
  this.convertEol;
  this.state = 0;
  this.queue = '';
  this.scrollTop = 0;
  this.scrollBottom = this.rows - 1;
  this.curPage = 0; // PJS
  this.curScrollPos = 0; // PJS
  this.inHandleCursor = false; // PJS

  // modes (PJS: initialize from options)
  this.allowApplicationMode = options.allowApplicationMode || false;
  this.applicationKeypad = this.allowApplicationMode & options.applicationKeypad || false;
  this.applicationCursor = this.allowApplicationMode & options.applicationCursor || false;
  this.wraparoundMode = options.wraparoundMode || false;
  this.savedAutoWrap = this.wraparoundMode; // PJS: save/restore cursor must save autowrap mode
  this.eightBitControls = options.eightBitControls || false;
  this.backspaceDel = options.backspaceDel || false; // PJS - DECBKM  
  this.answerBack = options.answerBack; // PJS
  this.scrollback = options.scrollback || 0; // PJS
  this.originMode = false;
  this.savedOrigin = false; // PJS: save/restore cursor must save origin mode
  this.insertMode = false;
  this.kbdLock = false; // PJS
  this.delayWrap = false; // PJS - ANSI delayed wrap
  this.allowUnderline = options.hasOwnProperty('allowUnderline') ? options.allowUnderline : true;
  this.allowBlinking = options.hasOwnProperty('allowBlinking') ? options.allowBlinking : true;
  this.normal = null;

  this.bkgndPict = options.bkgndPict || ''; // PJS
  this.bkgndAlpha = options.bkgndAlpha || 0; // PJS
  this.bkgndMode = options.bkgndMode || 0; // PJS
  //
  // PJS: add support for DECSCL
  //  Iinitialize the terminal conformance level (bitmask)
  //  bits: 1=vt100, 2=vt220, 4=vt320, 8=vt420, 16=linux, 32=xterm
  this.tmask = 0x01; // default to VT100 conformance level
  if (this.termName === 'VT100') this.tmask |= 0x01;
  if (this.termName === 'VT220') this.tmask |= 0x03;
  if (this.termName === 'VT320') this.tmask |= 0x07;
  if (this.termName === 'VT420') this.tmask |= 0x0f;
  if (this.termName === 'LINUX' || this.termName === 'linux') this.tmask |= 0x1f;
  if (this.termName === 'XTERM' || this.termName === 'xterm') this.tmask |= 0x3f;
  
  // select modes
  this.prefixMode = false;
  this.selectMode = false;
  this.visualMode = false;
  this.searchMode = false;
  this.searchDown;
  this.entry = '';
  this.entryPrefix = 'Search: ';
  this._real;
  this._selected;
  this._textarea;

  // charset
  this.charset = null;
  this.gcharset = null;
  this.glevel = 0; // the gcharset assigned to GL
  this.grlevel = 0; //PJS: the gcharset assigned to GR
  this.scsint = null; //PJS: SCS intermediate character - to differentiate between 94 & 96 character set, for example, ESC ( A vs ESC / A
  this.savedgLevel = 0; // PJS: save/restore cursor must save glevel
  this.savedgrLevel = 0; //PJS: save/restore cursor must save grlevel
  this.singleShift = null; // PJS: implement sigle-shift mode
  this.savedShift = null; // PJS: save/restore cursor must save single-shift mode
  this.charsets = [null];
  this.savedCharsets = [null]; // PJS: save/restore cursor must save character sets

  // mouse properties
  this.decLocator;
  this.x10Mouse;
  this.vt200Mouse;
  this.vt300Mouse;
  this.normalMouse;
  this.mouseEvents;
  this.sendFocus;
  this.utfMouse;
  this.sgrMouse;
  this.urxvtMouse;

  // misc
  this.element; // DOM div element containing live screen rows
  this.children;
  this.viewport; //PJS: wrapper div for scrollable main-terminal div
  this.mainterminal; //PJS wrapper div for element & history divs
  this.refreshStart;
  this.refreshEnd;
  this.savedX;
  this.savedY;
//PJS  this.savedCols; //PJS: use scrMode to indicate normal (80 column) or extended (132 column) geometry
  this.history; // PJS: DOM div element containing history rows (scrollback buffer contents)
  this.historyRows = 0; // PJS: number of rows in history div
  this.pendingScrollback = 0; // PJS: number of lines that have been scrolled off top of the screen
  this.screenPages = this.options.screenPages || 1; // PJS: number of pages allocated
  this.screenPage = []; // PJS: page memory
  // stream
  this.readable = true;
  this.writable = true;

  this.defAttr = (0 << 18) | (257 << 9) | (256 << 0);
  this.curAttr = this.defAttr;
  this.savedAttr = this.curAttr; // PJS: save/restore cursor must save current attribute
  this.reverseVideo = false; //PJS - added support for DECSCNM
  this.protMode = false; //PJS - set if DECSCA protect mode set
  this.savedProt = false; // PJS: save/restore cursor must save protect mode
  
  this.params = [];
  this.currentParam = 0;
  this.prefix = '';
  this.postfix = '';

  this.lines = [];
  var i = this.rows;
  while (i--) {
    this.lines.push(this.blankLine());
  }

  this.tabs;
  this.setupStops();

  // PJS: initialize font properties
  this.fontName = TermUtil.GetFontFamily(this.options.fontName); // get font-family style
  this.fontPoints = this.options.fontSize || 10; // points
  this.fontProps = {fontSize: 14}; // pixels
  
  // PJS: initialize the keyboard keys based on application mode, 8bit controls & backspace option
  if (this.initDfltKbd) {
    this.initDfltKbd(this.applicationCursor, this.applicationKeypad, this.backspaceDel, this.eightBitControls);
  }

}

inherits(Terminal, EventEmitter);

// back_color_erase feature for xterm.
Terminal.prototype.eraseAttr = function() {
  // if (this.is('screen')) return this.defAttr;
  /* PJS: commented out following line & replaced with "return this.defAttr"
   * Could not get xterm to behave the way this was originally implemented,
   * and DEC terminals use the screen attrbite when erasing.
  return (this.defAttr & ~0x1ff) | (this.curAttr & 0x1ff);
   */
  return this.defAttr; // PJS
};

/**
 * Colors
 */

// Colors 0-15
Terminal.tangoColors = [
  // dark:
  '#2e3436',
  '#cc0000',
  '#4e9a06',
  '#c4a000',
  '#3465a4',
  '#75507b',
  '#06989a',
  '#d3d7cf',
  // bright:
  '#555753',
  '#ef2929',
  '#8ae234',
  '#fce94f',
  '#729fcf',
  '#ad7fa8',
  '#34e2e2',
  '#eeeeec'
];

Terminal.xtermColors = [
  // dark:
  '#000000', // black
  '#cd0000', // red3
  '#00cd00', // green3
  '#cdcd00', // yellow3
  '#0000ee', // blue2
  '#cd00cd', // magenta3
  '#00cdcd', // cyan3
  '#e5e5e5', // gray90
  // bright:
  '#7f7f7f', // gray50
  '#ff0000', // red
  '#00ff00', // green
  '#ffff00', // yellow
  '#5c5cff', // rgb:5c/5c/ff
  '#ff00ff', // magenta
  '#00ffff', // cyan
  '#ffffff'  // white
];

// Colors 0-15 + 16-255
// Much thanks to TooTallNate for writing this.
Terminal.colors = (function() {
  var colors = Terminal.tangoColors.slice()
    , r = [0x00, 0x5f, 0x87, 0xaf, 0xd7, 0xff]
    , i;

  // 16-231
  i = 0;
  for (; i < 216; i++) {
    out(r[(i / 36) % 6 | 0], r[(i / 6) % 6 | 0], r[i % 6]);
  }

  // 232-255 (grey)
  i = 0;
  for (; i < 24; i++) {
    r = 8 + i * 10;
    out(r, r, r);
  }

  function out(r, g, b) {
    colors.push('#' + hex(r) + hex(g) + hex(b));
  }

  function hex(c) {
    c = c.toString(16);
    return c.length < 2 ? '0' + c : c;
  }

  return colors;
})();

// Default BG/FG
Terminal.colors[256] = '#000000';
Terminal.colors[257] = '#f0f0f0';

Terminal._colors = Terminal.colors.slice();

Terminal.vcolors = (function() {
  var out = []
    , colors = Terminal.colors
    , i = 0
    , color;

  for (; i < 256; i++) {
    color = parseInt(colors[i].substring(1), 16);
    out.push([
      (color >> 16) & 0xff,
      (color >> 8) & 0xff,
      color & 0xff
    ]);
  }

  return out;
})();

/**
 * Options
 */

Terminal.defaults = {
  colors: Terminal.colors,
  convertEol: false,
  termName: 'xterm',
  geometry: [80, 24],
  cursorStyle: 0, //PJS blinking block
  visualBell: false,
  popOnBell: false,
  scrollback: 0,
  screenKeys: false,
  debug: false,
  useStyle: false,
  allowApplicationMode: true, //PJS
  applicationCursor: false, //PJS
  applicationKeypad: false, //PJS
  backspaceDel: true, //PJS
  eightBitControls: false, //PJS
  screenPages: 1, //PJS
  wraparoundMode: false, //PJS
  normCols: 80, //PJS
  normRows: 24, //PJS
  extCols: 132, //PJS
  extRows: 24, //PJS
  scrMode: 0, //PJS 0 = 80 column, 1 = 132 column
  answerBack: '', //PJS
  allowUnderline: true, //PJS
  allowBlinking: true //PJS
  // programFeatures: false,
  // focusKeys: false,
};

Terminal.options = {};

each(keys(Terminal.defaults), function(key) {
  Terminal[key] = Terminal.defaults[key];
  Terminal.options[key] = Terminal.defaults[key];
});

/**
 * Focused Terminal
 */

Terminal.focus = null;

Terminal.prototype.focus = function() {
	
  DEBUG&&console.log('caught Terminal.prototype.focus');
  if (Terminal.focus === this) return;

  if (Terminal.focus) {
    Terminal.focus.blur();
  }

  if (this.sendFocus) this.send('\x1b[I');
  this.showCursor();

  // try {
  //   this.element.focus();
  // } catch (e) {
  //   ;
  // }

  // this.emit('focus');

  Terminal.focus = this;
};

Terminal.prototype.blur = function() {
  if (Terminal.focus !== this) return;

  this.cursorState = 0;
  this.refresh(this.y, this.y);
  if (this.sendFocus) this.send('\x1b[O');

  // try {
  //   this.element.blur();
  // } catch (e) {
  //   ;
  // }

  // this.emit('blur');

  Terminal.focus = null;
};

/**
 * Initialize global behavior
 */

Terminal.prototype.initGlobal = function() {
  var document = this.document;

  Terminal._boundDocs = Terminal._boundDocs || [];
  if (~indexOf(Terminal._boundDocs, document)) {
    return;
  }
  Terminal._boundDocs.push(document);

  Terminal.bindPaste(document);

  Terminal.bindKeys(document);

  Terminal.bindCopy(document);

/* PJS - removed - using input box outside of terminal for virtual keyboard
  if (this.isMobile) {
    this.fixMobile(document);
  }
*/

  if (this.useStyle) {
  	this.fontProps = this.getFontProps(this.fontName, this.fontPoints, this.cols, this.rows); //PJS: get our font properties
    if (this.fontProps) {
      Terminal.insertStyle(document, this.colors[256], this.colors[257], this.fontName, this.fontProps.fontSize, this.fontProps.scaleX, this.fontProps.scaleY, this.fontProps.fontHeight, this.bkgndPict, this.bkgndAlpha, this.bkgndMode); //PJS: pass font size & scale arguments & background image
    }
	  this.resizeTerminalViewport();
  }
  var window = document.defaultView;
  
  /* PJS - for accuterm.io, app will call termapp.containerResize() when window is resized,
   * but save this function in case needed for mobile version in the future.
  on(window, 'resize', throttle(function() {
    var headerHeight, footerHeight, containerHeight;
    headerHeight = $("#terminal-header").height();
    footerHeight = $("#terminal-footer").height();
    containerHeight = $(window).height() - headerHeight - footerHeight;
    $("#terminal").css('height', containerHeight+'px');
    this.resizeTerminalViewport();
  	this.scrollViewToCursorPos();
  }.bind(this), 50));
*/
};
 

/**
 * Bind to paste event
 */

Terminal.bindPaste = function(document) {
  // This seems to work well for ctrl-V and middle-click,
  // even without the contentEditable workaround.
  var window = document.defaultView;
  on(window, 'paste', function(ev) {
    var term = Terminal.focus;
    if (!term) return;
    if (ev.clipboardData) {
      term.send(ev.clipboardData.getData('text/plain'));
    } else if (term.context.clipboardData) {
      term.send(term.context.clipboardData.getData('Text'));
    }
    // Not necessary. Do it anyway for good measure.
    term.element.contentEditable = 'inherit';
    return cancel(ev);
  });
  
  /*
  $('#fake_text').on('functionKey', function(ev, funcVal) {
	DEBUG&&console.log('this function is not getting called');
	this.focus();
	var term = Terminal.focus;
    if (!term) return;
    term.send(funcVal);
	
    // Not necessary. Do it anyway for good measure.
    term.element.contentEditable = 'inherit';
    return cancel(ev);
  });
  */	
};


/**
 * Global Events for key handling
 */

Terminal.bindKeys = function(document) {
  // We should only need to check `target === body` below,
  // but we can check everything for good measure.
  on(document, 'keydown', function(ev) {
	if (!Terminal.focus) return;
	//PJS 03-15-16: Android scrolls view when focus set to terminal, need keyboard events no matter who has keyboard focus
/*  var target = ev.target || ev.srcElement;
    if (!target) return;
    if (target === Terminal.focus.element
        || target === Terminal.focus.context
        || target === Terminal.focus.document
        || target === Terminal.focus.body
        || target === Terminal._textarea
        || target === Terminal.focus.parent) */ {
      return Terminal.focus.keyDown(ev);
    }
  }, true);

  on(document, 'keypress', function(ev) {
    if (!Terminal.focus) return;
	//PJS 03-15-16: Android scrolls view when focus set to terminal, need keyboard events no matter who has keyboard focus
/*  var target = ev.target || ev.srcElement;
    if (!target) return;
    if (target === Terminal.focus.element
        || target === Terminal.focus.context
        || target === Terminal.focus.document
        || target === Terminal.focus.body
        || target === Terminal._textarea
        || target === Terminal.focus.parent) */ {
      return Terminal.focus.keyPress(ev);
    }
  }, true);

  // If we click somewhere other than a
  // terminal, unfocus the terminal.
  on(document, 'mousedown', function(ev) {
    if (!Terminal.focus) return;

    var el = ev.target || ev.srcElement;
    if (!el) return;

	// PJS - check if focus is in fake textarea & treat as if it were part of terminal
	if(el === Terminal._textarea) return;
	
	// PJS - treat container divs same as terminal
    do {
      if (el === Terminal.focus.element || el === this.viewport || el === this.mainterminal) return;
    } while (el = el.parentNode);
	
    Terminal.focus.blur();
  });
};

/**
 * Copy Selection w/ Ctrl-C (Select Mode)
 */

Terminal.bindCopy = function(document) {
  var window = document.defaultView;

  // if (!('onbeforecopy' in document)) {
  //   // Copies to *only* the clipboard.
  //   on(window, 'copy', function fn(ev) {
  //     var term = Terminal.focus;
  //     if (!term) return;
  //     if (!term._selected) return;
  //     var text = term.grabText(
  //       term._selected.x1, term._selected.x2,
  //       term._selected.y1, term._selected.y2);
  //     term.emit('copy', text);
  //     ev.clipboardData.setData('text/plain', text);
  //   });
  //   return;
  // }

  // Copies to primary selection *and* clipboard.
  // NOTE: This may work better on capture phase,
  // or using the `beforecopy` event.
  on(window, 'copy', function(ev) {
    var term = Terminal.focus;
    if (!term) return;
    if (!term._selected) return;
    var textarea = term.getCopyTextarea();
    var text = term.grabText(
      term._selected.x1, term._selected.x2,
      term._selected.y1, term._selected.y2);
    term.emit('copy', text);
    textarea.focus();
    textarea.textContent = text;
    textarea.value = text;
    textarea.setSelectionRange(0, text.length);
    setTimeout(function() {
      term.element.focus();
      term.focus();
    }, 1);
  });
};

/**
 * Fix Mobile
 */

/* PJS - removed - using input box outside of terminal for virtual keyboard
Terminal.prototype.fixMobile = function(document) {
  var self = this;

  var textarea = document.createElement('textarea');
  textarea.style.position = 'absolute';
  textarea.style.left = '-32000px';
  textarea.style.top = '-32000px';
  textarea.style.width = '0px';
  textarea.style.height = '0px';
  textarea.style.opacity = '0';
  textarea.style.backgroundColor = 'transparent';
  textarea.style.borderStyle = 'none';
  textarea.style.outlineStyle = 'none';
  textarea.autocapitalize = 'none';
  textarea.autocorrect = 'off';

  document.getElementsByTagName('body')[0].appendChild(textarea);

  Terminal._textarea = textarea;

  setTimeout(function() {
    textarea.focus();
  }, 1000);
  if (this.isAndroid) {
    on(textarea, 'change', function() {
      var value = textarea.textContent || textarea.value;
      textarea.value = '';
      textarea.textContent = '';
      self.send(value + '\r');
    });
  }
};
*/

/**
 * Insert a default style
 */

//PJS: added new fontName, fontSize, scaleX & scaleY arguments to handle scaleable fonts.
//PJS: added background picture URL & opacity.
//PJS TODO - except for .terminal div span and z-index is here to see if it fixes ATWeb on
// Safari (a few z-index: -1 are original). Ditto for .terminal min-width.
Terminal.insertStyle = function(document, bg, fg, fontName, fontSize, fontScaleX, fontScaleY, lineHeight, bkgndPict, bkgndAlpha, bkgndMode) {
  
  //PJS: create font style - if font needs to be scaled, create the transform
  fontName = fontName || 'monospace';
  fontSize = fontSize || 14; // default to 14px if not specified
  lineHeight = lineHeight || Math.round(fontSize * 1.3);
  var fontStyle = '';
  fontStyle += '  font-family: ' + fontName + ';\n';
  fontStyle += '  font-size: ' + fontSize +  'px;\n';
  fontStyle += '  line-height: ' + lineHeight + 'px;\n';
  var fontScale = '';
  if (fontScaleX && fontScaleY) {
	  if (fontScaleX !== 1 || fontScaleY !== 1) {
		fontScale += '  -webkit-transform: scale(' + fontScaleX + ',' + fontScaleY + ');\n';
		fontScale += '  transform: scale(' + fontScaleX + ',' + fontScaleY + ');\n';
		fontScale += '  transform-origin: left top;\n';
		fontScale += '  -webkit-transform-origin: left top;\n';
	  }
  }
    
  //PJS: background picture opacity / transparency
  var pict = ''
	, bg_opaq = bg
	, bg_tran = bg;
	
  if (bkgndPict) {
	pict = " url('" + bkgndPict + "') center center no-repeat";
	var bg_r = parseInt(bg.substring(1, 3), 16);
	var bg_g = parseInt(bg.substring(3, 5), 16);
	var bg_b = parseInt(bg.substring(5, 7), 16);
	var bg_a_opaq = (bkgndAlpha / 100).toFixed(3);
	var bg_a_tran = ((100 - bkgndAlpha) / 100).toFixed(3);
	bg_opaq = 'rgba(' + bg_r + ',' + bg_g + ',' + bg_b + ',' + bg_a_opaq + ')';
	bg_tran = 'rgba(' + bg_r + ',' + bg_g + ',' + bg_b + ',' + bg_a_tran + ')';
  }

    // Set the background picture mode
    var bgSize = '100% 100%';
    if (bkgndMode == 0) {
        bgSize = '100% 100%';
    } else if (bkgndMode == 1) {
        bgSize = 'contain';
    } else if (bkgndMode == 2) {
        bgSize = 'cover';
    }

  // for blinking cursor, use rgba() for Edge compatibility
  var fg_opaq = 'rgba(' + parseInt(fg.substring(1, 3), 16) + ', ' + parseInt(fg.substring(3, 5), 16) + ', ' + parseInt(fg.substring(5, 7), 16) + ', 1)';
  var fg_tran = 'rgba(' + parseInt(fg.substring(1, 3), 16) + ', ' + parseInt(fg.substring(3, 5), 16) + ', ' + parseInt(fg.substring(5, 7), 16) + ', 0)';

  var style = document.getElementById('term-style');  
  //PJS: allow insertStyle to update existing term-style element
  //if (style) return;
  var mustCreate = !style;
  if (mustCreate) {
    var head = document.getElementsByTagName('head')[0];
    if (!head) return;
    var style = document.createElement('style');
    style.id = 'term-style';
  }

  //PJS: merged css .terminal & .history styles into this function
  
  // textContent doesn't work well with IE for <style> elements.
  
  // PJS: this is different from the original term.js. Instead of
  // adding only an element <div>, we now add terminal-viewport and
  // main-terminal <div>s, so we need to include the css styles here.

  style.innerHTML = ''

    + '#terminal {\n'
    + '  background: ' + bg_opaq + pict + ';\n' // background picture URL & alpha opacity
    + '  background-size: ' + bgSize +';\n'
    + '}\n'
    + '\n'

    + '.terminal-viewport {\n'
    + '  background-color: ' + bg_tran + ';\n' // background picture transparency
    + '}\n'
    + '\n'

	+ '.terminal {\n'
/*	+ '  border: ' + bg + ' solid 5px;\n' */
	+ '  color: ' + fg + ';\n'
	+ fontStyle
	+ '}\n'
	+ '\n'

	+ '.terminal div {\n'
    + fontScale
	+ '}\n'
	+ '\n'



	+ '.history {\n'
/*  + '  border: ' + bg + ' solid 5px;\n' */
	+ '  color: ' + fg + ';\n'
/*	+ '  background-color: ' + bg + ';\n' */
	+ fontStyle
	+ '}\n'
	+ '\n'


	+ '.history div {\n'
    + fontScale
	+ '}\n'
	+ '\n'


	+ '.terminal-cursor {\n'
	+ '  color: ' + bg + ';\n'
	+ '  background-color: ' + fg + ';\n'
	+ '}\n'
	+ '\n'

	+ '.reverse-video {\n'
	+ '  color: ' + bg + ';\n'
	+ '  background-color: ' + fg + ';\n'
	+ '}\n'
    + '\n'

/*	+ '.terminal-cursor-reverse {\n'
	+ '  color: ' + fg + ';\n'
*//*	+ '  background-color: ' + bg + ';\n' *//*
	+ '}\n'
	+ '\n'*/

    + '.text-underline {\n'
    + '  box-shadow: inset 0 -3px 0 0 ' + fg + ';\n'
    + '}\n'

    + '.terminal-cursor-blink {\n'
    + '  color: ' + bg + ';\n'
    + '}\n'
    + '\n'

    + '@keyframes blink-cursor {\n'
    + '  0%, 100% { background-color: ' + fg_tran + '; }\n'
    + '  50% { background-color: ' + fg_opaq + '; }\n'
    + '}\n'

    + '@keyframes blink-underline-cursor {\n'
      + '  0%, 100% { box-shadow: inset 0 -3px 0 0 ' + fg_tran + '; }\n'
      + '  50% { box-shadow: inset 0 -3px 0 0 ' + fg_opaq + '; }\n'
    + '}\n'

	/*
	 * alternative double-high styles (top & bottom half don't always align properly)
	 *
	+ '.terminal-double-high-top {\n'
	+ '  top: ' + fontSize + 'px;\n'
	+ '}\n'
	+ '\n'
	+ '.terminal-double-wide-high-top {\n'
	+ '  top: ' + fontSize + 'px;\n'
	+ '}\n'
	+ '\n'*/
	
	+ '\n'
	;

  // var out = '';
  // each(Terminal.colors, function(color, i) {
  //   if (i === 256) {
  //     out += '\n.term-bg-color-default { background-color: ' + color + '; }';
  //   }
  //   if (i === 257) {
  //     out += '\n.term-fg-color-default { color: ' + color + '; }';
  //   }
  //   out += '\n.term-bg-color-' + i + ' { background-color: ' + color + '; }';
  //   out += '\n.term-fg-color-' + i + ' { color: ' + color + '; }';
  // });
  // style.innerHTML += out + '\n';
  
  //PJS: only insert if we just created the element
  if (mustCreate) {
    head.insertBefore(style, head.firstChild);
    //head.insertBefore(style, null);
  }
};

/**
 * Open Terminal
 */

Terminal.prototype.open = function(parent) {
  var self = this
    , i = 0
    , div;

  this.parent = parent || this.parent;

  if (!this.parent) {
    throw new Error('Terminal requires a parent element.');
  }

  // Grab global elements.
  this.context = this.parent.ownerDocument.defaultView;
  this.document = this.parent.ownerDocument;
  this.body = this.document.getElementsByTagName('body')[0];

  // Parse user-agent strings.
  if (this.context.navigator && this.context.navigator.userAgent) {
    this.isMac = !!~this.context.navigator.userAgent.indexOf('Mac');
    this.isIpad = !!~this.context.navigator.userAgent.indexOf('iPad');
    this.isIphone = !!~this.context.navigator.userAgent.indexOf('iPhone');
    this.isAndroid = !!~this.context.navigator.userAgent.indexOf('Android');
    this.isMobile = this.isIpad || this.isIphone || this.isAndroid;
    this.isMSIE = !!~this.context.navigator.userAgent.indexOf('MSIE');
	//stash Android version to accomodate special Android behavior (PJS 03-15-16)
	var match = this.context.navigator.userAgent.match(/Android\s([0-9\.]*)/i);
	this.androidVersion = match ? parseFloat(match[1]) : 0.0;
  }

  // PJS: this differs from the original term.js. Instead of creating
  // a single element div, we now create 4 divs. The outer div
  // (terminal-viewport) uses overflow-y=scroll to enable scrolling of
  // history + live screen. This is wrapped by the main-terminal div
  // which wraps the history and element divs, and uses overflow-y=visible
  // so that the height of the main-terminal div can be calculated and
  // scroll position set precicesly.
  
  // Create the outer terminal-viewport div.
  this.viewport = this.document.createElement('div');
  this.viewport.id = 'terminal-viewport';
  this.viewport.className = 'terminal-viewport';
  
  // Create the inner main-terminal div.
  this.mainterminal = this.document.createElement('div');
  this.mainterminal.id = 'main-terminal';
  this.mainterminal.className = 'main-terminal';
  
  // Create our main terminal element.
  this.element = this.document.createElement('div');
  this.element.className = 'terminal';
  this.element.style.outline = 'none';
  this.element.setAttribute('tabindex', 0);
  this.element.setAttribute('spellcheck', 'false');
  //this.element.style.backgroundColor = this.colors[256];
  //this.element.style.color = this.colors[257];

//***************************************************//

    // Image container
    this.terminalImageContainer = this.document.createElement('div');
    this.terminalImageContainer.className = 'terminal-image-wrap';
    this.element.appendChild(this.terminalImageContainer);
	
//***************************************************//

  //PJS: create our history element.
  this.history = this.document.createElement('div');
  this.history.className = 'history';
  this.history.style.outline = 'none';
  //this.history.style.backgroundColor = this.colors[256];
  //this.history.style.color = this.colors[257];
  this.historyRows = 0;
  // Create the lines for our terminal.
  this.children = [];
  for (; i < this.rows; i++) {
    div = this.document.createElement('div');
    this.element.appendChild(div);
    this.children.push(div);
  }
  
  // Nest the viewport, mainterminal, element & history divs
  this.viewport.appendChild(this.mainterminal);
  this.mainterminal.appendChild(this.history); //PJS: history (scrollback) before main terminal screen
  this.mainterminal.appendChild(this.element);
  this.parent.appendChild(this.viewport);

    //RDM experimental code to handle browser which does not render css pseudo elements
  /*
  this.floatClear = this.document.createElement('div');
  this.floatClear.style.clear = 'both';
  this.parent.appendChild(this.floatClear);
*/

  // Draw the screen.
  this.refresh(0, this.rows - 1);
  this.resetRange(); //PJS: reset the refresh range after initial display
  
  // Initialize global actions that
  // need to be taken on the document.
  this.initGlobal();

  // Ensure there is a Terminal.focus.
  this.focus();

  // Bind to DOM events related
  // to focus and paste behavior.
  on(this.element, 'focus', function() {
    DEBUG&&console.log('caught terminal.div focus');
    self.focus();
/* PJS - removed - using input box outside of terminal for virtual keyboard
    if (self.isMobile) {
      Terminal._textarea.focus();
    }
*/
  });

  // This causes slightly funky behavior.
  // on(this.element, 'blur', function() {
  //   self.blur();
  // });

  on(this.element, 'mousedown', function() {
    self.focus();
  });

  // Clickable paste workaround, using contentEditable.
  // This probably shouldn't work,
  // ... but it does. Firefox's paste
  // event seems to only work for textareas?
  on(this.element, 'mousedown', function(ev) {
    var button = ev.button != null
      ? +ev.button
      : ev.which != null
        ? ev.which - 1
        : null;

    // Does IE9 do this?
    if (self.isMSIE) {
      button = button === 1 ? 0 : button === 4 ? 1 : button;
    }

    if (button !== 2) return;

    self.element.contentEditable = 'true';
    setTimeout(function() {
      self.element.contentEditable = 'inherit'; // 'false';
    }, 1);
  }, true);

  // Listen for mouse events and translate
  // them into terminal mouse protocols.
  this.bindMouse();

  // Figure out whether boldness affects
  // the character width of monospace fonts.
  if (Terminal.brokenBold == null) {
    Terminal.brokenBold = isBoldBroken(this.document);
  }

  // this.emit('open');

  // This can be useful for pasting,
  // as well as the iPad fix.
  setTimeout(function() {
    self.element.focus();
  }, 100);
};

// XTerm mouse events
// http://invisible-island.net/xterm/ctlseqs/ctlseqs.html#Mouse%20Tracking
// To better understand these
// the xterm code is very helpful:
// Relevant files:
//   button.c, charproc.c, misc.c
// Relevant functions in xterm/button.c:
//   BtnCode, EmitButtonCode, EditorButton, SendMousePosition
Terminal.prototype.bindMouse = function() {
  var el = this.element
    , self = this
    , pressed = 32;
/*	
  DEBUG&&console.log('bindMouse was called on element ' + el.id);
  on(el, 'touchstart', function(ev) {
	DEBUG&&console.log('touchstart: '+ev);
  });
  on(el, 'touchend', function(ev) {
	DEBUG&&console.log('touchend: '+ev);
  });
}*/

  var wheelEvent = 'onmousewheel' in this.context
    ? 'mousewheel'
    : 'DOMMouseScroll';

  // mouseup, mousedown, mousewheel
  // left click: ^[[M 3<^[[M#3<
  // mousewheel up: ^[[M`3>
  function sendButton(ev) {
    var button
      , pos;

    // get the xterm-style button
    button = getButton(ev);

    // get mouse coordinates
    pos = getCoords(ev);
    if (!pos) return;

    sendEvent(button, pos);

    switch (ev.type) {
      case 'mousedown':
        pressed = button;
        break;
      case 'mouseup':
        // keep it at the left
        // button, just in case.
        pressed = 32;
        break;
      case wheelEvent:
        // nothing. don't
        // interfere with
        // `pressed`.
        break;
    }
  }

  // motion example of a left click:
  // ^[[M 3<^[[M@4<^[[M@5<^[[M@6<^[[M@7<^[[M#7<
  function sendMove(ev) {
    var button = pressed
      , pos;

    pos = getCoords(ev);
    if (!pos) return;

    // buttons marked as motions
    // are incremented by 32
    button += 32;

    sendEvent(button, pos);
  }

  // encode button and
  // position to characters
  function encode(data, ch) {
    if (!self.utfMouse) {
      if (ch === 255) return data.push(0);
      if (ch > 127) ch = 127;
      data.push(ch);
    } else {
      if (ch === 2047) return data.push(0);
      if (ch < 127) {
        data.push(ch);
      } else {
        if (ch > 2047) ch = 2047;
        data.push(0xC0 | (ch >> 6));
        data.push(0x80 | (ch & 0x3F));
      }
    }
  }

  // send a mouse event:
  // regular/utf8: ^[[M Cb Cx Cy
  // urxvt: ^[[ Cb ; Cx ; Cy M
  // sgr: ^[[ Cb ; Cx ; Cy M/m
  // vt300: ^[[ 24(1/3/5)~ [ Cx , Cy ] \r
  // locator: CSI P e ; P b ; P r ; P c ; P p & w
  function sendEvent(button, pos) {
    // self.emit('mouse', {
    //   x: pos.x - 32,
    //   y: pos.x - 32,
    //   button: button
    // });

    if (self.vt300Mouse) {
      // NOTE: Unstable.
      // http://www.vt100.net/docs/vt3xx-gp/chapter15.html
      button &= 3;
      pos.x -= 32;
      pos.y -= 32;
      var data = '\x1b[24';
      if (button === 0) data += '1';
      else if (button === 1) data += '3';
      else if (button === 2) data += '5';
      else if (button === 3) return;
      else data += '0';
      data += '~[' + pos.x + ',' + pos.y + ']\r';
      self.send(data);
      return;
    }

    if (self.decLocator) {
      // NOTE: Unstable.
      button &= 3;
      pos.x -= 32;
      pos.y -= 32;
      if (button === 0) button = 2;
      else if (button === 1) button = 4;
      else if (button === 2) button = 6;
      else if (button === 3) button = 3;
      self.send('\x1b['
        + button
        + ';'
        + (button === 3 ? 4 : 0)
        + ';'
        + pos.y
        + ';'
        + pos.x
        + ';'
        + (pos.page || 0)
        + '&w');
      return;
    }

    if (self.urxvtMouse) {
      pos.x -= 32;
      pos.y -= 32;
      pos.x++;
      pos.y++;
      self.send('\x1b[' + button + ';' + pos.x + ';' + pos.y + 'M');
      return;
    }

    if (self.sgrMouse) {
      pos.x -= 32;
      pos.y -= 32;
      self.send('\x1b[<'
        + ((button & 3) === 3 ? button & ~3 : button)
        + ';'
        + pos.x
        + ';'
        + pos.y
        + ((button & 3) === 3 ? 'm' : 'M'));
      return;
    }

    var data = [];

    encode(data, button);
    encode(data, pos.x);
    encode(data, pos.y);

    self.send('\x1b[M' + String.fromCharCode.apply(String, data));
  }

  function getButton(ev) {
    var button
      , shift
      , meta
      , ctrl
      , mod;

    // two low bits:
    // 0 = left
    // 1 = middle
    // 2 = right
    // 3 = release
    // wheel up/down:
    // 1, and 2 - with 64 added
    switch (ev.type) {
      case 'mousedown':
        button = ev.button != null
          ? +ev.button
          : ev.which != null
            ? ev.which - 1
            : null;

        if (self.isMSIE) {
          button = button === 1 ? 0 : button === 4 ? 1 : button;
        }
        break;
      case 'mouseup':
        button = 3;
        break;
      case 'DOMMouseScroll':
        button = ev.detail < 0
          ? 64
          : 65;
        break;
      case 'mousewheel':
        button = ev.wheelDeltaY > 0
          ? 64
          : 65;
        break;
    }

    // next three bits are the modifiers:
    // 4 = shift, 8 = meta, 16 = control
    shift = ev.shiftKey ? 4 : 0;
    meta = ev.metaKey ? 8 : 0;
    ctrl = ev.ctrlKey ? 16 : 0;
    mod = shift | meta | ctrl;

    // no mods
    if (self.vt200Mouse) {
      // ctrl only
      mod &= ctrl;
    } else if (!self.normalMouse) {
      mod = 0;
    }

    // increment to SP
    button = (32 + (mod << 2)) + button;

    return button;
  }

  // mouse coordinates measured in cols/rows
  function getCoords(ev) {
    var x, y, w, h, el;

    // ignore browsers without pageX for now
    if (ev.pageX == null) return;

    x = ev.pageX;
    y = ev.pageY;
    el = self.element;

//PJS: include scrollLeft/scrollTop in following loop because terminal window may be inside scrolling div
    // should probably check offsetParent
    // but this is more portable
    while (el && el !== self.document.documentElement) {
      x -= (el.offsetLeft - el.scrollLeft);
      y -= (el.offsetTop - el.scrollTop);
      el = 'offsetParent' in el
        ? el.offsetParent
        : el.parentNode;
    }
    
    // convert to cols/rows
	//PJS: use font properties for column/row conversion	 
	//PJS: use Math.floor instead of Math.round for better precision
	if (self.fontProps) {
		x = Math.floor(x / self.fontProps.fontWidth);
		y = Math.floor(y / self.fontProps.fontHeight);
	} else {	
      w = self.element.clientWidth;
      h = self.element.clientHeight;
      x = Math.floor((x / w) * self.cols);
      y = Math.floor((y / h) * self.rows);
    }
	
    // be sure to avoid sending
    // bad positions to the program
    if (x < 0) x = 0;
    if (x >= self.cols) x = self.cols - 1;
    if (y < 0) y = 0;
    if (y >= self.rows) y = self.rows - 1;

    // xterm sends raw bytes and
    // starts at 32 (SP) for each.
    x += 32;
    y += 32;

    return {
      x: x,
      y: y,
      type: ev.type === wheelEvent
        ? 'mousewheel'
        : ev.type
    };
  }

  on(el, 'mousedown', function(ev) {
    DEBUG&&console.log('mousedown');	  
    if (!self.mouseEvents) return;

    // send the button
    sendButton(ev);

    // ensure focus
    self.focus();

    // fix for odd bug
    //if (self.vt200Mouse && !self.normalMouse) {
    if (self.vt200Mouse) {
      sendButton({ __proto__: ev, type: 'mouseup' });
      return cancel(ev);
    }

    // bind events
    if (self.normalMouse) on(self.document, 'mousemove', sendMove);

    // x10 compatibility mode can't send button releases
    if (!self.x10Mouse) {
      on(self.document, 'mouseup', function up(ev) {
        sendButton(ev);
        if (self.normalMouse) off(self.document, 'mousemove', sendMove);
        off(self.document, 'mouseup', up);
        return cancel(ev);
      });
    }

    return cancel(ev);
  });

  //PJS: add click/dblclk support
  on(el, 'click', function(ev) {
	var xy = getCoords(ev);
	var btn = getButton(ev);
	DEBUG&&console.log('click: btn='+btn+' x='+(xy.x-32)+' y='+(xy.y-32));
  });
  on(el, 'dblclick', function(ev){
  	var xy = getCoords(ev);
	var btn = getButton(ev);
	DEBUG&&console.log('dblclick: btn='+btn+' x='+(xy.x-32)+' y='+(xy.y-32));
  });
  on(el, 'contextmenu', function(ev) {
	var xy = getCoords(ev);
	var btn = getButton(ev);
	DEBUG&&console.log('contextmenu: btn='+btn+' x='+(xy.x-32)+' y='+(xy.y-32));
	return cancel(ev);
  });
  
  //if (self.normalMouse) {
  //  on(self.document, 'mousemove', sendMove);
  //}

  on(el, wheelEvent, function(ev) {
    if (!self.mouseEvents) return;
    if (self.x10Mouse
        || self.vt300Mouse
        || self.decLocator) return;
    sendButton(ev);
    return cancel(ev);
  });

  // allow mousewheel scrolling in
  // the shell for example
  on(el, wheelEvent, function(ev) {
    if (self.mouseEvents) return;
    if (self.applicationKeypad) return;
    if (ev.type === 'DOMMouseScroll') {
      self.scrollDisp(ev.detail < 0 ? -5 : 5);
    } else {
      self.scrollDisp(ev.wheelDeltaY > 0 ? -5 : 5);
    }
    return cancel(ev);
  });
};

/**
 * Destroy Terminal
 */

Terminal.prototype.destroy = function() {
  this.readable = false;
  this.writable = false;
  this._events = {};
  this.handler = function() {};
  this.write = function() {};
  if (this.viewport.parentNode) {
    this.viewport.parentNode.removeChild(this.viewport);
  }
  //this.emit('close');
};

/**
 * Rendering Engine
 */

// PJS - added mode value as a 3rd 32-bit integer & added dim to attribute list.
//       Added line attribute for double-size characters:
//         1=double wide, 2 = double high top, 4 = double high bottom
//
//     - split into two functions: refresh() & renderLine(). This way we can render
//       history lines (scrollback) independently from main terminal screen.
 
// In the screen buffer, each character
// is stored as a an array with a character
// and one or two 32-bit integers.
// First value:
//   bits 0-8: background color (0-511).
//   bits 9-17: foreground color (0-511).
//   bits 18-31: a mask for misc. flags:
//     1=bold, 2=underline, 4=blink, 8=inverse, 16=invisible, 32=dim (PJS - added dim attribute to list)
// Second value: a utf-16 character.
// Third value: optional attribute mode used to calculate the attribute & color.
//   bits 0-7: mode bits
//     1=protect, 2=direct background color, 4=embedded attribute, 8=direct foreground color, 16=tag attribute, 32=opaque tag
//   bits 8-15: embedded attribute value (valid if mode bit 4 (above) is set)
   
Terminal.prototype.refresh = function(start, end) {
  var y
    , row;

  // PJS: in the original version, if refreshing more than half the screen, remove the screen node
  // from parent, then update rows (children[]) while removed from DOM, then reattach screen. But
  // testing shows that simply updating the screen while in the DOM is faster (at least in Chrome).
  
  this.updateHistory(); //PJS: update the scrollback div  
  if (end >= this.lines.length) {
    end = this.lines.length - 1;
  }
  for (y = start; y <= end; y++) {
    row = y + this.ydisp;
    this.children[y].innerHTML = this.renderLine(row, y);
  }  
  if (this._cursorX !== this.x || this._cursorY !== this.y || this._historyScrolled) {
      this._historyScrolled = false;
	  this.handleCursor(this._cursorX = this.x, this._cursorY = this.y, this.curPage);
  }
    
};

// PJS: split out of original refresh() function so we can render each line independently
//   row = index into lines[] array for the line to render
//   y = screen line being rendered, or -1 if history line (when y === this.y, show the cursor)
//   returns the html for the rendered line
Terminal.prototype.renderLine = function(row, y) {
  var x
    , i
    , line
    , out
    , ch
    , width
	, lwidth
    , data
    , attr
	, lattr
    , bg
    , fg
	, xg
    , flags
	, mask
	, defAttr;

  width = this.cols;

  line = this.lines[row];
  out = '';
  if (!line) {
	  return out; // what?
  }

  if (y === this.y
	  && this.cursorState
      && (this.ydisp === this.ybase || this.selectMode)
      && !this.cursorHidden) {
    x = this.x;
  } else {
    x = -1;
  }

  mask = ~((this.allowUnderline ? 0 : (2 << 18)) | (this.allowBlinking ? 0 : (4 << 18))); // underline & blink effect mask
  attr = defAttr = this.defAttr & mask;

  //PJS: line attributes for double-size characters
  lattr = line.lineAttr || 0;
  switch(lattr) {
    case 1: out += '<span class="terminal-double-wide">'; break;
	case 2: out += '<span class="terminal-double-high-top">'; break;
	case 3: out += '<span class="terminal-double-wide-high-top">'; break;
	case 4: out += '<span class="terminal-double-high-bottom">'; break;
	case 5: out += '<span class="terminal-double-wide-high-bottom">'; break;
  }
	  
  lwidth = lattr & 1 ? width >> 1 : width;
  i = 0;

  for (; i < lwidth; i++) {
    data = line[i][0] & mask; // kill the blink & underline bits if disabled
    ch = line[i][1];
      
    if (i === x) data = -1;

    if (data !== attr) {
      if (attr !== defAttr) {
        out += '</span>';
      }
      if (data !== defAttr) {		  
        if (data === -1) {
		  /*if (this.reverseVideo) {*/
		  // TODO: cursor style = block or underline
		  if (this.cursorStyle === 0 || this.cursorStyle === 1) {
            out += '<span class="terminal-cursor-blink">'; // blinking block
		  } else if (this.cursorStyle === 3) {
            out += '<span class="terminal-underline-cursor-blink">'; // blinking underline
		  } else {
            out += '<span class="terminal-cursor">'; // steady block (TODO: steady underline)
		  }
        } else {
			
          bg = data & 0x1ff;
          fg = (data >> 9) & 0x1ff;
          flags = data >> 18;

		  if (this.reverseVideo) {
		    out += '<span class="reverse-video" ';
		  } else {
			out += '<span ';
		  }

		  if ( (flags & 4) && !(flags & 16) ) {
			out += 'class="text-blink" ';
		  }
		  
		  out += 'style="';
		  			
		  //PJS - add support for DECSCNM
		  if (this.reverseVideo) {
		    xg = fg; fg = bg; bg = xg;
		  }

          // bold
          if (flags & 1) {
            if (!Terminal.brokenBold) {
              out += 'font-weight:bold;';
            }
            // See: XTerm*boldColors
            //PJS: handled in charAttributes --- if (fg < 8) fg += 8;
          }

          // underline
          if (flags & 2) {
            out += 'text-decoration:underline;';
          }

/*
 * PJS - the following block is commented out because text-decroation:blink
 * has been deprecated and does not work in most browsers. Instead we are
 * using CSS animation to handle blinking cursor and blinking text.
 */

// PJS - start commented out block
/*
          // blink
          if (flags & 4) {
            if (flags & 2) {
              out = out.slice(0, -1);
              out += ' blink;';
            } else {
              out += 'text-decoration:blink;';
            }
          }
*/						
// PJS - end commented out block

/*
 * PJS - reversal of fg / bg colors is now handled in charAttributes so
 * we comment out the following block...
 */

// PJS - start commented out block
/*
		  // inverse
          if (flags & 8) {
            bg = (data >> 9) & 0x1ff;
            fg = data & 0x1ff;
			// Should inverse just be before the
            // above boldColors effect instead?
            if ((flags & 1) && fg < 8) fg += 8;
          }
*/						
// PJS - end commented out block

/*
 * PJS - hidden - use rgba(0,0,0,0) for foreground color so that background
 * color is still visible (replace following block):
 */

// PJS - start commented out block
/*
          // invisible
          if (flags & 16) {
            out += 'visibility:hidden;';
          }
*/						
// PJS - end commented out block

		  // PJS - new way to do invisible text
		  if (flags & 16) {
            out += 'color:rgba(0,0,0,0);'; // make foreground color transparent
		    fg = 257;	// dont change the foreground color!
		  }
			
          // out += '" class="'
          //   + 'term-bg-color-' + bg
          //   + ' '
          //   + 'term-fg-color-' + fg
          //   + '">';
			
          if (bg !== 256) {
            out += 'background-color:'
              + this.colors[bg]
              + ';';
          }
            
          if (fg !== 257) {
            out += 'color:'
              + this.colors[fg]
              + ';';
          }
          out += '">';
        }
      }
      }

    switch (ch) {
      case '&':
        out += '&amp;';
        break;
      case '<':
        out += '&lt;';
        break;
      case '>':
        out += '&gt;';
        break;
      default:
        if (ch <= ' ') {
          out += '&nbsp;';
        } else {
          if (isWide(ch)) i++;
          out += ch;
        }
        break;
    }

    attr = data;
	  
  }

  if (attr !== defAttr) {
    out += '</span>';
  }
    
  if(lattr) {
    out += '</span>';
  }
	
  return out;
  
};

//PJS: new function to update history div with scrollback content
Terminal.prototype.updateHistory = function() {
  var n
    , i
    , row
    , parent
	, div;

  // To re-render all history, call this with pendingScrollback === scrollback,
  // useful for resizeHistory() to synchronize history div with lines[].
   
  if (this.pendingScrollback > 0) {
  
    this._historyScrolled = true; //PJS 03-15-16: make sure handleCursor() gets called - on iOS is may be below keyboard now!
	
    if (this.historyRows === 0) {
      // The first time we add to history, add a bottom border to history div
	  // and top border to live screen in contrasting (black/white) colors
	  // to indicate the boundary between live screen & history.
	  this.history.style.paddingTop = "5px";
	  //this.history.style.borderBottomStyle = "dotted";
	  //this.history.style.borderBottomWidth = "1px";
	  //this.history.style.borderBottomColor = "black";
	  this.element.style.paddingTop = "0";
	  //this.element.style.borderTopStyle = "dotted";
	  //this.element.style.borderTopWidth = "1px";
	  //this.element.style.borderTopColor = "white";
	}
	
    if (this.pendingScrollback > this.rows / 2) {
	  parent = this.history.parentNode;
      if (parent) parent.removeChild(this.history);
    }

    if (this.pendingScrollback >= this.scrollback) {
		// re-render the whole history
		this.pendingScrollback = this.scrollback;
		n = this.rows;
	} else {
		n = this.historyRows - this.scrollback + this.pendingScrollback; // how many lines to remove from history div?
	}
    if (n >= this.rows) {
	  // just re-render the whole history if we have too many lines to remove
	  row = 0;
	  this.history.innerHTML = '';
    } else {
	  while (n-- > 0) {
	    this.history.removeChild(this.history.firstChild);
      }
	  row = this.ybase - this.pendingScrollback;
    }

    for (; row < this.ybase; row++) {
      div = this.document.createElement('div');
      div.innerHTML = this.renderLine(row, -1);
      this.history.appendChild(div);
    }

    if (parent) parent.insertBefore(this.history, this.element);	
	this.pendingScrollback = 0;
	this.historyRows = this.ybase;
  } else if (this.scrollback === 0) {
      // clear the history
	  this.pendingScrollback = this.historyRows = 0;
	  this.history.innerHTML = '';
	  this.history.style.paddingTop = "0";
  	  //this.history.style.borderBottomStyle = "none";
	  this.element.style.paddingTop = "5px";
	  //this.element.style.borderTopStyle = "none";
  }
  
};

Terminal.prototype.showCursor = function() {
  if(!this.cursorState) {
    this.cursorState = 1;
    this.refresh(this.y, this.y);
  }
};

Terminal.prototype.scheduleUpdate = function() {
  var self = this;
  if(!this._updateTimer) {
    this._updateTimer = setTimeout(function() {
		self._updateTimer = null;
		self.refresh(self.refreshStart, self.refreshEnd);
		self.resetRange();
    }, 1);		
  }
}

Terminal.prototype.scroll = function(n) {
  var row;

// PJS: added number of lines argument 'n'

// PJS start AccuTerm mod - use pendingScrollback
 
/* comment out old code
 
  if (++this.ybase === this.scrollback) {
    this.ybase = this.ybase / 2 | 0;
    this.lines = this.lines.slice(-(this.ybase + this.rows) + 1);
  }
  this.ydisp = this.ybase;

  // last line
  row = this.ybase + this.rows - 1;

  // subtract the bottom scroll region
  row -= this.rows - 1 - this.scrollBottom;

  if (row === this.lines.length) {
    // potential optimization:
    // pushing is faster than splicing
    // when they amount to the same
    // behavior.
    this.lines.push(this.blankLine());
  } else {
    // add our new line
    this.lines.splice(row, 0, this.blankLine());
  }

  if (this.scrollTop !== 0) {
    if (this.ybase !== 0) {
      this.ybase--;
      this.ydisp = this.ybase;
    }
    this.lines.splice(this.ybase + this.scrollTop, 1);
  }
*/
	
// begin new code

  n = n || 1;
  
  for ( ; n > 0 ; n--) { // PJS: allow scrolling multiple lines - may be used to clear screen & copy old screen to history
	  
	if (this.scrollTop === 0) {
	  if (this.scrollback > 0) {
		this.pendingScrollback++; // accumulate number of lines added to scrollback since last refresh
		if (this.ybase === this.scrollback) {
		  // scrollback is full, discard oldest line
		  this.lines.shift();
		} else {
		  // shift base of terminal screen down one line
		  this.ybase++;
		}
	  } else {
		// no scrollback - just delete the top line
		this.lines.shift();
	  }
	} else {
	  // if not scrolling from top of screen, just delete the first line in the scrolling region
	  this.lines.splice(this.ybase + this.scrollTop, 1);	  
	}

	//TODO: scroll to top of live screen
	this.ydisp = this.ybase;

	// last line
	row = this.ybase + this.scrollBottom;
	if (row === this.lines.length) {
	  // potential optimization:
	  // pushing is faster than splicing
	  // when they amount to the same
	  // behavior.
	  this.lines.push(this.blankLine());
	} else {
	  // add our new line
	  this.lines.splice(row, 0, this.blankLine());
	}

  }
  
// PJS end AccuTerm mod

  // this.maxRange();
  this.updateRange(this.scrollTop);
  this.updateRange(this.scrollBottom);
};

Terminal.prototype.scrollDisp = function(disp, reset) {
  
/* PJS begin AccuTerm mod
 * 
 * using ydisp to scroll the display is not compatible with
 * using history div and using the viewport div scrollTop
 * to perform the scrolling...
 * 
   
  this.ydisp += disp;

  if (this.ydisp > this.ybase) {
    this.ydisp = this.ybase;
  } else if (this.ydisp < 0) {
    this.ydisp = 0;
  }
  this.refresh(0, this.rows - 1);

*/	
    var lineHeight = this.getLineHeight();
    var mainHeight = this.rows * lineHeight;
    var historyRows = this.historyRows;
    var historyBot = historyRows * lineHeight;
    if (historyBot > 0) 
        historyBot += 5; // add 5px for padding
    var viewportHeight = this.viewport.clientHeight;
    var maxScrollTop = historyBot + (mainHeight > viewportHeight ? mainHeight - viewportHeight : 0);
    var curScrollTop = this.viewport.scrollTop;
	var newScrollTop = reset ? historyBot : curScrollTop + (disp * lineHeight);
    if (newScrollTop < 0) 
        newScrollTop = 0;
    if (newScrollTop > maxScrollTop) 
        newScrollTop = maxScrollTop;

    // save new scroll position
    this.curScrollPos = newScrollTop - historyBot;	

    // scroll the viewport if needed
	if (reset) this.viewport.scrollLeft = 0; // reset horizontal scrolling 
    if (newScrollTop !== curScrollTop) {
        this.viewport.scrollTop = newScrollTop;
        //this.refresh(0, this.rows - 1);
        DEBUG&&console.log('scrollDisp: cur scroll top=' + curScrollTop + ' new scroll top=' + newScrollTop);
    }
    
// PJS end AccuTerm mod

};

Terminal.prototype.write = function(data) {
  var l = data.length
    , i = 0
    , j
    , cs
    , ch
	, xch
	, wc
	, initial_y;
	
//DEBUG&&console.log('Terminal.prototype.write: len=' + data.length);

/* PJS: don't reset refreshStart on each invocation of write()!
  this.refreshStart = this.y;
  this.refreshEnd = this.y; */
  //this.updateRange(this.y); //PJS: do this instead of resetting refreshStart & refreshEnd
  initial_y = this.y; //PJS: defer updateRange until end

  if (this.ybase !== this.ydisp) {
    this.ydisp = this.ybase;
    this.maxRange();
  }

  // this.log(JSON.stringify(data.replace(/\x1b/g, '^[')));

  for (; i < l; i++) {
    ch = data[i];
    switch (this.state) {
      case normal:
        switch (ch) {
          // '\0'
          // case '\0':
          // case '\200':
          //   break;
          case '\x05': //PJS: ENQ - send answerback
		    this.send(this.answerBack); //TODO: encoding?
			break;
          // '\a'
          case '\x07':
            this.bell();
            break;

          // '\n', '\v', '\f'
          case '\n':
          case '\x0b':
          case '\x0c':
            if (this.convertEol) {
              this.x = 0;
            }
            // TODO: Implement eat_newline_glitch.
            // if (this.realX >= this.cols) break;
            // this.realX = 0;
            this.y++;
            if (this.y > this.scrollBottom) {
              this.y--;
              this.scroll();
            }
			this.delayWrap = false;
            break;

          // '\r'
          case '\r':
            this.x = 0;
			this.delayWrap = false; // PJS
            break;

          // '\b'
          case '\x08':
            if (this.x > 0) {
              this.x--;
            }
			this.delayWrap = false; // PJS
            break;

          // '\t'
          case '\t':
            this.x = this.nextStop();
            break;

          // shift out
          case '\x0e':
            this.setgLevel(1, 0); //PJS: pass 2nd argument to setgLevel: 0=GL, 1=GR
            break;

          // shift in
          case '\x0f':
            this.setgLevel(0, 0); //PJS: pass 2nd argument to setgLevel: 0=GL, 1=GR
            break;

          // '\e'
          case '\x1b':
            this.state = escaped;
            break;

		  //PJS: add support for 8-bit controls
		  // IND
		  case '\x84': 
            this.x = 0;
            /* fall through */
		  // NEL
		  case '\x85':
            this.index();
			this.delayWrap = false; // PJS
			this.state = normal;
            break;
		  // HTS
		  case '\x88':
            this.tabSet();
			this.state = normal;
            break;
		  // RI
		  case '\x8d':
            this.reverseIndex();
			this.delayWrap = false; // PJS
			this.state = normal;
            break;
		  // SS2
		  case '\x8e':
			this.singleShift = this.glevel; //PJS
			this.setgLevel(2, 0); //PJS: update character set: G2 -> GL
			this.state = normal;
            break;
		  // SS3
		  case '\x8f':
			this.singleShift = this.glevel; //PJS
			this.setgLevel(3, 0); //PJS: update character set: G3 -> GL
			this.state = normal;
            break;
		  // DCS
		  case '\x90':
            this.params = [];
            this.currentParam = 0;
            this.state = dcs;
            break;
		  // CSI
		  case '\x9b':
            this.params = [];
            this.currentParam = 0;
            this.state = csi;
            break;
		  // ST
		  case '\x9c':
			// ST is checked in the 'osc' state
		    break;
		  // OSC
		  case '\x9d':
            this.params = [];
            this.currentParam = 0;
            this.state = osc;
            break;
		  // PM
		  case '\x9e':
            this.state = ignore;
			break;
		  // APC
		  case '\x9f':
            this.state = ignore;
			break;
		  case '\x7f': //PJS: display DEL if its in the character set map
		  /* fall through */
          default:
            // ' '
            if (ch >= ' ') {
			  //PJS start AccuTerm mod - if character translates to null, skip it; if undefined use original character
              //if (this.charset && this.charset[ch]) {
              //  ch = this.charset[ch];
              //}
			  if (this.charset) {
				xch = this.charset[ch];
				if (xch === '') break; // skip this one (for example, DEL)
				if (xch) ch = xch; // use translated character
			  }
			  // handle single shift state
			  if (this.singleShift !== null) {
				  this.setgLevel(this.singleShift, 0); // restore previous character set
				  this.singleShift = null;
			  }			  
			  //PJS end AccuTerm mod
			  
//PJS start AccuTerm mod - handle delay wrap properly			  
/*            if (this.x >= this.cols) {
                this.x = 0;
                this.y++;
                if (this.y > this.scrollBottom) {
                  this.y--;
                  this.scroll();
                }
              }
*/				
			  // handle insert mode
			  if (this.insertMode) {
				  this.insertChars([1]);
			  }
			  
			  // handle delayed wrap
			  if (this.delayWrap) {
				this.advanceCursor();
			  }
			  
			  // check if wide character will fit on line
			  wc = isWide(ch);
			  if (wc && (this.lastCol(this.y) - this.x + 1) < 2) {
				  // wide character cannot be displayed in last column - need at least two columns!
				  ch = ' '; // TODO - should we use substitution character?
				  wc = false; // clear the wide character flag
			  }
			  
//PJS end AccuTerm mod

              this.lines[this.y + this.ybase][this.x] = [this.curAttr, ch];
			  if (this.protMode) {
				this.lines[this.y + this.ybase][this.x][2] = 0x01; // PJS - set protect bit				
			  }
			  
//PJS start AccuTerm mod
              //this.x++;
			  this.advanceCursor(); // PJS - use new function to advance the cursor - handles scrolling region, auto wrap, delayed wrap, scrolling
//PJS end AccuTerm mod

              this.updateRange(this.y);

//PJS start AccuTerm mod			  
/*
              if (isWide(ch)) {
                j = this.y + this.ybase;
                if (this.cols < 2 || this.x >= this.cols) {
                  this.lines[j][this.x - 1] = [this.curAttr, ' '];
                  break;
                }
                this.lines[j][this.x] = [this.curAttr, ' '];
                this.x++;
              }
*/
			  if (wc) {
                this.lines[this.y + this.ybase][this.x] = [this.curAttr, ' '];
				this.advanceCursor();			  
			  }
			  
		    } else {
			  if(this.ctlchr && this.ctlchr(ch)) { // PJS: handle AccuTerm private control sequences
				return (l - i - 1);
			  }
//PJS end AccuTerm mod			  
		    }
            break;
        }
        break;
      case escaped:
        switch (ch) {
          // ESC [ Control Sequence Introducer ( CSI is 0x9b).
          case '[':
            this.params = [];
            this.currentParam = 0;
            this.state = csi;
            break;

          // ESC ] Operating System Command ( OSC is 0x9d).
          case ']':
            this.params = [];
            this.currentParam = 0;
            this.state = osc;
            break;

          // ESC P Device Control String ( DCS is 0x90).
          case 'P':
            this.params = [];
            this.currentParam = 0;
            this.state = dcs;
            break;

          // ESC _ Application Program Command ( APC is 0x9f).
          case '_':
            this.state = ignore;
            break;

          // ESC ^ Privacy Message ( PM is 0x9e).
          case '^':
            this.state = ignore;
            break;

          // ESC c Full Reset (RIS).
          case 'c':
            this.reset();
            break;

          // ESC E Next Line ( NEL is 0x85).
          // ESC D Index ( IND is 0x84).
          case 'E':
            this.x = 0;
            ;
          case 'D':
            this.index();
			this.delayWrap = false; // PJS
			this.state = normal;
            break;

          // ESC M Reverse Index ( RI is 0x8d).
          case 'M':
            this.reverseIndex();
			this.delayWrap = false; // PJS
			this.state = normal;
            break;
			
		  //PJS: use charset state to decode ESC %, ESC &, ESC " SCS sequences that have an intermediate character (this.scsint)
		  
		  // ESC & Designate character sets to G-set
		  case '&':
		  case '"':
          // ESC % Select default/utf-8 character set.
          // @ = default, G = utf-8
          case '%':
            //this.charset = null;
/*          this.setgLevel(0);
            this.setgCharset(0, Terminal.charsets.US);
            this.state = normal;
            i++; */
            this.scsint = ch;
			this.state = charset;
            break;

          // ESC (,),*,+,-,. Designate G0-G2 Character Set.
          case '(': // <-- this seems to get all the attention
          case ')':
          case '*':
          case '+':
          case '-':
          case '.':
		  case '/': //PJS: moved from below		  
            switch (ch) {
              case '(':
                this.gcharset = 0;
                break;
              case ')':
                this.gcharset = 1;
                break;
              case '*':
                this.gcharset = 2;
                break;
              case '+':
                this.gcharset = 3;
                break;
              case '-':
                this.gcharset = 1;
                break;
              case '.':
                this.gcharset = 2;
                break;
			  case '/': //PJS: moved from below (let charset state decode next character)
				this.gcharset = 3;
				break;
            }
			this.scsint = ch; //PJS: save intermediate character so we can diffentiate between 94 & 96 character sets
            this.state = charset;
            break;

          // Designate G3 Character Set (VT300).
          // A = ISO Latin-1 Supplemental.
          // Not implemented.
//PJS moved this case up to previous (charset) case
/*          case '/':
            this.gcharset = 3;
            this.state = charset;
            i--;
            break; */

          // ESC N
          // Single Shift Select of G2 Character Set
          // ( SS2 is 0x8e). This affects next character only.
          case 'N':
			this.singleShift = this.glevel; //PJS
			this.setgLevel(2, 0); //PJS: update character set: G2 -> GL
			this.state = normal;
            break;
          // ESC O
          // Single Shift Select of G3 Character Set
          // ( SS3 is 0x8f). This affects next character only.
          case 'O':
			this.singleShift = this.glevel; //PJS
			this.setgLevel(3, 0); //PJS: update character set: G3 -> GL
			this.state = normal;
            break;
          // ESC n
          // Invoke the G2 Character Set as GL (LS2).
          case 'n':
            this.setgLevel(2, 0); //PJS: pass 2nd argument to setgLevel: 0=GL, 1=GR
			this.state = normal;
            break;
          // ESC o
          // Invoke the G3 Character Set as GL (LS3).
          case 'o':
            this.setgLevel(3, 0); //PJS: pass 2nd argument to setgLevel: 0=GL, 1=GR
			this.state = normal;
            break;
          // ESC |
          // Invoke the G3 Character Set as GR (LS3R).
          case '|':
            this.setgLevel(3, 1); //PJS: pass 2nd argument to setgLevel: 0=GL, 1=GR
			this.state = normal;
            break;
          // ESC }
          // Invoke the G2 Character Set as GR (LS2R).
          case '}':
            this.setgLevel(2, 1); //PJS: pass 2nd argument to setgLevel: 0=GL, 1=GR
			this.state = normal;
            break;
          // ESC ~
          // Invoke the G1 Character Set as GR (LS1R).
          case '~':
            this.setgLevel(1, 1); //PJS: pass 2nd argument to setgLevel: 0=GL, 1=GR
			this.state = normal;
            break;

          // ESC 7 Save Cursor (DECSC).
          case '7':
            this.saveCursor();
            this.state = normal;
            break;

          // ESC 8 Restore Cursor (DECRC).
          case '8':
            this.restoreCursor();
            this.state = normal;
            break;

          // ESC # 3 DEC line height/width
          case '#':
            //this.state = normal;
            //i++;
			this.state = decdsc; // DEC double size characters
            break;

          // ESC H Tab Set (HTS is 0x88).
          case 'H':
            this.tabSet();
			this.state = normal;
            break;

          // ESC = Application Keypad (DECPAM).
          case '=':
			if (!this.applicationKeypad) {
              this.log('Serial port requested application keypad.');
			  this.applicationKeypad = this.allowApplicationMode; // PJS: override if disabled in settings
			  if (this.initDfltKbd) {
			    this.initDfltKbd(this.applicationCursor, this.applicationKeypad, this.backspaceDel, this.eightBitControls);
			  }
			}
            this.state = normal;
            break;

          // ESC > Normal Keypad (DECPNM).
          case '>':
			if (this.applicationKeypad) {
              this.log('Switching back to normal keypad.');
              this.applicationKeypad = false;
			  if (this.initDfltKbd) {
			    this.initDfltKbd(this.applicationCursor, this.applicationKeypad, this.backspaceDel, this.eightBitControls);
			  }
			}
            this.state = normal;
            break;

//PJS start AccuTerm mod
		  case ' ':
			this.state = esc_sp;
			break;
//PJS end AccuTerm mod			  

          default:
            this.state = normal;
//PJS start AccuTerm mod			  
			if(this.escseq && this.escseq(ch)) { //PJS: handle AccuTerm private escape sequences
				return (l - i - 1);
			}
//PJS end AccuTerm mod			  
            this.error('Unknown ESC control: %s.', ch);
            break;
        }
        break;

      case charset:
/* PJS:
 *  If this ch is '%', remain in the charset state for one more character,
 *  so we can handle 5 (Finnish) vs %5 (supplemental), as well as %@ (default)
 *  and %G (UTF-8). Use scsint to differentiate between 94 & 96 character sets,
 *  especially 'A' (UK vs Latin1).
 */
        switch (ch) {
          case '0': // DEC Special Character and Line Drawing Set.
            cs = Terminal.charsets.SCLD;
            break;
          case 'A': // UK or Latin1
			if (this.scsint === '-' || this.scsint === '.' || this.scsint === '/') {
				cs = Terminal.charsets.ISOLatin; // PJS
			} else {
				cs = Terminal.charsets.UK;
			}
            break;
          case 'B': // United States (USASCII).
            cs = Terminal.charsets.US;
            break;
          case '4': // Dutch
            cs = Terminal.charsets.Dutch;
            break;
          case 'C': // Finnish
            cs = Terminal.charsets.Finnish;
            break;
          case '5':
			if (this.scsint === '%') {
				cs = Terminal.charsets.MULTINATIONAL;
			} else if (this.scsint !== '&' && this.scsint !== '"' && this.scsint !== "'") {			
				cs = Terminal.charsets.Finnish;
			}
            break;
          case 'R': // French
            cs = Terminal.charsets.French;
            break;
          case 'Q': // FrenchCanadian
            cs = Terminal.charsets.FrenchCanadian;
            break;
          case 'K': // German
            cs = Terminal.charsets.German;
            break;
          case 'Y': // Italian
            cs = Terminal.charsets.Italian;
            break;
          case 'E': // NorwegianDanish
          case '6':
            cs = Terminal.charsets.NorwegianDanish;
            break;
          case 'Z': // Spanish
            cs = Terminal.charsets.Spanish;
            break;
          case 'H': // Swedish
          case '7':
            cs = Terminal.charsets.Swedish;
            break;
          case '=': // Swiss
            cs = Terminal.charsets.Swiss;
            break;
          //case '/': // ISOLatin (actually /A)
          //  cs = Terminal.charsets.ISOLatin;
          //  i++;
          //  break;
		  case '<': //PJS - UPSS
			cs = Terminal.charsets.UPSS;
		    break;
		  case '@': //PJS - Latin1 (cancel UTF8)
            this.setgLevel(0);
			this.gcharset = 0;
            cs = Terminal.charsets.DEFAULT;
			break;
		  case 'G': //PJS - UTF8
            this.setgLevel(0);
			this.gcharset = 0;
            cs = Terminal.charsets.UTF8;
			break;
          default: // Default
            cs = Terminal.charsets.US;
            break;
        }
		if (ch === '%' || ch === '&' || ch === '"' || ch === "'") {
			this.scsint = ch; // use to differntiate 5 vs %5
			this.state = charset; // get one more character
		} else {
	        this.setgCharset(this.gcharset, cs);
		    this.gcharset = null;
			this.state = normal;
		}
        break;

      case osc:
        // OSC Ps ; Pt ST
        // OSC Ps ; Pt BEL
        //   Set Text Parameters.
        if (ch === '\x1b' || ch === '\x07' || ch === '\x9c') { //PJS: added test for ST (0x9c)
          if (ch === '\x1b') i++;

          this.params.push(this.currentParam);

          switch (this.params[0]) {
            case 0:
            case 1:
            case 2:
              if (this.params[1]) {
                this.title = this.params[1];
                this.handleTitle(this.title);
              }
              break;
            case 3:
              // set X property
              break;
            case 4:
            case 5:
              // change dynamic colors
              break;
            case 10:
            case 11:
            case 12:
            case 13:
            case 14:
            case 15:
            case 16:
            case 17:
            case 18:
            case 19:
              // change dynamic ui colors
              break;
            case 46:
              // change log file
              break;
            case 50:
              // dynamic font
              break;
            case 51:
              // emacs shell
              break;
            case 52:
              // manipulate selection data
              break;
            case 104:
            case 105:
            case 110:
            case 111:
            case 112:
            case 113:
            case 114:
            case 115:
            case 116:
            case 117:
            case 118:
              // reset colors
              break;
          }

          this.params = [];
          this.currentParam = 0;
          this.state = normal;
        } else {
          if (!this.params.length) {
            if (ch >= '0' && ch <= '9') {
              this.currentParam =
                this.currentParam * 10 + ch.charCodeAt(0) - 48;
            } else if (ch === ';') {
              this.params.push(this.currentParam);
              this.currentParam = '';
            }
          } else {
            this.currentParam += ch;
          }
        }
        break;

      case csi:
        // '?', '>', '!'
        if (ch === '?' || ch === '>' || ch === '!') {
          this.prefix = ch;
          break;
        }

        // 0 - 9
        if (ch >= '0' && ch <= '9') {
          this.currentParam = this.currentParam * 10 + ch.charCodeAt(0) - 48;
          break;
        }

        // '$', '"', ' ', '\''
        if (ch === '$' || ch === '"' || ch === ' ' || ch === '\'') {
          this.postfix = ch;
          break;
        }

        // PJS: handle control characters while processing csi sequence
		if (ch <= '\x1f' || (ch >= '\x7f' && ch <= '\x9f')) {
		  this.state = normal;
		  this.write(ch); // process the control character
		  if (this.state === normal && (ch != '\x18' && ch !== '\x1a')) {
		    this.state = csi; // continue processing the csi sequence
		  }
		  break;
		}
		
        this.params.push(this.currentParam);
        this.currentParam = 0;

        // ';'
        if (ch === ';') break;

        this.state = normal;

        switch (ch) {
          // CSI Ps A
          // Cursor Up Ps Times (default = 1) (CUU).
          case 'A':
            this.cursorUp(this.params);
            break;

          // CSI Ps B
          // Cursor Down Ps Times (default = 1) (CUD).
          case 'B':
            this.cursorDown(this.params);
            break;

          // CSI Ps C
          // Cursor Forward Ps Times (default = 1) (CUF).
          case 'C':
            this.cursorForward(this.params);
            break;

          // CSI Ps D
          // Cursor Backward Ps Times (default = 1) (CUB).
          case 'D':
            this.cursorBackward(this.params);
            break;

          // CSI Ps ; Ps H
          // Cursor Position [row;column] (default = [1,1]) (CUP).
          case 'H':
            this.cursorPos(this.params);
            break;

          // CSI Ps J  Erase in Display (ED).
          case 'J':
            this.eraseInDisplay(this.params);
            break;

          // CSI Ps K  Erase in Line (EL).
          case 'K':
            this.eraseInLine(this.params);
            break;

          // CSI Pm m  Character Attributes (SGR).
          case 'm':
            if (!this.prefix) {
              this.charAttributes(this.params);
            }
            break;

          // CSI Ps n  Device Status Report (DSR).
          case 'n':
            if (!this.prefix) {
              this.deviceStatus(this.params);
            }
            break;

          /**
           * Additions
           */

          // CSI Ps @
          // Insert Ps (Blank) Character(s) (default = 1) (ICH).
          case '@':
            this.insertChars(this.params);
            break;

          // CSI Ps E
          // Cursor Next Line Ps Times (default = 1) (CNL).
          case 'E':
            this.cursorNextLine(this.params);
            break;

          // CSI Ps F
          // Cursor Preceding Line Ps Times (default = 1) (CNL).
          case 'F':
            this.cursorPrecedingLine(this.params);
            break;

          // CSI Ps G
          // Cursor Character Absolute  [column] (default = [row,1]) (CHA).
          case 'G':
            this.cursorCharAbsolute(this.params);
            break;

          // CSI Ps L
          // Insert Ps Line(s) (default = 1) (IL).
          case 'L':
            this.insertLines(this.params);
            break;

          // CSI Ps M
          // Delete Ps Line(s) (default = 1) (DL).
          case 'M':
            this.deleteLines(this.params);
            break;

          // CSI Ps P
          // Delete Ps Character(s) (default = 1) (DCH).
		  // CSI SP Ps P
		  // Page position absolute (PPA) (added by PJS)
          case 'P':
			if (this.postfix === '') {			
              this.deleteChars(this.params); // DCH
		    } else if (this.postfix === ' ') {
		      this.switchPage((this.params[0] || 1) - 1, 0, 0);
		    }
            break;

		  // CSI SP Ps R
		  // Page position backward (PPB) (added by PJS)
          case 'R':
			if (this.postfix === ' ') {			
		      this.switchPage((this.params[0] || 1), -1, 0);
		    }
            break;

		  // CSI SP Ps Q
		  // Page position relative (PPR) (added by PJS)
          case 'Q':
			if (this.postfix === ' ') {			
		      this.switchPage((this.params[0] || 1), 1, 0);
		    }
            break;

          // CSI Ps X
          // Erase Ps Character(s) (default = 1) (ECH).
          case 'X':
            this.eraseChars(this.params);
            break;

          // CSI Pm `  Character Position Absolute
          //   [column] (default = [row,1]) (HPA).
          case '`':
            this.charPosAbsolute(this.params);
            break;

          // 141 61 a * HPR -
          // Horizontal Position Relative
          case 'a':
            this.HPositionRelative(this.params);
            break;

          // CSI P s c
          // Send Device Attributes (Primary DA).
          // CSI > P s c
          // Send Device Attributes (Secondary DA)
          case 'c':
            this.sendDeviceAttributes(this.params);
            break;

          // CSI Pm d
          // Line Position Absolute  [row] (default = [1,column]) (VPA).
          case 'd':
            this.linePosAbsolute(this.params);
            break;

          // 145 65 e * VPR - Vertical Position Relative
          case 'e':
            this.VPositionRelative(this.params);
            break;

          // CSI Ps ; Ps f
          //   Horizontal and Vertical Position [row;column] (default =
          //   [1,1]) (HVP).
          case 'f':
            this.HVPosition(this.params);
            break;

          // CSI Pm h  Set Mode (SM).
          // CSI ? Pm h - mouse escape codes, cursor escape codes
          case 'h':
            this.setMode(this.params);
            break;

          // CSI Pm l  Reset Mode (RM).
          // CSI ? Pm l
          case 'l':
            this.resetMode(this.params);
            break;

          // CSI Ps ; Ps r
          //   Set Scrolling Region [top;bottom] (default = full size of win-
          //   dow) (DECSTBM).
          // CSI ? Pm r
          // case 'r': // duplicate
          //   this.setScrollRegion(this.params);
          //   break;

          // CSI s
          //   Save cursor (ANSI.SYS).
          case 's':
            this.saveCursor(this.params);
            break;

          // CSI u
          //   Restore cursor (ANSI.SYS).
          case 'u':
            this.restoreCursor(this.params);
            break;

          /**
           * Lesser Used
           */

          // CSI Ps I
          // Cursor Forward Tabulation Ps tab stops (default = 1) (CHT).
          case 'I':
            this.cursorForwardTab(this.params);
            break;

          // CSI Ps S  Scroll up Ps lines (default = 1) (SU).
          case 'S':
            this.scrollUp(this.params); // TODO - xterm only? VT420 documents this as  "pan down", but terminal does nothing (PJS)
            break;

          // CSI Ps T  Scroll down Ps lines (default = 1) (SD).
          // CSI Ps ; Ps ; Ps ; Ps ; Ps T
          // CSI > Ps; Ps T
          case 'T':
            // if (this.prefix === '>') {
            //   this.resetTitleModes(this.params);
            //   break;
            // }
            // if (this.params.length > 2) {
            //   this.initMouseTracking(this.params);
            //   break;
            // }
            if (this.params.length < 2 && !this.prefix) {
              this.scrollDown(this.params); // TODO - xterm only? VT420 documents this as  "pan up", but terminal does nothing (PJS)
            }
            break;

          // CSI Ps U
		  // Next page (NP). (added by PJS)
		  case 'U':
		    this.switchPage(this.params[0] || 1, 1, 2);
			break;
			
          // CSI Ps V
		  // Previous page (PP). (added by PJS)
		  case 'V':
		    this.switchPage(this.params[0] || 1, -1, 2);
			break;
			
          // CSI Ps Z
          // Cursor Backward Tabulation Ps tab stops (default = 1) (CBT).
          case 'Z':
            this.cursorBackwardTab(this.params);
            break;

          // CSI Ps b  Repeat the preceding graphic character Ps times (REP).
          case 'b':
            this.repeatPrecedingCharacter(this.params); // TODO: xterm only (PJS)
            break;

          // CSI Ps g  Tab Clear (TBC).
          case 'g':
            this.tabClear(this.params);
            break;

          // CSI Pm i  Media Copy (MC).
          // CSI ? Pm i
          case 'i':
            this.mediaCopy(this.params);
            break;

          // CSI Pm m  Character Attributes (SGR).
          // CSI > Ps; Ps m
          // case 'm': // duplicate
          //   if (this.prefix === '>') {
          //     this.setResources(this.params);
          //   } else {
          //     this.charAttributes(this.params);
          //   }
          //   break;

          // CSI Ps n  Device Status Report (DSR).
          // CSI > Ps n
          // case 'n': // duplicate
          //   if (this.prefix === '>') {
          //     this.disableModifiers(this.params);
          //   } else {
          //     this.deviceStatus(this.params);
          //   }
          //   break;

          // CSI > Ps p  Set pointer mode.
          // CSI ! p   Soft terminal reset (DECSTR).
          // CSI Ps$ p
          //   Request ANSI mode (DECRQM).
          // CSI ? Ps$ p
          //   Request DEC private mode (DECRQM).
          // CSI Ps ; Ps " p
          case 'p':
            switch (this.prefix) {
              // case '>':
              //   this.setPointerMode(this.params);
              //   break;
              case '!':
                this.softReset(this.params);
                break;
              // case '?':
              //   if (this.postfix === '$') {
              //     this.requestPrivateMode(this.params);
              //   }
              //   break;
              // default:
              //   if (this.postfix === '"') {
              //     this.setConformanceLevel(this.params);
              //   } else if (this.postfix === '$') {
              //     this.requestAnsiMode(this.params);
              //   }
              //   break;
            }
            break;

          // CSI Ps q  Load LEDs (DECLL).
          // CSI Ps SP q
          // CSI Ps " q
          case 'q':
            if (this.postfix === ' ') {
              this.setCursorStyle(this.params);
              break;
            }
			// TODO
          //   if (this.postfix === '"') {
          //     this.setCharProtectionAttr(this.params);
          //     break;
          //   }
          //   this.loadLEDs(this.params);
          //   break;

          // CSI Ps ; Ps r
          //   Set Scrolling Region [top;bottom] (default = full size of win-
          //   dow) (DECSTBM).
          // CSI ? Pm r
          // CSI Pt; Pl; Pb; Pr; Ps$ r
          case 'r':
            if (this.prefix === '?') {
              //TODO: this.restorePrivateValues(this.params);
            } else if (this.postfix === '$') {
              //TODO: this.setAttrInRectangle(this.params);
            } else {
              this.setScrollRegion(this.params);
            }
            break;

          // CSI s     Save cursor (ANSI.SYS).
          // CSI ? Pm s
          // case 's': // duplicate
          //   if (this.prefix === '?') {
          //     this.savePrivateValues(this.params);
          //   } else {
          //     this.saveCursor(this.params);
          //   }
          //   break;

          // CSI Ps ; Ps ; Ps t
          // CSI Pt; Pl; Pb; Pr; Ps$ t
          // CSI > Ps; Ps t
          // CSI Ps SP t
          // case 't':
          //   if (this.postfix === '$') {
          //     this.reverseAttrInRectangle(this.params);
          //   } else if (this.postfix === ' ') {
          //     this.setWarningBellVolume(this.params);
          //   } else {
          //     if (this.prefix === '>') {
          //       this.setTitleModeFeature(this.params);
          //     } else {
          //       this.manipulateWindow(this.params);
          //     }
          //   }
          //   break;

          // CSI u     Restore cursor (ANSI.SYS).
          // CSI Ps SP u
          // case 'u': // duplicate
          //   if (this.postfix === ' ') {
          //     this.setMarginBellVolume(this.params);
          //   } else {
          //     this.restoreCursor(this.params);
          //   }
          //   break;

          // CSI Pt; Pl; Pb; Pr; Pp; Pt; Pl; Pp$ v (DECCRA)
          case 'v':
            if (this.postfix === '$') {
              this.copyRectangle(this.params);
            }
            break;

          // CSI Pt ; Pl ; Pb ; Pr ' w
          // case 'w':
          //   if (this.postfix === '\'') {
          //     this.enableFilterRectangle(this.params);
          //   }
          //   break;

          // CSI Ps x  Request Terminal Parameters (DECREQTPARM).
          // CSI Ps x  Select Attribute Change Extent (DECSACE).
          // CSI Pc; Pt; Pl; Pb; Pr$ x
          // case 'x':
          //   if (this.postfix === '$') {
          //     this.fillRectangle(this.params);
          //   } else {
          //     this.requestParameters(this.params);
          //     //this.__(this.params);
          //   }
          //   break;

          // CSI Ps ; Pu ' z
          // CSI Pt; Pl; Pb; Pr$ z
          // case 'z':
          //   if (this.postfix === '\'') {
          //     this.enableLocatorReporting(this.params);
          //   } else if (this.postfix === '$') {
          //     this.eraseRectangle(this.params);
          //   }
          //   break;

          // CSI Pm ' {
          // CSI Pt; Pl; Pb; Pr$ {
          // case '{':
          //   if (this.postfix === '\'') {
          //     this.setLocatorEvents(this.params);
          //   } else if (this.postfix === '$') {
          //     this.selectiveEraseRectangle(this.params);
          //   }
          //   break;

          // CSI Ps ' |
		  // CSI Ps $ |   (PJS: added implementation for DECSCPP)
           case '|':
             if (this.postfix === '\'') {
               this.requestLocatorPosition(this.params);
             } else if (this.postfix === '$') {
			   this.setColumnsPerPage(this.params); // PJS 
			 }
             break;

          // CSI Pn ' }
          // Insert Pn Column(s) (default = 1) (DECIC), VT420 and up.
          case '}':
             if (this.postfix === '\'') { //PJS: change ' ' to '\''
               this.insertColumns(this.params);
             }
             break;

          // CSI Pn ' ~
          // Delete Pn Column(s) (default = 1) (DECDC), VT420 and up
           case '~':
             if (this.postfix === '\'') { //PJS: change ' ' to '\''
               this.deleteColumns(this.params);
             }
             break;

          default:			 			
            this.error('Unknown CSI code: %s.', ch);
            break;
        }

        this.prefix = '';
        this.postfix = '';
        break;

      case dcs:
        if (ch === '\x1b' || ch === '\x07') {
          if (ch === '\x1b') i++;

          switch (this.prefix) {
            // TODO: User-Defined Keys (DECUDK).
            case '':
              break;

            // Request Status String (DECRQSS).
            // test: echo -e '\eP$q"p\e\\'
            case '$q':
              var pt = this.currentParam
                , valid = false;

              switch (pt) {
                // DECSCA
                case '"q':
				  if (this.protMode) { //PJS
					pt = '1"q';
				  } else {
                    pt = '0"q';
				  }
                  break;

                // DECSCL
                case '"p':
                  pt = '61"p';
				  // PJS: add support for DECSCL
				  if (this.tmask & 0x30 === 0) {
				    if (this.tmask & 0x08) {
					  pt = '64;' + (this.eightBitControls ? '2' : '1') + '"p';
					} else if (this.tmask & 0x04) {
					  pt = '63;' + (this.eightBitControls ? '2' : '1') + '"p';
					} else if (this.tmask & 0x02) {
					  pt = '62;' + (this.eightBitControls ? '2' : '1') + '"p';
					}
				  }
                  break;

                // DECSTBM
                case 'r':
                  pt = ''
                    + (this.scrollTop + 1)
                    + ';'
                    + (this.scrollBottom + 1)
                    + 'r';
                  break;

                // SGR
                case 'm':
                  pt = '0m';
                  break;

                default:
                  this.error('Unknown DCS Pt: %s.', pt);
                  pt = '';
                  break;
              }

              this.send('\x1bP' + +valid + '$r' + pt + '\x1b\\');
              break;

            // Set Termcap/Terminfo Data (xterm, experimental).
            case '+p':
              break;

            // Request Termcap/Terminfo String (xterm, experimental)
            // Regular xterm does not even respond to this sequence.
            // This can cause a small glitch in vim.
            // test: echo -ne '\eP+q6b64\e\\'
            case '+q':
              var pt = this.currentParam
                , valid = false;

              this.send('\x1bP' + +valid + '+r' + pt + '\x1b\\');
              break;

            default:
              this.error('Unknown DCS prefix: %s.', this.prefix);
              break;
          }

          this.currentParam = 0;
          this.prefix = '';
          this.state = normal;
        } else if (!this.currentParam) {
          if (!this.prefix && ch !== '$' && ch !== '+') {
            this.currentParam = ch;
          } else if (this.prefix.length === 2) {
            this.currentParam = ch;
          } else {
            this.prefix += ch;
          }
        } else {
          this.currentParam += ch;
        }
        break;

      case ignore:
        // For PM and APC.
        if (ch === '\x1b' || ch === '\x07') {
          if (ch === '\x1b') i++;
          this.state = normal;
        }
        break;
		
      case decdsc:
		// handle ESC # DEC double size characters
        switch (ch) {
          // ESC # 3 (DECDHL)
          case '3':
            this.lineAttributes(this.y, 3); // double-wide-high-top
            break;
		  case '4':
			this.lineAttributes(this.y, 5); // double-wide-high-bottom
			break;
		  case '5':
			this.lineAttributes(this.y, 0); // single-wide
			break;
		  case '6':
			this.lineAttributes(this.y, 1); // double-wide
			break;
		  case '8':
			// DECALN - fill screen with 'E' (PJS: added implementation)
			this.curAttr = this.defAttr; // reset the current attribute to default
			this.cursorPos([1,1]); // home the cursor
			this.clearScreen(false); // clear screen including line attributes
			this.fillRectangle([69, 1, 1, this.rows - 1, this.cols - 1]); // fill with 'E's
			break;
		}
        this.state = normal;
		break;
//PJS start AccuTerm mod
      case esc_sp:
        switch (ch) {
          case 'F': // S7C1T
			if (this.eightBitControls) {
			  this.eightBitControls = false;
			  if (this.initDfltKbd) {
				this.initDfltKbd(this.applicationCursor, this.applicationKeypad, this.backspaceDel, this.eightBitControls);
			  }
			}
		    break;
          case 'G': // S8C1T
			if (!this.eightBitControls) {
			  this.eightBitControls = true;
			  if (this.initDfltKbd) {
				this.initDfltKbd(this.applicationCursor, this.applicationKeypad, this.backspaceDel, this.eightBitControls);
			  }
			}
			break;
		}
		this.state = normal;
		break;
//PJS end AccuTerm mod

    }
  }

  this.updateRange(initial_y); //PJS
  this.updateRange(this.y);
  //PJS this.refresh(this.refreshStart, this.refreshEnd);
  //PJS this.resetRange(); //PJS: reset refresh range
};

Terminal.prototype.writeln = function(data) {
  this.write(data + '\r\n');
};

// Key Resources:
// https://developer.mozilla.org/en-US/docs/DOM/KeyboardEvent
Terminal.prototype.keyDown = function(ev) {
  var self = this
    , key;

  switch (ev.keyCode) {
    // backspace
    case 8:
      //if (ev.shiftKey) {
	  if (ev.shiftKey || !this.backspaceDel) { // PJS: DECBKM
        key = '\x08'; // ^H
        break;
      }
      key = '\x7f'; // ^?
      break;
    // tab
    case 9:
      if (ev.shiftKey) {
        key = '\x1b[Z';
        break;
      }
      key = '\t';
      break;
    // return/enter
    case 13:
      key = '\r';
      break;
    // escape
    case 27:
      key = '\x1b';
      break;
    // left-arrow
    case 37:
      if (this.applicationCursor) {
        key = '\x1bOD'; // SS3 as ^[O for 7-bit
        //key = '\x8fD'; // SS3 as 0x8f for 8-bit
        break;
      }
      key = '\x1b[D';
      break;
    // right-arrow
    case 39:
      if (this.applicationCursor) {
        key = '\x1bOC';
        break;
      }
      key = '\x1b[C';
      break;
    // up-arrow
    case 38:
      if (this.applicationCursor) {
        key = '\x1bOA';
        break;
      }
      if (ev.ctrlKey) {
        this.scrollDisp(-1);
        return cancel(ev);
      } else {
        key = '\x1b[A';
      }
      break;
    // down-arrow
    case 40:
      if (this.applicationCursor) {
        key = '\x1bOB';
        break;
      }
      if (ev.ctrlKey) {
        this.scrollDisp(1);
        return cancel(ev);
      } else {
        key = '\x1b[B';
      }
      break;
    // delete
    case 46:
      key = '\x1b[3~';
      break;
    // insert
    case 45:
      key = '\x1b[2~';
      break;
    // home
    case 36:
      if (this.applicationKeypad) {
        key = '\x1bOH';
        break;
      }
      key = '\x1bOH';
      break;
    // end
    case 35:
      if (this.applicationKeypad) {
        key = '\x1bOF';
        break;
      }
      key = '\x1bOF';
      break;
    // page up
    case 33:
      if (ev.shiftKey) {
        this.scrollDisp(-(this.rows - 1));
        return cancel(ev);
      } else {
        key = '\x1b[5~';
      }
      break;
    // page down
    case 34:
      if (ev.shiftKey) {
        this.scrollDisp(this.rows - 1);
        return cancel(ev);
      } else {
        key = '\x1b[6~';
      }
      break;
    // F1
    case 112:
      key = '\x1bOP';
      break;
    // F2
    case 113:
      key = '\x1bOQ';
      break;
    // F3
    case 114:
      key = '\x1bOR';
      break;
    // F4
    case 115:
      key = '\x1bOS';
      break;
    // F5
    case 116:
      key = '\x1b[15~';
      break;
    // F6
    case 117:
      key = '\x1b[17~';
      break;
    // F7
    case 118:
      key = '\x1b[18~';
      break;
    // F8
    case 119:
      key = '\x1b[19~';
      break;
    // F9
    case 120:
      key = '\x1b[20~';
      break;
    // F10
    case 121:
      key = '\x1b[21~';
      break;
    // F11
    case 122:
      key = '\x1b[23~';
      break;
    // F12
    case 123:
      key = '\x1b[24~';
      break;
    default:
      // a-z and space
      if (ev.ctrlKey) {
        if (ev.keyCode >= 65 && ev.keyCode <= 90) {
          // Ctrl-A
          if (this.screenKeys) {
            if (!this.prefixMode && !this.selectMode && ev.keyCode === 65) {
              this.enterPrefix();
              return cancel(ev);
            }
          }
          // Ctrl-V
          if (this.prefixMode && ev.keyCode === 86) {
            this.leavePrefix();
            return;
          }
          // Ctrl-C
          if ((this.prefixMode || this.selectMode) && ev.keyCode === 67) {
            if (this.visualMode) {
              setTimeout(function() {
                self.leaveVisual();
              }, 1);
            }
            return;
          }
          key = String.fromCharCode(ev.keyCode - 64);
        } else if (ev.keyCode === 32) {
          // NUL
          key = String.fromCharCode(0);
        } else if (ev.keyCode >= 51 && ev.keyCode <= 55) {
          // escape, file sep, group sep, record sep, unit sep
          key = String.fromCharCode(ev.keyCode - 51 + 27);
        } else if (ev.keyCode === 56) {
          // delete
          key = String.fromCharCode(127);
        } else if (ev.keyCode === 219) {
          // ^[ - escape
          key = String.fromCharCode(27);
        } else if (ev.keyCode === 221) {
          // ^] - group sep
          key = String.fromCharCode(29);
        }
      } else if ((!this.isMac && ev.altKey) || (this.isMac && ev.metaKey)) {
        if (ev.keyCode >= 65 && ev.keyCode <= 90) {
          key = '\x1b' + String.fromCharCode(ev.keyCode + 32);
        } else if (ev.keyCode === 192) {
          key = '\x1b`';
        } else if (ev.keyCode >= 48 && ev.keyCode <= 57) {
          key = '\x1b' + (ev.keyCode - 48);
        }
      }
      break;
  }

  if (!key) return true;

  if (this.prefixMode) {
    this.leavePrefix();
    return cancel(ev);
  }

  if (this.selectMode) {
    this.keySelect(ev, key);
    return cancel(ev);
  }

  this.emit('keydown', ev);
  this.emit('key', key, ev);

  this.showCursor();
  
  if (!this.isKbdLocked()) { // PJS - add support for keyboard lock
	//this.handler(key);
	this.send(key); //PJS: call send instead of handler so we can process host character set translation
  }

  return cancel(ev);
};

Terminal.prototype.setgLevel = function(g) {
  this.glevel = g;
  this.charset = this.charsets[g];
};

Terminal.prototype.setgCharset = function(g, charset) {
  this.charsets[g] = charset;
  if (this.glevel === g) {
    this.charset = charset;
  }
};

Terminal.prototype.keyPress = function(ev) {
  var key;

  cancel(ev);

  if (ev.charCode) {
    key = ev.charCode;
  } else if (ev.which == null) {
    key = ev.keyCode;
  } else if (ev.which !== 0 && ev.charCode !== 0) {
    key = ev.which;
  } else {
    return false;
  }

  if (!key || ev.ctrlKey || ev.altKey || ev.metaKey) return false;

  key = String.fromCharCode(key);

  if (this.prefixMode) {
    this.leavePrefix();
    this.keyPrefix(ev, key);
    return false;
  }

  if (this.selectMode) {
    this.keySelect(ev, key);
    return false;
  }

  this.emit('keypress', key, ev);
  this.emit('key', key, ev);

  this.showCursor();
  
  if (!this.isKbdLocked()) { // PJS - add support for keyboard lock
	//this.handler(key);
	this.send(key); //PJS: call send instead of handler, so we can handle character set translation
  }

  return false;
};

Terminal.prototype.send = function(data) {
  var self = this;

  if (!this.queue) {
    setTimeout(function() {
	  if (self.queue.length) { // do not send empty string (causes socket.io connection to choke)
		try {
	      self.handler(self.queue);
		  self.queue = '';
		} catch(e) {
		  DEBUG&&console.log('Terminal.prototype.send exception sending data: ' + e);			
		}
      }
    }, 1);
  }

  //PJS: diagnostics
  if (this.diagCtx && (this.diagCtx.mode & 1)) {
	if (this.charmap && !this.charmap.isUtf8)
	  this._pvt_diag_update(this.charmap.charOut(data), 1);
	else
	  this._pvt_diag_update(data, 1);
  }
  
  //PJS: use charmap to convert output to host character set before sending
  if (this.charmap) {
	this.queue += this.charmap.charOut(data);
  } else {
    this.queue += data;
  }
};

Terminal.prototype.bell = function() {
  this.emit('bell');
  if (!this.visualBell) return;
  var self = this;
  this.element.style.borderColor = 'white';
  setTimeout(function() {
    self.element.style.borderColor = '';
  }, 10);
  if (this.popOnBell) this.focus();
};

Terminal.prototype.log = function() {
  if (!this.debug) return;
  if (!this.context.console || !this.context.console.log) return;
  var args = Array.prototype.slice.call(arguments);
  this.context.console.log.apply(this.context.console, args);
};

Terminal.prototype.error = function() {
  if (!this.debug) return;
  if (!this.context.console || !this.context.console.error) return;
  var args = Array.prototype.slice.call(arguments);
  this.context.console.error.apply(this.context.console, args);
};

Terminal.prototype.resize = function(x, y) {
  var line
    , el
    , i
    , j
    , ch
    , colsChanged = true
    , rowsChanged = true;

  if (x < 1) x = 1;
  if (y < 1) y = 1;

  // resize cols
  j = this.cols;
  if (j < x) {
    ch = [this.defAttr, ' ']; // does xterm use the default attr?
    i = this.lines.length;
    while (i--) {
      while (this.lines[i].length < x) {
        this.lines[i].push(ch);
      }
    }
  } else if (j > x) {
    i = this.lines.length;
    while (i--) {
      while (this.lines[i].length > x) {
        this.lines[i].pop();
      }
    }
  } else {
	  colsChanged = false;
  }
  
  // PJS: only call setupStops if it appears that the default 8-column tabs are set
  // this.setupStops(j);
  if (this.tabs[0] && this.tabs[8]) {
	  this.setupStops();	  
  }
  
  this.cols = x;

  // resize rows
  j = this.rows;
  if (j < y) {
    el = this.element;
    while (j++ < y) {
      if (this.lines.length < y + this.ybase) {
        this.lines.push(this.blankLine());
      }
      if (this.children.length < y) {
        line = this.document.createElement('div');
        el.appendChild(line);
        this.children.push(line);
      }
    }
  } else if (j > y) {
    while (j-- > y) {
      if (this.lines.length > y + this.ybase) {
        this.lines.pop();
      }
      if (this.children.length > y) {
        el = this.children.pop();
        if (!el) continue;
        el.parentNode.removeChild(el);
      }
    }
  } else {
    rowsChanged = false;
  }
  this.rows = y;

  // make sure the cursor stays on screen
  if (this.y >= y) this.y = y - 1;
  if (this.x >= x) this.x = x - 1;

  //TODO: DECSCPP does not reset the horizontal or vertical scrolling region!
  this.scrollTop = 0;
  this.scrollBottom = y - 1;

  this.delayWrap = false; // PJS
  
  // PJS: handle font scaling
  if (colsChanged || rowsChanged) {
    this.updateFont(); // scale font for new screen size
    this.resizeTerminalViewport(); // adjust padding for new font size
    this.updateImages(); // make sure images are scaled & positioned
  }
  
  this.refresh(0, this.rows - 1);
  this.resetRange(); //PJS: reset refresh range after refresh
  
  // it's a real nightmare trying
  // to resize the original
  // screen buffer. just set it
  // to null for now.
  this.normal = null;
};

// PJS: new function to resize the scrollback buffer - fixup lines[] & history div
Terminal.prototype.resizeHistory = function(y) {
	var n;
	if (y < 0) {
		return;
	} else if (y > 1000) {
		y = 1000; // max scrollback lines	
	}
	if (y === this.scrollback) {
		return;
	}
	if (y < this.scrollback) {
		// reduce size of history
		n = this.ybase; // number of lines in scrollback
		if (y < n) {
			// remove extra lines
			this.lines.splice(0, n - y);
			this.ybase -= (n - y);
			this.ydisp = this.ybase;
		}			
		this.scrollback = this.pendingScrollback = y;
		this.updateHistory();		
	} else {
		this.scrollback = y;
	}
};

Terminal.prototype.updateRange = function(y) {
  if (y < this.refreshStart) this.refreshStart = y;
  if (y > this.refreshEnd) this.refreshEnd = y;
  // if (y > this.refreshEnd) {
  //   this.refreshEnd = y;
  //   if (y > this.rows - 1) {
  //     this.refreshEnd = this.rows - 1;
  //   }
  // }
  this.scheduleUpdate(); //PJS: schedule a refresh
};

Terminal.prototype.maxRange = function() {
  this.refreshStart = 0;
  this.refreshEnd = this.rows - 1;
  this.scheduleUpdate(); //PJS: schedule a refresh
};

//PJS: new function to initialize refresh range
Terminal.prototype.resetRange = function() {
	this.refreshStart = this.rows - 1;
	this.refreshEnd = 0;
};

Terminal.prototype.setupStops = function(i) {
  if (i != null) {
    if (!this.tabs[i]) {
      i = this.prevStop(i);
    }
  } else {
    this.tabs = {};
    i = 0;
  }

  for (; i < this.cols; i += 8) {
    this.tabs[i] = true;
  }
};

Terminal.prototype.prevStop = function(x) {
  if (x == null) x = this.x;
  while (!this.tabs[--x] && x > 0);
  return x >= this.cols
    ? this.cols - 1
    : x < 0 ? 0 : x;
};

Terminal.prototype.nextStop = function(x) {
  if (x == null) x = this.x;
  while (!this.tabs[++x] && x < this.cols);
  return x >= this.cols
    ? this.cols - 1
    : x < 0 ? 0 : x;
};

Terminal.prototype.eraseRight = function(x, y) {
  var line = this.lines[this.ybase + y]
    , ch = [this.eraseAttr(), ' ']; // xterm

  for (; x < this.cols; x++) {
    line[x] = ch;
  }

  this.updateRange(y);
};

Terminal.prototype.eraseLeft = function(x, y) {
  var line = this.lines[this.ybase + y]
    , ch = [this.eraseAttr(), ' ']; // xterm

  x++;
  while (x--) line[x] = ch;

  this.updateRange(y);
};

Terminal.prototype.eraseLine = function(y, eraseLineAttr) {
  this.eraseRight(0, y);
  if(eraseLineAttr) {
	  this.lineAttributes(y, 0);
  }
};

//PJS: support for protected characters
Terminal.prototype.eraseRightUnprotected = function(x, y) {
  var line = this.lines[this.ybase + y]
    , ch = [this.eraseAttr(), ' ']; // xterm

  for (; x < this.cols; x++) {
	if(!(line[x][2] & 0x01)) {
      line[x] = ch;
    }
  }

  this.updateRange(y);
};

//PJS: support for protected characters
Terminal.prototype.eraseLeftUnprotected = function(x, y) {
  var line = this.lines[this.ybase + y]
    , ch = [this.eraseAttr(), ' ']; // xterm

  x++;
  while (x--) {
	if(!(line[x][2] & 0x01)) {
      line[x] = ch;
    }
  }
  
  this.updateRange(y);
};

//PJS: support for protected characters
Terminal.prototype.eraseLineUnprotected = function(y) {
  this.eraseRightUnprotected(0, y);
};

Terminal.prototype.blankLine = function(cur) {
  /* PJS: changed true case from eraseAttr() to curAttr, since eraseAttr() always returns
   * defAttr now. Changed false case to return eraseAttr() instead of defAttr.
   * /	
/*  var attr = cur
    ? this.eraseAttr()
    : this.defAttr; */
  var attr = cur
    ? this.curAttr
    : this.eraseAttr();

  var ch = [attr, ' ']
    , line = []
    , i = 0;

  for (; i < this.cols; i++) {
    line[i] = ch;
  }

  return line;
};

Terminal.prototype.ch = function(cur) {
  /* PJS: changed true case from eraseAttr() to curAttr, since eraseAttr() always returns
   * defAttr now. Changed false case to return eraseAttr() instead of defAttr.
   * /	
/*  return cur
    ? [this.eraseAttr(), ' ']
    : [this.defAttr, ' ']; */
    return cur
	? [this.curAttr, ' ']
	: [this.eraseAttr(), ' '];
};

Terminal.prototype.is = function(term) {
  // PJS - case-insensitive (caller must use lower case name)
  var name = (this.termName + '').toLowerCase();
  return name.indexOf(term) === 0;
};

Terminal.prototype.handler = function(data) {
  this.emit('data', data);
};

Terminal.prototype.handleTitle = function(title) {
  this.emit('title', title);
};

//PJS: added new event to notify client of cursor change
Terminal.prototype.handleCursor = function(x, y, pg) {
  function debounced(x, y, pg) {
	this.inHandleCursor = null;
	// Fire cursor event (ATMobile resets the pan/zoom).
	this.emit('cursor', {x: x, y: y, pg: pg});
	// Make sure cursor position is scrolled into view.
	// Delay this a little in case cursor event needs a little
	// time to complete (see old ATMobile shift_o_matic function).
	setTimeout(this.scrollViewToCursorPos.bind(this), 1); 
  }
  if (this.inHandleCursor)
    clearTimeout(this.inHandleCursor);
  this.inHandleCursor = setTimeout(debounced.bind(this, x, y, pg), 50);
};

/**
 * ESC
 */

// ESC D Index (IND is 0x84).
Terminal.prototype.index = function() {
  //PJS: cursor can be placed outside of scrolling region when origin mode is disabled
  if (this.y > this.scrollBottom) {
	if (this.y < this.rows - 1) {
		this.y++;		
	}
	return;
  }
  this.y++;
  if (this.y > this.scrollBottom) {
    this.y--;
    this.scroll();
  }
  //this.state = normal;
};

// ESC M Reverse Index (RI is 0x8d).
Terminal.prototype.reverseIndex = function() {
  var j;
  //PJS: cursor can be placed outside of scrolling region when origin mode is disabled
  if (this.y < this.scrollTop) {
	if (this.y > 0) {
		this.y--;		
	}
	return;
  }
  this.y--;
  if (this.y < this.scrollTop) {
    this.y++;
    // possibly move the code below to term.reverseScroll();
    // test: echo -ne '\e[1;1H\e[44m\eM\e[0m'
    // blankLine(true) is xterm/linux behavior
    this.lines.splice(this.y + this.ybase, 0, this.blankLine()); //PJS: changed blankLine(true) to blankLine() to use eraseAttr() instead of curAttr
    j = this.rows - 1 - this.scrollBottom;
    this.lines.splice(this.rows - 1 + this.ybase - j + 1, 1);
    // this.maxRange();
    this.updateRange(this.scrollTop);
    this.updateRange(this.scrollBottom);
  }
  //this.state = normal;
};

// ESC c Full Reset (RIS).
Terminal.prototype.reset = function() {
  this.options.rows = this.rows;
  this.options.cols = this.cols;
  Terminal.call(this, this.options);
  // PJS: update the term-style css classes to use current colors & font size
  if (this.useStyle) {
	this.fontProps = this.getFontProps(this.fontName, this.fontPoints, this.cols, this.rows); //PJS: get our font properties
    if (this.fontProps) {
      Terminal.insertStyle(this.document, this.colors[256], this.colors[257], this.fontName, this.fontProps.fontSize, this.fontProps.scaleX, this.fontProps.scaleY, this.fontProps.fontHeight, this.bkgndPict, this.bkgndAlpha, this.bkgndMode); //PJS: pass font size & scale arguments & background image
    }
  }
  this.refresh(0, this.rows - 1);
  this.resetRange(); //PJS: reset the refresh range
  // PJS: clear the history
  var savescrollback = this.scrollback;
  this.scrollback = 0;
  this.updateHistory();
  this.scrollback = savescrollback;
};

// ESC H Tab Set (HTS is 0x88).
Terminal.prototype.tabSet = function() {
  this.tabs[this.x] = true;
  //this.state = normal;
};

/**
 * CSI
 */

// CSI Ps A
// Cursor Up Ps Times (default = 1) (CUU).
Terminal.prototype.cursorUp = function(params) {
  var param = params[0];
  if (param < 1) param = 1;
  // PJS: bug fix - dont move up beyond scrollTop
  //this.y -= param;
  //if (this.y < 0) this.y = 0;
  if (this.y < this.scrollTop) {
    this.y -= param;
    if (this.y < 0) this.y = 0;
  } else {
    this.y -= param;
	if (this.y < this.scrollTop) this.y = this.scrollTop;
  }
  this.delayWrap = false; // PJS
};

// CSI Ps B
// Cursor Down Ps Times (default = 1) (CUD).
Terminal.prototype.cursorDown = function(params) {
  var param = params[0];
  if (param < 1) param = 1;
  // PJS: bug fix - dont move down beyond scrollBottom
  //this.y += param;
  //if (this.y >= this.rows) {
  //  this.y = this.rows - 1;
  if (this.y > this.scrollBottom) {
    this.y += param;
    if (this.y >= this.rows) {
      this.y = this.rows - 1;
	}
  } else {
    this.y += param;
    if (this.y > this.scrollBottom) {
      this.y = this.scrollBottom;
	}
  }
  this.delayWrap = false; // PJS
};

// CSI Ps C
// Cursor Forward Ps Times (default = 1) (CUF).
Terminal.prototype.cursorForward = function(params) {
  var param = params[0];
  if (param < 1) param = 1;
  this.x += param;
  if (this.x >= this.cols) {
    this.x = this.cols - 1;
  }
};

// CSI Ps D
// Cursor Backward Ps Times (default = 1) (CUB).
Terminal.prototype.cursorBackward = function(params) {
  var param = params[0];
  if (param < 1) param = 1;
  this.x -= param;
  if (this.x < 0) this.x = 0;
  this.delayWrap = false; // PJS
};

// CSI Ps ; Ps H
// Cursor Position [row;column] (default = [1,1]) (CUP).
Terminal.prototype.cursorPos = function(params) {
  var row, col;

  row = params[0] - 1;

  if (params.length >= 2) {
    col = params[1] - 1;
  } else {
    col = 0;
  }

  //PJS - fixed bug - need to check row against scrollTop & scrollBottom in case of origin mode
  if (this.originMode) {
	row += this.scrollTop;
    if (row < this.scrollTop) {
  	  row = this.scrollTop;
    } else if (row > this.scrollBottom) {
	  row = this.scrollBottom;
    }
  } else {
    if (row < 0) {
      row = 0;
    } else if (row >= this.rows) {
      row = this.rows - 1;
    }
  }

  if (col < 0) {
    col = 0;
  } else if (col >= this.cols) {
    col = this.cols - 1;
  }

  this.x = col;
  this.y = row;
  
  this.delayWrap = false; // PJS
  
};

// CSI Ps J  Erase in Display (ED).
//     Ps = 0  -> Erase Below (default).
//     Ps = 1  -> Erase Above.
//     Ps = 2  -> Erase All.
//     Ps = 3  -> Erase Saved Lines (xterm).
// CSI ? Ps J
//   Erase in Display (DECSED).
//     Ps = 0  -> Selective Erase Below (default).
//     Ps = 1  -> Selective Erase Above.
//     Ps = 2  -> Selective Erase All.
Terminal.prototype.eraseInDisplay = function(params) {
  var i
    , j
	, line
	, histline
    , n = params[0] || 0;
  var eraseRight, eraseLeft, eraseLine;
  // PJS: added support for DECSED
  if (this.prefix === '?') {
	  eraseRight = this.eraseRightUnprotected;
	  eraseLeft = this.eraseLeftUnprotected;
	  eraseLine = this.eraseLineUnprotected;
  } else {
  	  eraseRight = this.eraseRight;
	  eraseLeft = this.eraseLeft;
	  eraseLine = this.eraseLine;
  }
  
  if (n === 0 && this.x === 0 && this.y === 0) {
	  n = 2; // erase entire screen (saves current screen to history before clearing)
  }
  
  switch (n) {
    case 0:
	  if(this.x > 0) {
        eraseRight.call(this, this.x, this.y);
        j = this.y + 1;
	  } else j = this.y; // we do it this way because when erasing entire line, we also need to clear the line attributes
      for (; j < this.rows; j++) {
        eraseLine.call(this, j, true);
      }
      break;
    case 1:
	  if(this.x < this.lastCol(this.y)) {
	    eraseLeft.call(this, this.x, this.y);
	    j = this.y;
      } else j = this.y + 1; // we do it this way because when erasing entire line, we also need to clear the line attributes
      while (j--) {
        eraseLine.call(this, j, true);
      }
      break;
    case 2:
	  // PJS: use new clearScreen function to handle history
	  this.clearScreen(this.prefix === '?');
      break;
    case 3:
      ; // no saved lines
      break;
  }
};

// PJS: new function to clear entire screen, saving to history before clearing
Terminal.prototype.clearScreen = function(unprotected) {
	  
  // PJS: this code is really strange! If we need to save the screen in history before
  // clearing, we can call scroll() to save to history and clear the screen. But, if we
  // are only clearing unprotected data, we need to restore the screen from the history
  // and re-clear using eraseLine() to maintain the protected data.
  var i
    , j
    , line
    , histline
	, eraseLine;
	
  if (unprotected) {
    eraseLine = this.eraseLineUnprotected;
  } else {
    eraseLine = this.eraseLine;
  }
	  
  if (this.scrollback >= this.rows && !this.hasScrollingRegion()) {
    // We need to save the screen in the history before clearing, so use scroll() instead of eraseLine().
    this.scroll(this.rows); // scroll the whole screen - saves to history & clears
    if (!unprotected) {      
      this.clearImages(); // Erase any images that are displayed
      return; // done unless selective erase
    }
    // Copy the scrolled-out lines from history back to live screen lines, then use eraseLine()
    // to erase only the unrotected data.
    if (this.ybase >= this.rows) {
      for (j = 0; j < this.rows; j++) {
        // make copy of line, dont want a reference
        histline = this.lines[j + this.ybase - this.rows]; // the scrolled-out line
        line = []; // the live line
        for (i = 0; i < this.cols; i++) {
          line[i] = histline[i]; // copy each cell (ref is OK, cause cleared cells get new array)
        }
        this.lines[j + this.ybase] = line;
      }
    }
  }
  
  // Either no scrollback, or need to erase unprotected data
  j = this.rows;
  while (j--) eraseLine.call(this, j, true);  
};



// CSI Ps K  Erase in Line (EL).
//     Ps = 0  -> Erase to Right (default).
//     Ps = 1  -> Erase to Left.
//     Ps = 2  -> Erase All.
// CSI ? Ps K
//   Erase in Line (DECSEL).
//     Ps = 0  -> Selective Erase to Right (default).
//     Ps = 1  -> Selective Erase to Left.
//     Ps = 2  -> Selective Erase All.
Terminal.prototype.eraseInLine = function(params) {
  // PJS: add support for DECSEL
  var eraseRight, eraseLeft, eraseLine;
  if (this.prefix === '?') {
	  eraseRight = this.eraseRightUnprotected;
	  eraseLeft = this.eraseLeftUnprotected;
	  eraseLine = this.eraseLineUnprotected;
  } else {
  	  eraseRight = this.eraseRight;
	  eraseLeft = this.eraseLeft;
	  eraseLine = this.eraseLine;
  }
  switch (params[0] || 0) {
    case 0:
      eraseRight.call(this, this.x, this.y);
      break;
    case 1:
      eraseLeft.call(this, this.x, this.y);
      break;
    case 2:
      eraseLine.call(this, this.y);
      break;
  }
};

// CSI Pm m  Character Attributes (SGR).
//     Ps = 0  -> Normal (default).
//     Ps = 1  -> Bold.
//     Ps = 4  -> Underlined.
//     Ps = 5  -> Blink (appears as Bold).
//     Ps = 7  -> Inverse.
//     Ps = 8  -> Invisible, i.e., hidden (VT300).
//     Ps = 2 2  -> Normal (neither bold nor faint).
//     Ps = 2 4  -> Not underlined.
//     Ps = 2 5  -> Steady (not blinking).
//     Ps = 2 7  -> Positive (not inverse).
//     Ps = 2 8  -> Visible, i.e., not hidden (VT300).
//     Ps = 3 0  -> Set foreground color to Black.
//     Ps = 3 1  -> Set foreground color to Red.
//     Ps = 3 2  -> Set foreground color to Green.
//     Ps = 3 3  -> Set foreground color to Yellow.
//     Ps = 3 4  -> Set foreground color to Blue.
//     Ps = 3 5  -> Set foreground color to Magenta.
//     Ps = 3 6  -> Set foreground color to Cyan.
//     Ps = 3 7  -> Set foreground color to White.
//     Ps = 3 9  -> Set foreground color to default (original).
//     Ps = 4 0  -> Set background color to Black.
//     Ps = 4 1  -> Set background color to Red.
//     Ps = 4 2  -> Set background color to Green.
//     Ps = 4 3  -> Set background color to Yellow.
//     Ps = 4 4  -> Set background color to Blue.
//     Ps = 4 5  -> Set background color to Magenta.
//     Ps = 4 6  -> Set background color to Cyan.
//     Ps = 4 7  -> Set background color to White.
//     Ps = 4 9  -> Set background color to default (original).

//   If 16-color support is compiled, the following apply.  Assume
//   that xterm's resources are set so that the ISO color codes are
//   the first 8 of a set of 16.  Then the aixterm colors are the
//   bright versions of the ISO colors:
//     Ps = 9 0  -> Set foreground color to Black.
//     Ps = 9 1  -> Set foreground color to Red.
//     Ps = 9 2  -> Set foreground color to Green.
//     Ps = 9 3  -> Set foreground color to Yellow.
//     Ps = 9 4  -> Set foreground color to Blue.
//     Ps = 9 5  -> Set foreground color to Magenta.
//     Ps = 9 6  -> Set foreground color to Cyan.
//     Ps = 9 7  -> Set foreground color to White.
//     Ps = 1 0 0  -> Set background color to Black.
//     Ps = 1 0 1  -> Set background color to Red.
//     Ps = 1 0 2  -> Set background color to Green.
//     Ps = 1 0 3  -> Set background color to Yellow.
//     Ps = 1 0 4  -> Set background color to Blue.
//     Ps = 1 0 5  -> Set background color to Magenta.
//     Ps = 1 0 6  -> Set background color to Cyan.
//     Ps = 1 0 7  -> Set background color to White.

//   If xterm is compiled with the 16-color support disabled, it
//   supports the following, from rxvt:
//     Ps = 1 0 0  -> Set foreground and background color to
//     default.

//   If 88- or 256-color support is compiled, the following apply.
//     Ps = 3 8  ; 5  ; Ps -> Set foreground color to the second
//     Ps.
//     Ps = 4 8  ; 5  ; Ps -> Set background color to the second
//     Ps.
Terminal.prototype.charAttributes = function(params) {
  // Optimize a single SGR0.
  if (params.length === 1 && params[0] === 0) {
    this.curAttr = this.defAttr;
    return;
  }

  var l = params.length
    , i = 0
    , flags = this.curAttr >> 18
    , fg = (this.curAttr >> 9) & 0x1ff
    , bg = this.curAttr & 0x1ff
	, xg
    , p;

  //PJS: if inverse attribute is set, swap colors first
  if (flags & 8) {
	  xg = fg; fg = bg; bg = xg;
  }
  
  for (; i < l; i++) {
    p = params[i];
    if (p >= 30 && p <= 37) {
      // fg color 8
      fg = p - 30;
	  if (flags & 1) {
		  fg += 8; //PJS
	  }
    } else if (p >= 40 && p <= 47) {
      // bg color 8
      bg = p - 40;
    } else if (p >= 90 && p <= 97) {
      // fg color 16
      p += 8;
      fg = p - 90;
    } else if (p >= 100 && p <= 107) {
      // bg color 16
      p += 8;
      bg = p - 100;
    } else if (p === 0) {
      // default
      flags = this.defAttr >> 18;
      fg = (this.defAttr >> 9) & 0x1ff;
      bg = this.defAttr & 0x1ff;
      // flags = 0;
      // fg = 0x1ff;
      // bg = 0x1ff;
    } else if (p === 1) {
      // bold text
      flags |= 1;
	  if (fg >= 0 && fg <= 7) {
		  fg += 8; //PJS
	  }
    } else if (p === 4) {
      // underlined text
      flags |= 2;
    } else if (p === 5) {
      // blink
      flags |= 4;
    } else if (p === 7) {
      // inverse and positive
      // test with: echo -e '\e[31m\e[42mhello\e[7mworld\e[27mhi\e[m'
      flags |= 8;
    } else if (p === 8) {
      // invisible
      flags |= 16;
    } else if (p === 22) {
      // not bold
      flags &= ~1;
	  if (fg >= 8 && fg <= 15) {
		  fg -= 8; //PJS
	  }
    } else if (p === 24) {
      // not underlined
      flags &= ~2;
    } else if (p === 25) {
      // not blink
      flags &= ~4;
    } else if (p === 27) {
      // not inverse
      flags &= ~8;
    } else if (p === 28) {
      // not invisible
      flags &= ~16;
    } else if (p === 39) {
      // reset fg
      fg = (this.defAttr >> 9) & 0x1ff;
    } else if (p === 49) {
      // reset bg
      bg = this.defAttr & 0x1ff;
    } else if (p === 38) {
      // fg color 256
      if (params[i + 1] === 2) {
        i += 2;
        fg = matchColor(
          params[i] & 0xff,
          params[i + 1] & 0xff,
          params[i + 2] & 0xff);
        if (fg === -1) fg = 0x1ff;
        i += 2;
      } else if (params[i + 1] === 5) {
        i += 2;
        p = params[i] & 0xff;
        fg = p;
      }
    } else if (p === 48) {
      // bg color 256
      if (params[i + 1] === 2) {
        i += 2;
        bg = matchColor(
          params[i] & 0xff,
          params[i + 1] & 0xff,
          params[i + 2] & 0xff);
        if (bg === -1) bg = 0x1ff;
        i += 2;
      } else if (params[i + 1] === 5) {
        i += 2;
        p = params[i] & 0xff;
        bg = p;
      }
    } else if (p === 100) {
      // reset fg/bg
      fg = (this.defAttr >> 9) & 0x1ff;
      bg = this.defAttr & 0x1ff;
    } else {
      this.error('Unknown SGR attribute: %d.', p);
    }
  }
  
  //PJS: if inverse attribute is set, swap colors
  if (flags & 8) {
	  xg = fg; fg = bg; bg = xg;
  }

  this.curAttr = (flags << 18) | (fg << 9) | bg;
};

// PJS: add support for line attributes (double-size characters)
Terminal.prototype.lineAttributes = function(y, param) {
  // Set line attribute
  var line = this.lines[y + this.ybase];
  if(param) {
	line.lineAttr = param;
  } else {
	delete line.lineAttr;
  }
  this.updateRange(y);
};
  
// CSI Ps n  Device Status Report (DSR).
//     Ps = 5  -> Status Report.  Result (``OK'') is
//   CSI 0 n
//     Ps = 6  -> Report Cursor Position (CPR) [row;column].
//   Result is
//   CSI r ; c R
// CSI ? Ps n
//   Device Status Report (DSR, DEC-specific).
//     Ps = 6  -> Report Cursor Position (CPR) [row;column] as CSI
//     ? r ; c R (assumes page is zero).
//     Ps = 1 5  -> Report Printer status as CSI ? 1 0  n  (ready).
//     or CSI ? 1 1  n  (not ready).
//     Ps = 2 5  -> Report UDK status as CSI ? 2 0  n  (unlocked)
//     or CSI ? 2 1  n  (locked).
//     Ps = 2 6  -> Report Keyboard status as
//   CSI ? 2 7  ;  1  ;  0  ;  0  n  (North American).
//   The last two parameters apply to VT400 & up, and denote key-
//   board ready and LK01 respectively.
//     Ps = 5 3  -> Report Locator status as
//   CSI ? 5 3  n  Locator available, if compiled-in, or
//   CSI ? 5 0  n  No Locator, if not.
Terminal.prototype.deviceStatus = function(params) {
  if (!this.prefix) {
    switch (params[0]) {
      case 5:
        // status report
        this.send('\x1b[0n');
        break;
      case 6:
        // cursor position
		var y = this.originMode ? this.y + this.scrollTop : this.y; // PJS - handle origin mode in cursor report
        this.send('\x1b['
          + (y + 1)
          + ';'
          + (this.x + 1)
          + 'R');
        break;
    }
  } else if (this.prefix === '?') {
    // modern xterm doesnt seem to
    // respond to any of these except ?6, 6, and 5
    switch (params[0]) {
      case 6:
        // cursor position
        this.send('\x1b[?'
          + (this.y + 1)
          + ';'
          + (this.x + 1)
          + 'R');
        break;
      case 15:
        // no printer
        // this.send('\x1b[?11n');
        break;
      case 25:
        // dont support user defined keys
        // this.send('\x1b[?21n');
        break;
      case 26:
        // north american keyboard
        // this.send('\x1b[?27;1;0;0n');
        break;
      case 53:
        // no dec locator/mouse
        // this.send('\x1b[?50n');
        break;
    }
  }
};

/**
 * Additions
 */

// CSI Ps @
// Insert Ps (Blank) Character(s) (default = 1) (ICH).
Terminal.prototype.insertChars = function(params) {
  var param, row, j, ch;

  param = params[0];
  if (param < 1) param = 1;
//TODO: ICH has no effect outside horizontal scrolling region (PJS)
  row = this.y + this.ybase;
  j = this.x;
  ch = [this.eraseAttr(), ' ']; // xterm

  while (param-- && j < this.cols) {
    this.lines[row].splice(j++, 0, ch);
    this.lines[row].pop();
  }  
  this.updateRange(this.y);
};

// CSI Ps E
// Cursor Next Line Ps Times (default = 1) (CNL).
// same as CSI Ps B ?
Terminal.prototype.cursorNextLine = function(params) {
  var param = params[0];
  if (param < 1) param = 1;
  this.y += param;
  if (this.y >= this.rows) {
    this.y = this.rows - 1;
  }
  this.x = 0;
  this.delayWrap = false; // PJS
};

// CSI Ps F
// Cursor Preceding Line Ps Times (default = 1) (CNL).
// reuse CSI Ps A ?
Terminal.prototype.cursorPrecedingLine = function(params) {
  var param = params[0];
  if (param < 1) param = 1;
  this.y -= param;
  if (this.y < 0) this.y = 0;
  this.x = 0;
  this.delayWrap = false; // PJS
};

// CSI Ps G
// Cursor Character Absolute  [column] (default = [row,1]) (CHA).
Terminal.prototype.cursorCharAbsolute = function(params) {
  var param = params[0];
  if (param < 1) param = 1;
  this.x = param - 1;
  this.delayWrap = false; // PJS
};

// CSI Ps L
// Insert Ps Line(s) (default = 1) (IL).
Terminal.prototype.insertLines = function(params) {
  var param, row, j;

  param = params[0];
  if (param < 1) param = 1;
  row = this.y + this.ybase;

  j = this.rows - 1 - this.scrollBottom;
  j = this.rows - 1 + this.ybase - j + 1;

  while (param--) {
    // test: echo -e '\e[44m\e[1L\e[0m'
    // blankLine(true) - xterm/linux behavior
    this.lines.splice(row, 0, this.blankLine()); //PJS: changed blankLine(true) to blankLine() to use eraseAttr() instead of curAttr
    this.lines.splice(j, 1);
  }

  // this.maxRange();
  this.updateRange(this.y);
  this.updateRange(this.scrollBottom);
};

// CSI Ps M
// Delete Ps Line(s) (default = 1) (DL).
Terminal.prototype.deleteLines = function(params) {
  var param, row, j;

  param = params[0];
  if (param < 1) param = 1;
  row = this.y + this.ybase;

  j = this.rows - 1 - this.scrollBottom;
  j = this.rows - 1 + this.ybase - j;

  while (param--) {
    // test: echo -e '\e[44m\e[1M\e[0m'
    // blankLine(true) - xterm/linux behavior
    this.lines.splice(j + 1, 0, this.blankLine()); //PJS: changed blankLine(true) to blankLine() to use eraseAttr() instead of curAttr
    this.lines.splice(row, 1);
  }

  // this.maxRange();
  this.updateRange(this.y);
  this.updateRange(this.scrollBottom);
};

// CSI Ps P
// Delete Ps Character(s) (default = 1) (DCH).
Terminal.prototype.deleteChars = function(params) {
  var param, row, ch;

  param = params[0];
  if (param < 1) param = 1;
  //TODO: DCH has no effect outside horizontal scrolling region (PJS)
  row = this.y + this.ybase;
  ch = [this.eraseAttr(), ' ']; // xterm

  while (param--) {
    this.lines[row].splice(this.x, 1);
    this.lines[row].push(ch);
  }
  this.updateRange(this.y);
};

// CSI Ps X
// Erase Ps Character(s) (default = 1) (ECH).
Terminal.prototype.eraseChars = function(params) {
  var param, row, j, ch;

  param = params[0];
  if (param < 1) param = 1;

  row = this.y + this.ybase;
  j = this.x;
  ch = [this.eraseAttr(), ' ']; // xterm

  while (param-- && j < this.cols) {
    this.lines[row][j++] = ch;
  }
  this.updateRange(this.y);
};

// CSI Pm `  Character Position Absolute
//   [column] (default = [row,1]) (HPA).
Terminal.prototype.charPosAbsolute = function(params) {
  var param = params[0];
  if (param < 1) param = 1;
  this.x = param - 1;
  if (this.x >= this.cols) {
    this.x = this.cols - 1;
  }
  this.delayWrap = false; // PJS
};

// 141 61 a * HPR -
// Horizontal Position Relative
// reuse CSI Ps C ?
Terminal.prototype.HPositionRelative = function(params) {
  var param = params[0];
  if (param < 1) param = 1;
  this.x += param;
  if (this.x >= this.cols) {
    this.x = this.cols - 1;
  }
  this.delayWrap = false; // PJS
};

// CSI Ps c  Send Device Attributes (Primary DA).
//     Ps = 0  or omitted -> request attributes from terminal.  The
//     response depends on the decTerminalID resource setting.
//     -> CSI ? 1 ; 2 c  (``VT100 with Advanced Video Option'')
//     -> CSI ? 1 ; 0 c  (``VT101 with No Options'')
//     -> CSI ? 6 c  (``VT102'')
//     -> CSI ? 6 0 ; 1 ; 2 ; 6 ; 8 ; 9 ; 1 5 ; c  (``VT220'')
//   The VT100-style response parameters do not mean anything by
//   themselves.  VT220 parameters do, telling the host what fea-
//   tures the terminal supports:
//     Ps = 1  -> 132-columns.
//     Ps = 2  -> Printer.
//     Ps = 6  -> Selective erase.
//     Ps = 8  -> User-defined keys.
//     Ps = 9  -> National replacement character sets.
//     Ps = 1 5  -> Technical characters.
//     Ps = 2 2  -> ANSI color, e.g., VT525.
//     Ps = 2 9  -> ANSI text locator (i.e., DEC Locator mode).
// CSI > Ps c
//   Send Device Attributes (Secondary DA).
//     Ps = 0  or omitted -> request the terminal's identification
//     code.  The response depends on the decTerminalID resource set-
//     ting.  It should apply only to VT220 and up, but xterm extends
//     this to VT100.
//     -> CSI  > Pp ; Pv ; Pc c
//   where Pp denotes the terminal type
//     Pp = 0  -> ``VT100''.
//     Pp = 1  -> ``VT220''.
//   and Pv is the firmware version (for xterm, this was originally
//   the XFree86 patch number, starting with 95).  In a DEC termi-
//   nal, Pc indicates the ROM cartridge registration number and is
//   always zero.
// More information:
//   xterm/charproc.c - line 2012, for more information.
//   vim responds with ^[[?0c or ^[[?1c after the terminal's response (?)
Terminal.prototype.sendDeviceAttributes = function(params) {
  if (params[0] > 0) return;

  if (!this.prefix) {
    if (this.is('xterm')
        || this.is('rxvt-unicode')
        || this.is('screen')) {
      this.send('\x1b[?1;2c');
    } else if (this.is('linux')) {
      this.send('\x1b[?6c');
    } else if (this.is('vt100')) {
	  this.send('\x1b[?1;2c');
	} else if (this.is('vt220')) {
	  this.send('\x1b[?62;1;2;6;8c');
	} else if (this.is('vt320')) {
	  this.send('\x1b[?63;1;2;6;8c');
    } else if (this.is('vt420')) {
	  this.send('\x1b[?64;1;2;6;8c');
	}
  } else if (this.prefix === '>') {
    // xterm and urxvt
    // seem to spit this
    // out around ~370 times (?).
    if (this.is('xterm')) {
      this.send('\x1b[>0;276;0c');
    } else if (this.is('rxvt-unicode')) {
      this.send('\x1b[>85;95;0c');
    } else if (this.is('linux')) {
      // not supported by linux console.
      // linux console echoes parameters.
      this.send(params[0] + 'c');
    } else if (this.is('screen')) {
      this.send('\x1b[>83;40003;0c');
    } else if (this.is('vt220') || this.is('vt320') || this.is('vt420')) {
	  var info = {version: '0.0.0'};
	  this.emit('getinfo', info);
	  this.send('\x1b[>41;' + info.version.split('.')[0] + ';1c');
	}
  } else if (this.prefix === '=') {
	// DA3
    this.send('\x1bP!|00000000\x1b\\'); // PJS - AccuTerm sends product serial number in hex  
  }
};

// CSI Pm d
// Line Position Absolute  [row] (default = [1,column]) (VPA).
Terminal.prototype.linePosAbsolute = function(params) {
  var param = params[0];
  if (param < 1) param = 1;
  this.y = param - 1;
  if (this.y >= this.rows) {
    this.y = this.rows - 1;
  }
  this.delayWrap = false;
};

// 145 65 e * VPR - Vertical Position Relative
// reuse CSI Ps B ?
Terminal.prototype.VPositionRelative = function(params) {
  var param = params[0];
  if (param < 1) param = 1;
  this.y += param;
  if (this.y >= this.rows) {
    this.y = this.rows - 1;
  }
  this.delayWrap = false; // PJS
};

// CSI Ps ; Ps f
//   Horizontal and Vertical Position [row;column] (default =
//   [1,1]) (HVP).
Terminal.prototype.HVPosition = function(params) {
  this.cursorPos(params); // PJS: VT420 docs say HVP is identical to CUP!
  /*PJS: begin commented out block
  if (params[0] < 1) params[0] = 1;
  if (params[1] < 1) params[1] = 1;

  this.y = params[0] - 1;
  if (this.y >= this.rows) {
    this.y = this.rows - 1;
  }

  this.x = params[1] - 1;
  if (this.x >= this.cols) {
    this.x = this.cols - 1;
  }
  this.delayWrap = false; // PJS
  PJS: end commented out block */
};

// CSI Pm h  Set Mode (SM).
//     Ps = 2  -> Keyboard Action Mode (KAM).
//     Ps = 4  -> Insert Mode (IRM).
//     Ps = 1 2  -> Send/receive (SRM).
//     Ps = 2 0  -> Automatic Newline (LNM).
// CSI ? Pm h
//   DEC Private Mode Set (DECSET).
//     Ps = 1  -> Application Cursor Keys (DECCKM).
//     Ps = 2  -> Designate USASCII for character sets G0-G3
//     (DECANM), and set VT100 mode.
//     Ps = 3  -> 132 Column Mode (DECCOLM).
//     Ps = 4  -> Smooth (Slow) Scroll (DECSCLM).
//     Ps = 5  -> Reverse Video (DECSCNM).
//     Ps = 6  -> Origin Mode (DECOM).
//     Ps = 7  -> Wraparound Mode (DECAWM).
//     Ps = 8  -> Auto-repeat Keys (DECARM).
//     Ps = 9  -> Send Mouse X & Y on button press.  See the sec-
//     tion Mouse Tracking.
//     Ps = 1 0  -> Show toolbar (rxvt).
//     Ps = 1 2  -> Start Blinking Cursor (att610).
//     Ps = 1 8  -> Print form feed (DECPFF).
//     Ps = 1 9  -> Set print extent to full screen (DECPEX).
//     Ps = 2 5  -> Show Cursor (DECTCEM).
//     Ps = 3 0  -> Show scrollbar (rxvt).
//     Ps = 3 5  -> Enable font-shifting functions (rxvt).
//     Ps = 3 8  -> Enter Tektronix Mode (DECTEK).
//     Ps = 4 0  -> Allow 80 -> 132 Mode.
//     Ps = 4 1  -> more(1) fix (see curses resource).
//     Ps = 4 2  -> Enable Nation Replacement Character sets (DECN-
//     RCM).
//     Ps = 4 4  -> Turn On Margin Bell.
//     Ps = 4 5  -> Reverse-wraparound Mode.
//     Ps = 4 6  -> Start Logging.  This is normally disabled by a
//     compile-time option.
//     Ps = 4 7  -> Use Alternate Screen Buffer.  (This may be dis-
//     abled by the titeInhibit resource).
//     Ps = 6 6  -> Application keypad (DECNKM).
//     Ps = 6 7  -> Backarrow key sends backspace (DECBKM).
//     Ps = 1 0 0 0  -> Send Mouse X & Y on button press and
//     release.  See the section Mouse Tracking.
//     Ps = 1 0 0 1  -> Use Hilite Mouse Tracking.
//     Ps = 1 0 0 2  -> Use Cell Motion Mouse Tracking.
//     Ps = 1 0 0 3  -> Use All Motion Mouse Tracking.
//     Ps = 1 0 0 4  -> Send FocusIn/FocusOut events.
//     Ps = 1 0 0 5  -> Enable Extended Mouse Mode.
//     Ps = 1 0 1 0  -> Scroll to bottom on tty output (rxvt).
//     Ps = 1 0 1 1  -> Scroll to bottom on key press (rxvt).
//     Ps = 1 0 3 4  -> Interpret "meta" key, sets eighth bit.
//     (enables the eightBitInput resource).
//     Ps = 1 0 3 5  -> Enable special modifiers for Alt and Num-
//     Lock keys.  (This enables the numLock resource).
//     Ps = 1 0 3 6  -> Send ESC   when Meta modifies a key.  (This
//     enables the metaSendsEscape resource).
//     Ps = 1 0 3 7  -> Send DEL from the editing-keypad Delete
//     key.
//     Ps = 1 0 3 9  -> Send ESC  when Alt modifies a key.  (This
//     enables the altSendsEscape resource).
//     Ps = 1 0 4 0  -> Keep selection even if not highlighted.
//     (This enables the keepSelection resource).
//     Ps = 1 0 4 1  -> Use the CLIPBOARD selection.  (This enables
//     the selectToClipboard resource).
//     Ps = 1 0 4 2  -> Enable Urgency window manager hint when
//     Control-G is received.  (This enables the bellIsUrgent
//     resource).
//     Ps = 1 0 4 3  -> Enable raising of the window when Control-G
//     is received.  (enables the popOnBell resource).
//     Ps = 1 0 4 7  -> Use Alternate Screen Buffer.  (This may be
//     disabled by the titeInhibit resource).
//     Ps = 1 0 4 8  -> Save cursor as in DECSC.  (This may be dis-
//     abled by the titeInhibit resource).
//     Ps = 1 0 4 9  -> Save cursor as in DECSC and use Alternate
//     Screen Buffer, clearing it first.  (This may be disabled by
//     the titeInhibit resource).  This combines the effects of the 1
//     0 4 7  and 1 0 4 8  modes.  Use this with terminfo-based
//     applications rather than the 4 7  mode.
//     Ps = 1 0 5 0  -> Set terminfo/termcap function-key mode.
//     Ps = 1 0 5 1  -> Set Sun function-key mode.
//     Ps = 1 0 5 2  -> Set HP function-key mode.
//     Ps = 1 0 5 3  -> Set SCO function-key mode.
//     Ps = 1 0 6 0  -> Set legacy keyboard emulation (X11R6).
//     Ps = 1 0 6 1  -> Set VT220 keyboard emulation.
//     Ps = 2 0 0 4  -> Set bracketed paste mode.
// Modes:
//   http://vt100.net/docs/vt220-rm/chapter4.html
Terminal.prototype.setMode = function(params) {
  if (typeof params === 'object') {
    var l = params.length
      , i = 0
	  , x
	  , y;

    for (; i < l; i++) {
      this.setMode(params[i]);
    }

    return;
  }

  if (!this.prefix) {
    switch (params) {
	  case 2: // PJS - added support for KAM
		this.kbdLock = true; // set KAM
		break;
      case 4:
        this.insertMode = true; // set IRM
        break;
      case 20:
        this.convertEol = true; // PJS - uncommented to support set LNM
        break;
    }
  } else if (this.prefix === '?') {
    switch (params) {
      case 1:
		if (!this.applicationCursor) {
          this.applicationCursor = this.allowApplicationMode; // PJS: override if disabled in settings
          if (this.initDfltKbd) {
            this.initDfltKbd(this.applicationCursor, this.applicationKeypad, this.backspaceDel, this.eightBitControls);
          }
		}
        break;
      case 2:
        this.setgCharset(0, Terminal.charsets.US);
        this.setgCharset(1, Terminal.charsets.US);
        this.setgCharset(2, Terminal.charsets.US);
        this.setgCharset(3, Terminal.charsets.US);
        // set VT100 mode here
        break;
      case 3: // 132 col mode
		//PJS: use scrMode instead of savedCols to indicate normal (80 column) or extended (132 column) geometry
		if (!this.scrMode) { 
          this.scrMode = 1; //PJS
		  x = this.extCols || 132; //PJS
		  y = this.extRows || this.rows; //PJS
          this.resize(x, y);
		}
        this.clearScreen(false); // clear screen including line attributes
		this.cursorPos([1,1]);
        break;
	  case 5: // PJS - add support for DECSCNM
		if(!this.reverseVideo) {
	      this.reverseVideo = true;
		  this.maxRange();
		}
		break;
      case 6:
        this.originMode = true;
		this.cursorPos([1,1]); //PJS - after changing origin mode, must home cursor
        break;
      case 7:
        this.wraparoundMode = true;
        break;
      case 12:
        // ??
        break;
	  case 25:
	    this.cursorHidden = false; //PJS - show cursor
		break;
      case 66:
        this.log('Serial port requested application keypad.');
		if (!this.applicationKeypad) {
		  this.applicationKeypad = this.allowApplicationMode; // PJS: override if disabled in settings
          if (this.initDfltKbd) {
            this.initDfltKbd(this.applicationCursor, this.applicationKeypad, this.backspaceDel, this.eightBitControls);
          }
	    }
        break;
	  case 67: // PJS - add support for DECBKM
	    if (this.backspaceDel) {
          this.backspaceDel = false;
          if (this.initDfltKbd) {
            this.initDfltKbd(this.applicationCursor, this.applicationKeypad, this.backspaceDel, this.eightBitControls);
          }
	    }
	    break;
      case 9: // X10 Mouse
        // no release, no motion, no wheel, no modifiers.
      case 1000: // vt200 mouse
        // no motion.
        // no modifiers, except control on the wheel.
      case 1002: // button event mouse
      case 1003: // any event mouse
        // any event - sends motion events,
        // even if there is no button held down.
        this.x10Mouse = params === 9;
        this.vt200Mouse = params === 1000;
        this.normalMouse = params > 1000;
        this.mouseEvents = true;
        this.element.style.cursor = 'default';
        this.log('Binding to mouse events.');
        break;
      case 1004: // send focusin/focusout events
        // focusin: ^[[I
        // focusout: ^[[O
        this.sendFocus = true;
        break;
      case 1005: // utf8 ext mode mouse
        this.utfMouse = true;
        // for wide terminals
        // simply encodes large values as utf8 characters
        break;
      case 1006: // sgr ext mode mouse
        this.sgrMouse = true;
        // for wide terminals
        // does not add 32 to fields
        // press: ^[[<b;x;yM
        // release: ^[[<b;x;ym
        break;
      case 1015: // urxvt ext mode mouse
        this.urxvtMouse = true;
        // for wide terminals
        // numbers for fields
        // press: ^[[b;x;yM
        // motion: ^[[b;x;yT
        break;
      case 1049: // alt screen buffer cursor
        //this.saveCursor();
        ; // FALL-THROUGH
      case 47: // alt screen buffer
      case 1047: // alt screen buffer
        if (!this.normal) {
          var normal = {
            lines: this.lines,
            ybase: this.ybase,
            ydisp: this.ydisp,
            x: this.x,
            y: this.y,
            scrollTop: this.scrollTop,
            scrollBottom: this.scrollBottom,
            tabs: this.tabs
            // XXX save charset(s) here?
            // charset: this.charset,
            // glevel: this.glevel,
            // charsets: this.charsets
          };
          this.reset();
          this.normal = normal;
          this.showCursor();
        }
        break;
    }
  }
};

// CSI Pm l  Reset Mode (RM).
//     Ps = 2  -> Keyboard Action Mode (KAM).
//     Ps = 4  -> Replace Mode (IRM).
//     Ps = 1 2  -> Send/receive (SRM).
//     Ps = 2 0  -> Normal Linefeed (LNM).
// CSI ? Pm l
//   DEC Private Mode Reset (DECRST).
//     Ps = 1  -> Normal Cursor Keys (DECCKM).
//     Ps = 2  -> Designate VT52 mode (DECANM).
//     Ps = 3  -> 80 Column Mode (DECCOLM).
//     Ps = 4  -> Jump (Fast) Scroll (DECSCLM).
//     Ps = 5  -> Normal Video (DECSCNM).
//     Ps = 6  -> Normal Cursor Mode (DECOM).
//     Ps = 7  -> No Wraparound Mode (DECAWM).
//     Ps = 8  -> No Auto-repeat Keys (DECARM).
//     Ps = 9  -> Don't send Mouse X & Y on button press.
//     Ps = 1 0  -> Hide toolbar (rxvt).
//     Ps = 1 2  -> Stop Blinking Cursor (att610).
//     Ps = 1 8  -> Don't print form feed (DECPFF).
//     Ps = 1 9  -> Limit print to scrolling region (DECPEX).
//     Ps = 2 5  -> Hide Cursor (DECTCEM).
//     Ps = 3 0  -> Don't show scrollbar (rxvt).
//     Ps = 3 5  -> Disable font-shifting functions (rxvt).
//     Ps = 4 0  -> Disallow 80 -> 132 Mode.
//     Ps = 4 1  -> No more(1) fix (see curses resource).
//     Ps = 4 2  -> Disable Nation Replacement Character sets (DEC-
//     NRCM).
//     Ps = 4 4  -> Turn Off Margin Bell.
//     Ps = 4 5  -> No Reverse-wraparound Mode.
//     Ps = 4 6  -> Stop Logging.  (This is normally disabled by a
//     compile-time option).
//     Ps = 4 7  -> Use Normal Screen Buffer.
//     Ps = 6 6  -> Numeric keypad (DECNKM).
//     Ps = 6 7  -> Backarrow key sends delete (DECBKM).
//     Ps = 1 0 0 0  -> Don't send Mouse X & Y on button press and
//     release.  See the section Mouse Tracking.
//     Ps = 1 0 0 1  -> Don't use Hilite Mouse Tracking.
//     Ps = 1 0 0 2  -> Don't use Cell Motion Mouse Tracking.
//     Ps = 1 0 0 3  -> Don't use All Motion Mouse Tracking.
//     Ps = 1 0 0 4  -> Don't send FocusIn/FocusOut events.
//     Ps = 1 0 0 5  -> Disable Extended Mouse Mode.
//     Ps = 1 0 1 0  -> Don't scroll to bottom on tty output
//     (rxvt).
//     Ps = 1 0 1 1  -> Don't scroll to bottom on key press (rxvt).
//     Ps = 1 0 3 4  -> Don't interpret "meta" key.  (This disables
//     the eightBitInput resource).
//     Ps = 1 0 3 5  -> Disable special modifiers for Alt and Num-
//     Lock keys.  (This disables the numLock resource).
//     Ps = 1 0 3 6  -> Don't send ESC  when Meta modifies a key.
//     (This disables the metaSendsEscape resource).
//     Ps = 1 0 3 7  -> Send VT220 Remove from the editing-keypad
//     Delete key.
//     Ps = 1 0 3 9  -> Don't send ESC  when Alt modifies a key.
//     (This disables the altSendsEscape resource).
//     Ps = 1 0 4 0  -> Do not keep selection when not highlighted.
//     (This disables the keepSelection resource).
//     Ps = 1 0 4 1  -> Use the PRIMARY selection.  (This disables
//     the selectToClipboard resource).
//     Ps = 1 0 4 2  -> Disable Urgency window manager hint when
//     Control-G is received.  (This disables the bellIsUrgent
//     resource).
//     Ps = 1 0 4 3  -> Disable raising of the window when Control-
//     G is received.  (This disables the popOnBell resource).
//     Ps = 1 0 4 7  -> Use Normal Screen Buffer, clearing screen
//     first if in the Alternate Screen.  (This may be disabled by
//     the titeInhibit resource).
//     Ps = 1 0 4 8  -> Restore cursor as in DECRC.  (This may be
//     disabled by the titeInhibit resource).
//     Ps = 1 0 4 9  -> Use Normal Screen Buffer and restore cursor
//     as in DECRC.  (This may be disabled by the titeInhibit
//     resource).  This combines the effects of the 1 0 4 7  and 1 0
//     4 8  modes.  Use this with terminfo-based applications rather
//     than the 4 7  mode.
//     Ps = 1 0 5 0  -> Reset terminfo/termcap function-key mode.
//     Ps = 1 0 5 1  -> Reset Sun function-key mode.
//     Ps = 1 0 5 2  -> Reset HP function-key mode.
//     Ps = 1 0 5 3  -> Reset SCO function-key mode.
//     Ps = 1 0 6 0  -> Reset legacy keyboard emulation (X11R6).
//     Ps = 1 0 6 1  -> Reset keyboard emulation to Sun/PC style.
//     Ps = 2 0 0 4  -> Reset bracketed paste mode.
Terminal.prototype.resetMode = function(params) {
  if (typeof params === 'object') {
    var l = params.length
      , i = 0
	  , x
	  , y;

    for (; i < l; i++) {
      this.resetMode(params[i]);
    }

    return;
  }

  if (!this.prefix) {
    switch (params) {
	  case 2: // PJS - added support for KAM
		this.kbdLock = false; // reset KAM
		break;
      case 4:
        this.insertMode = false; // reset IRM
        break;
      case 20:
        this.convertEol = false; // PJS - uncommented to support reset LNM
        break;
    }
  } else if (this.prefix === '?') {
    switch (params) {
      case 1:
		if (this.applicationCursor) {
          this.applicationCursor = false;
          if (this.initDfltKbd) {
            this.initDfltKbd(this.applicationCursor, this.applicationKeypad, this.backspaceDel, this.eightBitControls);
          }
		}
        break;
      case 3:
		//PJS: use scrMode instead of savedCols to indicate normal (80 column) or extended (132 column) geometry
        if (this.scrMode) {
		  this.scrMode = 0; //PJS
		  x = this.normCols || 80; //PJS
		  y = this.normRows || this.rows; //PJS
          this.resize(x, y);
		}
        this.clearScreen(false); // clear screen including line attributes
		this.cursorPos([1,1]);        
        //PJS: delete this.savedCols;
        break;
	  case 5: // PJS - add support for DECSCNM
		if(this.reverseVideo) {
		  this.reverseVideo = false;
		  this.maxRange();
		}
		break;
      case 6:
        this.originMode = false;
		this.cursorPos([1,1]); //PJS - after changing origin mode, must home cursor
        break;
      case 7:
        this.wraparoundMode = false;
        break;
      case 12:
        // ??
        break;
	  case 25:
		this.cursorHidden = true; //PJS - hide cursor
		break;
      case 66:
		if (this.applicationKeypad) {
          this.log('Switching back to normal keypad.');
          this.applicationKeypad = false;
          if (this.initDfltKbd) {
            this.initDfltKbd(this.applicationCursor, this.applicationKeypad, this.backspaceDel, this.eightBitControls);
          }
	    }
        break;
	  case 67: // PJS - add support for DECBKM
	    if (!this.backspaceDel) {
          this.backspaceDel = true;
          if (this.initDfltKbd) {
            this.initDfltKbd(this.applicationCursor, this.applicationKeypad, this.backspaceDel, this.eightBitControls);
          }
	    }
	    break;
      case 9: // X10 Mouse
      case 1000: // vt200 mouse
      case 1002: // button event mouse
      case 1003: // any event mouse
        this.x10Mouse = false;
        this.vt200Mouse = false;
        this.normalMouse = false;
        this.mouseEvents = false;
        this.element.style.cursor = '';
        break;
      case 1004: // send focusin/focusout events
        this.sendFocus = false;
        break;
      case 1005: // utf8 ext mode mouse
        this.utfMouse = false;
        break;
      case 1006: // sgr ext mode mouse
        this.sgrMouse = false;
        break;
      case 1015: // urxvt ext mode mouse
        this.urxvtMouse = false;
        break;
      case 1049: // alt screen buffer cursor
        ; // FALL-THROUGH
      case 47: // normal screen buffer
      case 1047: // normal screen buffer - clearing it first
        if (this.normal) {
          this.lines = this.normal.lines;
          this.ybase = this.normal.ybase;
          this.ydisp = this.normal.ydisp;
          this.x = this.normal.x;
          this.y = this.normal.y;
          this.scrollTop = this.normal.scrollTop;
          this.scrollBottom = this.normal.scrollBottom;
          this.tabs = this.normal.tabs;
          this.normal = null;
          // if (params === 1049) {
          //   this.x = this.savedX;
          //   this.y = this.savedY;
          // }
          this.refresh(0, this.rows - 1);
		  this.resetRange(); //PJS: reset refresh range after refresh
          this.showCursor();
        }
        break;
    }
  }
};

// CSI Ps ; Ps r
//   Set Scrolling Region [top;bottom] (default = full size of win-
//   dow) (DECSTBM).
// CSI ? Pm r
Terminal.prototype.setScrollRegion = function(params) {
  if (this.prefix) return;
  this.scrollTop = (params[0] || 1) - 1;
  this.scrollBottom = (params[1] || this.rows) - 1;
  // PJS - fixed bug - should call cursorPos instead of setting to 0,0 - cursorPos checks origin mode
  //this.x = 0;
  //this.y = 0;
  this.cursorPos([1,1]);
};

// CSI s
//   Save cursor (ANSI.SYS).
Terminal.prototype.saveCursor = function(params) {
  // PJS: fixed to save more state info (per vt420 docs)
  this.savedX = this.x;
  this.savedY = this.y;
  this.savedAttr = this.curAttr;
  this.savedAutoWrap = this.wraparoundMode;
  this.savedOrigin = this.originMode;
  this.savedProt = this.protMode;
  this.savedShift = this.singleShift;
  this.savedgLevel = this.glevel;
  this.savedgrLevel = this.grlevel; // PJS
  this.savedCharsets = [null];
  each(this.charsets, function(el, key) {
    this.savedCharsets[key] = el;
  }, this);  
};

// CSI u
//   Restore cursor (ANSI.SYS).
Terminal.prototype.restoreCursor = function(params) {
  // PJS: fixed to restore more state info (per vt420 docs)
  this.x = this.savedX || 0;
  this.y = this.savedY || 0;
  this.curAttr = this.savedAttr || this.curAttr;
  this.wraparoundMode = this.savedAutoWrap || false;
  this.originMode = this.savedOrigin || false;
  this.protMode = this.savedProt || false;
  this.singleShift = this.savedShift;
  this.charsets = [null];
  each(this.savedCharsets, function(el, key) {
    this.charsets[key] = el;
  }, this);  
  this.setgLevel(this.savedgLevel, this.savedgrLevel); //PJS: added 2nd argument for GR
  this.delayWrap = false; // PJS
  this.updateRange(this.y);
};

/**
 * Lesser Used
 */

// CSI Ps I
//   Cursor Forward Tabulation Ps tab stops (default = 1) (CHT).
Terminal.prototype.cursorForwardTab = function(params) {
  var param = params[0] || 1;
  while (param--) {
    this.x = this.nextStop();
  }
};

// CSI Ps S  Scroll up Ps lines (default = 1) (SU).
Terminal.prototype.scrollUp = function(params) {
  //TODO: vt420 defines this "pan up" and moves the user window. Should be NOP (PJS)
  var param = params[0] || 1;
  while (param--) {
    this.lines.splice(this.ybase + this.scrollTop, 1);
    this.lines.splice(this.ybase + this.scrollBottom, 0, this.blankLine());
  }
  // this.maxRange();
  this.updateRange(this.scrollTop);
  this.updateRange(this.scrollBottom);
};

// CSI Ps T  Scroll down Ps lines (default = 1) (SD).
Terminal.prototype.scrollDown = function(params) {
  //TODO: vt420 defines this "pan down" and moves the user window. Should be NOP (PJS)
  var param = params[0] || 1;
  while (param--) {
    this.lines.splice(this.ybase + this.scrollBottom, 1);
    this.lines.splice(this.ybase + this.scrollTop, 0, this.blankLine());
  }
  // this.maxRange();
  this.updateRange(this.scrollTop);
  this.updateRange(this.scrollBottom);
};

// CSI Ps ; Ps ; Ps ; Ps ; Ps T
//   Initiate highlight mouse tracking.  Parameters are
//   [func;startx;starty;firstrow;lastrow].  See the section Mouse
//   Tracking.
Terminal.prototype.initMouseTracking = function(params) {
  // Relevant: DECSET 1001
};

// CSI > Ps; Ps T
//   Reset one or more features of the title modes to the default
//   value.  Normally, "reset" disables the feature.  It is possi-
//   ble to disable the ability to reset features by compiling a
//   different default for the title modes into xterm.
//     Ps = 0  -> Do not set window/icon labels using hexadecimal.
//     Ps = 1  -> Do not query window/icon labels using hexadeci-
//     mal.
//     Ps = 2  -> Do not set window/icon labels using UTF-8.
//     Ps = 3  -> Do not query window/icon labels using UTF-8.
//   (See discussion of "Title Modes").
Terminal.prototype.resetTitleModes = function(params) {
  ;
};

// CSI Ps Z  Cursor Backward Tabulation Ps tab stops (default = 1) (CBT).
Terminal.prototype.cursorBackwardTab = function(params) {
  var param = params[0] || 1;
  while (param--) {
    this.x = this.prevStop();
  }
};

// CSI Ps b  Repeat the preceding graphic character Ps times (REP).
Terminal.prototype.repeatPrecedingCharacter = function(params) {
  var param = params[0] || 1
    , line = this.lines[this.ybase + this.y]
    , ch
	, y = this.y;
  // PJS - fixed bug which could reference line position -1, fix line wrapping & scrolling
  if (this.x > 0) {
	ch = line[this.x - 1] || [this.defAttr, ' '];
	while (param--) {
	  line[this.x] = ch;
	  this.advanceCursor(); // PJS - use new function to advance the cursor - handles scrolling region, auto wrap, delayed wrap, scrolling
	  if (this.y !== y) {
		  this.updateRange(y);
		  y = this.y;
		  line = this.lines[this.ybase + y];
	  }
	}
	this.updateRange(y);
  }
};

// CSI Ps g  Tab Clear (TBC).
//     Ps = 0  -> Clear Current Column (default).
//     Ps = 3  -> Clear All.
// Potentially:
//   Ps = 2  -> Clear Stops on Line.
//   http://vt100.net/annarbor/aaa-ug/section6.html
Terminal.prototype.tabClear = function(params) {
  var param = params[0];
  if (param <= 0) {
    delete this.tabs[this.x];
  } else if (param === 3) {
    this.tabs = {};
  }
};

// CSI Pm i  Media Copy (MC).
//     Ps = 0  -> Print screen (default).
//     Ps = 4  -> Turn off printer controller mode.
//     Ps = 5  -> Turn on printer controller mode.
// CSI ? Pm i
//   Media Copy (MC, DEC-specific).
//     Ps = 1  -> Print line containing cursor.
//     Ps = 4  -> Turn off autoprint mode.
//     Ps = 5  -> Turn on autoprint mode.
//     Ps = 1  0  -> Print composed display, ignores DECPEX.
//     Ps = 1  1  -> Print all pages.
Terminal.prototype.mediaCopy = function(params) {
  var param = params[0];
  // TODO: print screen, print cursor line, send screen
  if (param === 4) {
	this.printer_off(false); // cancel autoprint / transparent print
  } else if (param === 5) {
	if (!this.prefix) {
	  this.printer_on(true); // start transparent print
	} else if (this.prefix === '?') {
	  this.printer_on(false); // autoprint (TODO: implement ANSI_AUTOPRINT)
	}
  }
};

// CSI > Ps; Ps m
//   Set or reset resource-values used by xterm to decide whether
//   to construct escape sequences holding information about the
//   modifiers pressed with a given key.  The first parameter iden-
//   tifies the resource to set/reset.  The second parameter is the
//   value to assign to the resource.  If the second parameter is
//   omitted, the resource is reset to its initial value.
//     Ps = 1  -> modifyCursorKeys.
//     Ps = 2  -> modifyFunctionKeys.
//     Ps = 4  -> modifyOtherKeys.
//   If no parameters are given, all resources are reset to their
//   initial values.
Terminal.prototype.setResources = function(params) {
  ;
};

// CSI > Ps n
//   Disable modifiers which may be enabled via the CSI > Ps; Ps m
//   sequence.  This corresponds to a resource value of "-1", which
//   cannot be set with the other sequence.  The parameter identi-
//   fies the resource to be disabled:
//     Ps = 1  -> modifyCursorKeys.
//     Ps = 2  -> modifyFunctionKeys.
//     Ps = 4  -> modifyOtherKeys.
//   If the parameter is omitted, modifyFunctionKeys is disabled.
//   When modifyFunctionKeys is disabled, xterm uses the modifier
//   keys to make an extended sequence of functions rather than
//   adding a parameter to each function key to denote the modi-
//   fiers.
Terminal.prototype.disableModifiers = function(params) {
  ;
};

// CSI > Ps p
//   Set resource value pointerMode.  This is used by xterm to
//   decide whether to hide the pointer cursor as the user types.
//   Valid values for the parameter:
//     Ps = 0  -> never hide the pointer.
//     Ps = 1  -> hide if the mouse tracking mode is not enabled.
//     Ps = 2  -> always hide the pointer.  If no parameter is
//     given, xterm uses the default, which is 1 .
Terminal.prototype.setPointerMode = function(params) {
  ;
};

// CSI ! p   Soft terminal reset (DECSTR).
// http://vt100.net/docs/vt220-rm/table4-10.html
Terminal.prototype.softReset = function(params) {
  this.cursorHidden = false;
  this.insertMode = false;
  this.originMode = false;
  this.wraparoundMode = this.options.wraparoundMode || false;
  this.applicationKeypad = this.options.applicationKeypad || false; // ?
  this.applicationCursor = this.options.applicationCursor || false;
  this.backspaceDel = this.options.backspaceDel || true; //PJS
  this.eightBitControls = this.options.eightBitControls || false; //PJS
  this.scrollTop = 0;
  this.scrollBottom = this.rows - 1;
  this.curAttr = this.defAttr;
  this.protMode = false; //PJS
  this.x = this.y = 0; // ?
  this.delayWrap = false; //PJS
  this.kbdLock = false; //PJS
  this.charset = null;
  this.glevel = 0; // ??
  this.grlevel = 0; //PJS
  this.charsets = [null]; // ??
  this.singleShift = null; //PJS
  
  this.savedAttr = this.curAttr; //PJS
  this.savedAutoWrap = this.wraparoundMode; //PJS
  this.savedOrigin = this.originMode; //PJS
  this.savedProt = this.protMode; //PJS
  this.savedShift = this.singleShift; //PJS
  this.savedgLevel = this.glevel; //PJS
  this.savedgrLevel = this.grlevel; //PJS
  this.savedCharsets = [null]; //PJS
};

// CSI Ps$ p
//   Request ANSI mode (DECRQM).  For VT300 and up, reply is
//     CSI Ps; Pm$ y
//   where Ps is the mode number as in RM, and Pm is the mode
//   value:
//     0 - not recognized
//     1 - set
//     2 - reset
//     3 - permanently set
//     4 - permanently reset
Terminal.prototype.requestAnsiMode = function(params) {
  ;
};

// CSI ? Ps$ p
//   Request DEC private mode (DECRQM).  For VT300 and up, reply is
//     CSI ? Ps; Pm$ p
//   where Ps is the mode number as in DECSET, Pm is the mode value
//   as in the ANSI DECRQM.
Terminal.prototype.requestPrivateMode = function(params) {
  ;
};

// CSI Ps ; Ps " p
//   Set conformance level (DECSCL).  Valid values for the first
//   parameter:
//     Ps = 6 1  -> VT100.
//     Ps = 6 2  -> VT200.
//     Ps = 6 3  -> VT300.
//   Valid values for the second parameter:
//     Ps = 0  -> 8-bit controls.
//     Ps = 1  -> 7-bit controls (always set for VT100).
//     Ps = 2  -> 8-bit controls.
Terminal.prototype.setConformanceLevel = function(params) {
  // PJS - add support for DECSCL
  var level = params[0] || 61
    , ebc = params[1] || 0;
  if (level === 61 || level === 62 || level === 63 || level === 64) {
    ebc = (ebc === 0 || ebc === 2) && (level !== 61);
	if (this.eightBitControls !== ebc) {
      this.eightBitControls = false;
      if (this.initDfltKbd) {
        this.initDfltKbd(this.applicationCursor, this.applicationKeypad, this.backspaceDel, this.eightBitControls);
      }
	}
  }
  
};

// CSI Ps q  Load LEDs (DECLL).
//     Ps = 0  -> Clear all LEDS (default).
//     Ps = 1  -> Light Num Lock.
//     Ps = 2  -> Light Caps Lock.
//     Ps = 3  -> Light Scroll Lock.
//     Ps = 2  1  -> Extinguish Num Lock.
//     Ps = 2  2  -> Extinguish Caps Lock.
//     Ps = 2  3  -> Extinguish Scroll Lock.
Terminal.prototype.loadLEDs = function(params) {
  ;
};

// CSI Ps SP q
//   Set cursor style (DECSCUSR, VT520).
//     Ps = 0  -> blinking block.
//     Ps = 1  -> blinking block (default).
//     Ps = 2  -> steady block.
//     Ps = 3  -> blinking underline.
//     Ps = 4  -> steady underline.
Terminal.prototype.setCursorStyle = function(params) {
  this.cursorStyle = params[0] || 0;
};

// CSI Ps " q
//   Select character protection attribute (DECSCA).  Valid values
//   for the parameter:
//     Ps = 0  -> DECSED and DECSEL can erase (default).
//     Ps = 1  -> DECSED and DECSEL cannot erase.
//     Ps = 2  -> DECSED and DECSEL can erase.
Terminal.prototype.setCharProtectionAttr = function(params) {
  //PJS - add support for DECSCA
  var param = params[0] || 0;
  if (param === 0 || param === 2) {
	this.protMode = false;
  } else if (param === 1) {
	this.protMode = true;
  }
};

// CSI ? Pm r
//   Restore DEC Private Mode Values.  The value of Ps previously
//   saved is restored.  Ps values are the same as for DECSET.
Terminal.prototype.restorePrivateValues = function(params) {
  ;
};

// CSI Pt; Pl; Pb; Pr; Ps$ r
//   Change Attributes in Rectangular Area (DECCARA), VT400 and up.
//     Pt; Pl; Pb; Pr denotes the rectangle.
//     Ps denotes the SGR attributes to change: 0, 1, 4, 5, 7.
// NOTE: xterm doesn't enable this code by default.
Terminal.prototype.setAttrInRectangle = function(params) {
  var t = params[0]
    , l = params[1]
    , b = params[2]
    , r = params[3]
    , attr = params[4];

  var line
    , i;

  for (; t < b + 1; t++) {
    line = this.lines[this.ybase + t];
    for (i = l; i < r; i++) {
      line[i] = [attr, line[i][1]];
    }
  }

  // this.maxRange();
  this.updateRange(params[0]);
  this.updateRange(params[2]);
};

// CSI ? Pm s
//   Save DEC Private Mode Values.  Ps values are the same as for
//   DECSET.
Terminal.prototype.savePrivateValues = function(params) {
  ;
};

// CSI Ps ; Ps ; Ps t
//   Window manipulation (from dtterm, as well as extensions).
//   These controls may be disabled using the allowWindowOps
//   resource.  Valid values for the first (and any additional
//   parameters) are:
//     Ps = 1  -> De-iconify window.
//     Ps = 2  -> Iconify window.
//     Ps = 3  ;  x ;  y -> Move window to [x, y].
//     Ps = 4  ;  height ;  width -> Resize the xterm window to
//     height and width in pixels.
//     Ps = 5  -> Raise the xterm window to the front of the stack-
//     ing order.
//     Ps = 6  -> Lower the xterm window to the bottom of the
//     stacking order.
//     Ps = 7  -> Refresh the xterm window.
//     Ps = 8  ;  height ;  width -> Resize the text area to
//     [height;width] in characters.
//     Ps = 9  ;  0  -> Restore maximized window.
//     Ps = 9  ;  1  -> Maximize window (i.e., resize to screen
//     size).
//     Ps = 1 0  ;  0  -> Undo full-screen mode.
//     Ps = 1 0  ;  1  -> Change to full-screen.
//     Ps = 1 1  -> Report xterm window state.  If the xterm window
//     is open (non-iconified), it returns CSI 1 t .  If the xterm
//     window is iconified, it returns CSI 2 t .
//     Ps = 1 3  -> Report xterm window position.  Result is CSI 3
//     ; x ; y t
//     Ps = 1 4  -> Report xterm window in pixels.  Result is CSI
//     4  ;  height ;  width t
//     Ps = 1 8  -> Report the size of the text area in characters.
//     Result is CSI  8  ;  height ;  width t
//     Ps = 1 9  -> Report the size of the screen in characters.
//     Result is CSI  9  ;  height ;  width t
//     Ps = 2 0  -> Report xterm window's icon label.  Result is
//     OSC  L  label ST
//     Ps = 2 1  -> Report xterm window's title.  Result is OSC  l
//     label ST
//     Ps = 2 2  ;  0  -> Save xterm icon and window title on
//     stack.
//     Ps = 2 2  ;  1  -> Save xterm icon title on stack.
//     Ps = 2 2  ;  2  -> Save xterm window title on stack.
//     Ps = 2 3  ;  0  -> Restore xterm icon and window title from
//     stack.
//     Ps = 2 3  ;  1  -> Restore xterm icon title from stack.
//     Ps = 2 3  ;  2  -> Restore xterm window title from stack.
//     Ps >= 2 4  -> Resize to Ps lines (DECSLPP).
Terminal.prototype.manipulateWindow = function(params) {
  ;
};

// CSI Pt; Pl; Pb; Pr; Ps$ t
//   Reverse Attributes in Rectangular Area (DECRARA), VT400 and
//   up.
//     Pt; Pl; Pb; Pr denotes the rectangle.
//     Ps denotes the attributes to reverse, i.e.,  1, 4, 5, 7.
// NOTE: xterm doesn't enable this code by default.
Terminal.prototype.reverseAttrInRectangle = function(params) {
  ;
};

// CSI > Ps; Ps t
//   Set one or more features of the title modes.  Each parameter
//   enables a single feature.
//     Ps = 0  -> Set window/icon labels using hexadecimal.
//     Ps = 1  -> Query window/icon labels using hexadecimal.
//     Ps = 2  -> Set window/icon labels using UTF-8.
//     Ps = 3  -> Query window/icon labels using UTF-8.  (See dis-
//     cussion of "Title Modes")
Terminal.prototype.setTitleModeFeature = function(params) {
  ;
};

// CSI Ps SP t
//   Set warning-bell volume (DECSWBV, VT520).
//     Ps = 0  or 1  -> off.
//     Ps = 2 , 3  or 4  -> low.
//     Ps = 5 , 6 , 7 , or 8  -> high.
Terminal.prototype.setWarningBellVolume = function(params) {
  ;
};

// CSI Ps SP u
//   Set margin-bell volume (DECSMBV, VT520).
//     Ps = 1  -> off.
//     Ps = 2 , 3  or 4  -> low.
//     Ps = 0 , 5 , 6 , 7 , or 8  -> high.
Terminal.prototype.setMarginBellVolume = function(params) {
  ;
};

// CSI Pt; Pl; Pb; Pr; Pp; Pt; Pl; Pp$ v
//   Copy Rectangular Area (DECCRA, VT400 and up).
//     Pt; Pl; Pb; Pr denotes the rectangle.
//     Pp denotes the source page.
//     Pt; Pl denotes the target location.
//     Pp denotes the target page.
// NOTE: xterm doesn't enable this code by default.
Terminal.prototype.copyRectangle = function(params) {
  var ts = (params[0] || 1) - 1
    , ls = (params[1] || 1) - 1
    , bs = (params[2] || this.rows) - 1
    , rs = (params[3] || this.cols) - 1
    , ps = (params[4] || 1) - 1
	, td = (params[5] || 1) - 1
	, ld = (params[6] || 1) - 1
	, pd = (params[7] || 1) - 1
	, action = (params[8] || 1);
  
  // PJS: added support for multiple pages.
  // Added extra params[8] = action code, so we can reuse this
  // function from other emulations (Wyse has copy, move & swap
  // actions = 1, 2 or 3).
  
  var srcline
    , dstline
	, srclines
	, dstlines
	, srcbase
	, dstbase
    , x
	, y
	, nx
	, ny
	, dx = 1
	, dy = 1
	, xd
	, yd
	, xs
	, ys
	, ch;

  // adjust for origin mode
  if(this.originMode) {
	ts += this.scrollTop;
	bs += this.scrollTop;
	td += this.scrollTop;
  }
  
  // check parameters
  if(ls < 0) ls = 0;
  if(rs >= this.cols) rs = this.cols - 1;
  if(ts < 0) ts = 0;
  if(bs >= this.rows) bs = this.rows - 1;
  if(rs < ls || bs < ts || ld < 0 || ld >= this.cols || td < 0 || td >= this.rows)
	  return;
  
  // get number of columns to copy
  nx = rs - ls + 1;
  if(this.cols - ld < nx) {
	  nx = this.cols - ld;
	  rs = ls + nx - 1;
  }
  
  // get number of rows to copy
  ny = bs - ts + 1;
  if(this.rows - td < ny) {
	  ny = this.rows - td;
	  bs = ts + ny - 1;
  }
  
  if (ld > ls) {
	  // copy from right to left in case overlapping source & destination
	  ls = ls + nx - 1;
	  ld = ld + nx - 1;
	  dx = -1;
  }
  if (td > ts) {
	  // copy from bottom to top in case overlapping source & destination
	  ts = ts + ny - 1;
	  td = td + ny - 1;
	  dy = -1;
  }
  
  //PJS: setup source & destination lines arrays
  if (ps < 0) {
	  ps = 0; 
  } else if (ps >= this.pages) {
	ps = this.pages - 1;
  }
  if (ps === this.curPage) {
	  srclines = this.lines;
	  srcbase = this.ybase;
  } else {
	  srclines = (this.screenPage[ps] && this.screenPage[ps].lines) || [];
	  srcbase = 0;
  }
  if (pd < 0) {
	  pd = 0; 
  } else if (pd >= this.pages) {
	pd = this.pages - 1;
  }
  if (pd === this.curPage) {
	  dstlines = this.lines;
	  dstbase = this.ybase;
  } else {
	  dstlines = (this.screenPage[pd] && this.screenPage[pd].lines) || [];
	  dstbase = 0;
  }
  
  y = ny;
  yd = td + dstbase;
  ys = ts + srcbase;
  while (y--) {
    srcline = srclines[ys] || this.blankLine();
	dstline = dstlines[yd] || this.blankLine();
	x = nx;
	xd = ld;
	xs = ls;
    while (x--) {
	  ch = srcline[xs] || [this.defAttr, ' '];
	  if (action === 3) {
		  srcline[xs] = dstline[xd] || [this.defAttr, ' ']; // swap
	  } else if (action === 2) {
		  srcline[xs] = [this.defAttr, ' '];
	  }
      dstline[xd] = ch;
	  xd += dx;
	  xs += dx;
    }
	ys += dy;
	yd += dy;
  }

  if (pd === this.curPage) {
    this.updateRange(td);
    this.updateRange(td + ((ny - 1) * dy));
  }
  if (ps === this.curPage && action !== 1) {
    this.updateRange(ts);
    this.updateRange(ts + ((ny - 1) * dy));
  }
  
};

// CSI Pt ; Pl ; Pb ; Pr ' w
//   Enable Filter Rectangle (DECEFR), VT420 and up.
//   Parameters are [top;left;bottom;right].
//   Defines the coordinates of a filter rectangle and activates
//   it.  Anytime the locator is detected outside of the filter
//   rectangle, an outside rectangle event is generated and the
//   rectangle is disabled.  Filter rectangles are always treated
//   as "one-shot" events.  Any parameters that are omitted default
//   to the current locator position.  If all parameters are omit-
//   ted, any locator motion will be reported.  DECELR always can-
//   cels any prevous rectangle definition.
Terminal.prototype.enableFilterRectangle = function(params) {
  ;
};

// CSI Ps x  Request Terminal Parameters (DECREQTPARM).
//   if Ps is a "0" (default) or "1", and xterm is emulating VT100,
//   the control sequence elicits a response of the same form whose
//   parameters describe the terminal:
//     Ps -> the given Ps incremented by 2.
//     Pn = 1  <- no parity.
//     Pn = 1  <- eight bits.
//     Pn = 1  <- 2  8  transmit 38.4k baud.
//     Pn = 1  <- 2  8  receive 38.4k baud.
//     Pn = 1  <- clock multiplier.
//     Pn = 0  <- STP flags.
Terminal.prototype.requestParameters = function(params) {
  ;
};

// CSI Ps x  Select Attribute Change Extent (DECSACE).
//     Ps = 0  -> from start to end position, wrapped.
//     Ps = 1  -> from start to end position, wrapped.
//     Ps = 2  -> rectangle (exact).
Terminal.prototype.selectChangeExtent = function(params) {
  ;
};

// CSI Pc; Pt; Pl; Pb; Pr$ x
//   Fill Rectangular Area (DECFRA), VT420 and up.
//     Pc is the character to use.
//     Pt; Pl; Pb; Pr denotes the rectangle.
// NOTE: xterm doesn't enable this code by default.
Terminal.prototype.fillRectangle = function(params) {
  // PJS: fixed bugs here!
  var ch = params[0] || 0
    , t = params[1] || 1
    , l = params[2] || 1
    , b = params[3] || this.rows
    , r = params[4] || this.cols;

  var line
    , i;
  if (ch < 32 || ch > 255 || (ch >= 128 && ch <= 159)) {
	  return; // ignore characters that are out of range
  }
  // ch = [this.line[i][0], String.fromCharCode(ch)];
  ch = [this.eraseAttr(), String.fromCharCode(ch)];
  for (; t < b + 1; t++) {
    line = this.lines[this.ybase + t - 1];
    for (i = l; i < r + 1; i++) {
      //line[i] = [line[i][0], ch];
	  line[i - 1] = ch;
    }
  }

  // this.maxRange();
  this.updateRange(params[1]);
  this.updateRange(params[3]);
};

// CSI Ps ; Pu ' z
//   Enable Locator Reporting (DECELR).
//   Valid values for the first parameter:
//     Ps = 0  -> Locator disabled (default).
//     Ps = 1  -> Locator enabled.
//     Ps = 2  -> Locator enabled for one report, then disabled.
//   The second parameter specifies the coordinate unit for locator
//   reports.
//   Valid values for the second parameter:
//     Pu = 0  <- or omitted -> default to character cells.
//     Pu = 1  <- device physical pixels.
//     Pu = 2  <- character cells.
Terminal.prototype.enableLocatorReporting = function(params) {
  var val = params[0] > 0;
  //this.mouseEvents = val;
  //this.decLocator = val;
};

// CSI Pt; Pl; Pb; Pr$ z
//   Erase Rectangular Area (DECERA), VT400 and up.
//     Pt; Pl; Pb; Pr denotes the rectangle.
// NOTE: xterm doesn't enable this code by default.
Terminal.prototype.eraseRectangle = function(params) {
  var t = params[0]
    , l = params[1]
    , b = params[2]
    , r = params[3];

  var line
    , i
    , ch;
	
  //TODO: coordinates are affected by origin mode (PJS)
  
  ch = [this.eraseAttr(), ' ']; // xterm?

  for (; t < b + 1; t++) {
    line = this.lines[this.ybase + t];
    for (i = l; i < r; i++) {
      line[i] = ch;
    }
  }

  // this.maxRange();
  this.updateRange(params[0]);
  this.updateRange(params[2]);
};

// CSI Pm ' {
//   Select Locator Events (DECSLE).
//   Valid values for the first (and any additional parameters)
//   are:
//     Ps = 0  -> only respond to explicit host requests (DECRQLP).
//                (This is default).  It also cancels any filter
//   rectangle.
//     Ps = 1  -> report button down transitions.
//     Ps = 2  -> do not report button down transitions.
//     Ps = 3  -> report button up transitions.
//     Ps = 4  -> do not report button up transitions.
Terminal.prototype.setLocatorEvents = function(params) {
  ;
};

// CSI Pt; Pl; Pb; Pr$ {
//   Selective Erase Rectangular Area (DECSERA), VT400 and up.
//     Pt; Pl; Pb; Pr denotes the rectangle.
Terminal.prototype.selectiveEraseRectangle = function(params) {
  ;
};

// CSI Ps ' |
//   Request Locator Position (DECRQLP).
//   Valid values for the parameter are:
//     Ps = 0 , 1 or omitted -> transmit a single DECLRP locator
//     report.

//   If Locator Reporting has been enabled by a DECELR, xterm will
//   respond with a DECLRP Locator Report.  This report is also
//   generated on button up and down events if they have been
//   enabled with a DECSLE, or when the locator is detected outside
//   of a filter rectangle, if filter rectangles have been enabled
//   with a DECEFR.

//     -> CSI Pe ; Pb ; Pr ; Pc ; Pp &  w

//   Parameters are [event;button;row;column;page].
//   Valid values for the event:
//     Pe = 0  -> locator unavailable - no other parameters sent.
//     Pe = 1  -> request - xterm received a DECRQLP.
//     Pe = 2  -> left button down.
//     Pe = 3  -> left button up.
//     Pe = 4  -> middle button down.
//     Pe = 5  -> middle button up.
//     Pe = 6  -> right button down.
//     Pe = 7  -> right button up.
//     Pe = 8  -> M4 button down.
//     Pe = 9  -> M4 button up.
//     Pe = 1 0  -> locator outside filter rectangle.
//   ``button'' parameter is a bitmask indicating which buttons are
//     pressed:
//     Pb = 0  <- no buttons down.
//     Pb & 1  <- right button down.
//     Pb & 2  <- middle button down.
//     Pb & 4  <- left button down.
//     Pb & 8  <- M4 button down.
//   ``row'' and ``column'' parameters are the coordinates of the
//     locator position in the xterm window, encoded as ASCII deci-
//     mal.
//   The ``page'' parameter is not used by xterm, and will be omit-
//   ted.
Terminal.prototype.requestLocatorPosition = function(params) {
  //TODO (PJS)
  ;
};

// PJS: added implementation for DECSCPP
Terminal.prototype.setColumnsPerPage = function(params) {
	var x = params[0] || 0,
		y, st, sb;
	if (x === 0 || x === 80) {
		x = this.normCols || 80;
		y = this.normRows || this.rows;
		this.scrMode = 0;
	} else if (x === 132) {
		x = this.extCols || 132;
		y = this.extRows || this.rows;
		this.scrMode = 1;
	} else {
		return;
	}
	if (this.cols !== x || this.rows !== y) {
	  st = this.scrollTop >= y ? y - 1 : this.scrollTop;
	  sb = this.scrollBottom >= y ? y - 1 : this.scrollBottom;
	  //TODO: save left/right margins if vertical scrolling region defined
      this.resize(x, y);
	  // do not erase screen
	  // restore scrolling region
      this.scrollTop = st;
	  this.scrollBottom = sb;        	
	}
};

// CSI P m ' }
// Insert P s Column(s) (default = 1) (DECIC), VT420 and up.
// NOTE: xterm doesn't enable this code by default.
Terminal.prototype.insertColumns = function() {
  var param = params[0]
    , l = this.ybase + this.rows
    , ch = [this.eraseAttr(), ' '] // xterm?
    , i;

    //TODO: DECIC is restricted to horizontal scrolling region (PJS)
  
	while (param--) {
    for (i = this.ybase; i < l; i++) {
      this.lines[i].splice(this.x + 1, 0, ch);
      this.lines[i].pop();
    }
  }

  this.maxRange();
};

// CSI P m ' ~
// Delete P s Column(s) (default = 1) (DECDC), VT420 and up
// NOTE: xterm doesn't enable this code by default.
Terminal.prototype.deleteColumns = function() {
  var param = params[0]
    , l = this.ybase + this.rows
    , ch = [this.eraseAttr(), ' '] // xterm?
    , i;

  while (param--) {
    for (i = this.ybase; i < l; i++) {
      this.lines[i].splice(this.x, 1);
      this.lines[i].push(ch);
    }
  }

  this.maxRange();
};

/**
 * Prefix/Select/Visual/Search Modes
 */

Terminal.prototype.enterPrefix = function() {
  this.prefixMode = true;
};

Terminal.prototype.leavePrefix = function() {
  this.prefixMode = false;
};

Terminal.prototype.enterSelect = function() {
  this._real = {
    x: this.x,
    y: this.y,
    ydisp: this.ydisp,
    ybase: this.ybase,
    cursorHidden: this.cursorHidden,
    lines: this.copyBuffer(this.lines),
    write: this.write
  };
  this.write = function() {};
  this.selectMode = true;
  this.visualMode = false;
  this.cursorHidden = false;
  this.refresh(this.y, this.y);
};

Terminal.prototype.leaveSelect = function() {
  this.x = this._real.x;
  this.y = this._real.y;
  this.ydisp = this._real.ydisp;
  this.ybase = this._real.ybase;
  this.cursorHidden = this._real.cursorHidden;
  this.lines = this._real.lines;
  this.write = this._real.write;
  delete this._real;
  this.selectMode = false;
  this.visualMode = false;
  this.refresh(0, this.rows - 1);
};

Terminal.prototype.enterVisual = function() {
  this._real.preVisual = this.copyBuffer(this.lines);
  this.selectText(this.x, this.x, this.ydisp + this.y, this.ydisp + this.y);
  this.visualMode = true;
};

Terminal.prototype.leaveVisual = function() {
  this.lines = this._real.preVisual;
  delete this._real.preVisual;
  delete this._selected;
  this.visualMode = false;
  this.refresh(0, this.rows - 1);
};

Terminal.prototype.enterSearch = function(down) {
  this.entry = '';
  this.searchMode = true;
  this.searchDown = down;
  this._real.preSearch = this.copyBuffer(this.lines);
  this._real.preSearchX = this.x;
  this._real.preSearchY = this.y;
  var bottom = this.ydisp + this.rows - 1;
  for (var i = 0; i < this.entryPrefix.length; i++) {
    //this.lines[bottom][i][0] = (this.defAttr & ~0x1ff) | 4;
    //this.lines[bottom][i][1] = this.entryPrefix[i];
    this.lines[bottom][i] = [
      (this.defAttr & ~0x1ff) | 4,
      this.entryPrefix[i]
    ];
  }
  this.y = this.rows - 1;
  this.x = this.entryPrefix.length;
  this.refresh(this.rows - 1, this.rows - 1);
};

Terminal.prototype.leaveSearch = function() {
  this.searchMode = false;

  if (this._real.preSearch) {
    this.lines = this._real.preSearch;
    this.x = this._real.preSearchX;
    this.y = this._real.preSearchY;
    delete this._real.preSearch;
    delete this._real.preSearchX;
    delete this._real.preSearchY;
  }

  this.refresh(this.rows - 1, this.rows - 1);
};

Terminal.prototype.copyBuffer = function(lines) {
  var lines = lines || this.lines
    , out = [];

  for (var y = 0; y < lines.length; y++) {
    out[y] = [];
    for (var x = 0; x < lines[y].length; x++) {
      out[y][x] = [lines[y][x][0], lines[y][x][1]];
    }
  }

  return out;
};

Terminal.prototype.getCopyTextarea = function(text) {
  var textarea = this._copyTextarea
    , document = this.document;

  if (!textarea) {
    textarea = document.createElement('textarea');
    textarea.style.position = 'absolute';
    textarea.style.left = '-32000px';
    textarea.style.top = '-32000px';
    textarea.style.width = '0px';
    textarea.style.height = '0px';
    textarea.style.opacity = '0';
    textarea.style.backgroundColor = 'transparent';
    textarea.style.borderStyle = 'none';
    textarea.style.outlineStyle = 'none';

    document.getElementsByTagName('body')[0].appendChild(textarea);

    this._copyTextarea = textarea;
  }

  return textarea;
};

// NOTE: Only works for primary selection on X11.
// Non-X11 users should use Ctrl-C instead.
Terminal.prototype.copyText = function(text) {
  var self = this
    , textarea = this.getCopyTextarea();

  this.emit('copy', text);

  textarea.focus();
  textarea.textContent = text;
  textarea.value = text;
  textarea.setSelectionRange(0, text.length);

  setTimeout(function() {
    self.element.focus();
    self.focus();
  }, 1);
};

Terminal.prototype.selectText = function(x1, x2, y1, y2) {
  var ox1
    , ox2
    , oy1
    , oy2
    , tmp
    , x
    , y
    , xl
    , attr;

  if (this._selected) {
    ox1 = this._selected.x1;
    ox2 = this._selected.x2;
    oy1 = this._selected.y1;
    oy2 = this._selected.y2;

    if (oy2 < oy1) {
      tmp = ox2;
      ox2 = ox1;
      ox1 = tmp;
      tmp = oy2;
      oy2 = oy1;
      oy1 = tmp;
    }

    if (ox2 < ox1 && oy1 === oy2) {
      tmp = ox2;
      ox2 = ox1;
      ox1 = tmp;
    }

    for (y = oy1; y <= oy2; y++) {
      x = 0;
      xl = this.cols - 1;
      if (y === oy1) {
        x = ox1;
      }
      if (y === oy2) {
        xl = ox2;
      }
      for (; x <= xl; x++) {
        if (this.lines[y][x].old != null) {
          //this.lines[y][x][0] = this.lines[y][x].old;
          //delete this.lines[y][x].old;
          attr = this.lines[y][x].old;
          delete this.lines[y][x].old;
          this.lines[y][x] = [attr, this.lines[y][x][1]];
        }
      }
    }

    y1 = this._selected.y1;
    x1 = this._selected.x1;
  }

  y1 = Math.max(y1, 0);
  y1 = Math.min(y1, this.ydisp + this.rows - 1);

  y2 = Math.max(y2, 0);
  y2 = Math.min(y2, this.ydisp + this.rows - 1);

  this._selected = { x1: x1, x2: x2, y1: y1, y2: y2 };

  if (y2 < y1) {
    tmp = x2;
    x2 = x1;
    x1 = tmp;
    tmp = y2;
    y2 = y1;
    y1 = tmp;
  }

  if (x2 < x1 && y1 === y2) {
    tmp = x2;
    x2 = x1;
    x1 = tmp;
  }

  for (y = y1; y <= y2; y++) {
    x = 0;
    xl = this.cols - 1;
    if (y === y1) {
      x = x1;
    }
    if (y === y2) {
      xl = x2;
    }
    for (; x <= xl; x++) {
      //this.lines[y][x].old = this.lines[y][x][0];
      //this.lines[y][x][0] &= ~0x1ff;
      //this.lines[y][x][0] |= (0x1ff << 9) | 4;
      attr = this.lines[y][x][0];
      this.lines[y][x] = [
        (attr & ~0x1ff) | ((0x1ff << 9) | 4),
        this.lines[y][x][1]
      ];
      this.lines[y][x].old = attr;
    }
  }

  y1 = y1 - this.ydisp;
  y2 = y2 - this.ydisp;

  y1 = Math.max(y1, 0);
  y1 = Math.min(y1, this.rows - 1);

  y2 = Math.max(y2, 0);
  y2 = Math.min(y2, this.rows - 1);

  //this.refresh(y1, y2);
  this.refresh(0, this.rows - 1);
};

Terminal.prototype.grabText = function(x1, x2, y1, y2) {
  var out = ''
    , buf = ''
    , ch
    , x
    , y
    , xl
    , tmp;

  if (y2 < y1) {
    tmp = x2;
    x2 = x1;
    x1 = tmp;
    tmp = y2;
    y2 = y1;
    y1 = tmp;
  }

  if (x2 < x1 && y1 === y2) {
    tmp = x2;
    x2 = x1;
    x1 = tmp;
  }

  for (y = y1; y <= y2; y++) {
    x = 0;
    xl = this.cols - 1;
    if (y === y1) {
      x = x1;
    }
    if (y === y2) {
      xl = x2;
    }
    for (; x <= xl; x++) {
      ch = this.lines[y][x][1];
      if (ch === ' ') {
        buf += ch;
        continue;
      }
      if (buf) {
        out += buf;
        buf = '';
      }
      out += ch;
      if (isWide(ch)) x++;
    }
    buf = '';
    out += '\n';
  }

  // If we're not at the end of the
  // line, don't add a newline.
  for (x = x2, y = y2; x < this.cols; x++) {
    if (this.lines[y][x][1] !== ' ') {
      out = out.slice(0, -1);
      break;
    }
  }

  return out;
};

//PJS start AccuTerm mod

//  New function to get rectangular block of text from screen using terminal
//  rows for y1,y2. This is different from grabText() which uses lines[] index
//  for y1,y2, and returns ragged (not rectangular) selection.
//
//  To retrieve text from history (scrollback), pass negative value
//  for y1,y2.

Terminal.prototype.getText = function(x1, x2, y1, y2) {
  var out = ''
    , buf = ''
    , ch
    , x
    , y
    , xl
    , tmp;

  if (y2 < y1) {
    tmp = x2;
    x2 = x1;
    x1 = tmp;
    tmp = y2;
    y2 = y1;
    y1 = tmp;
  }

  if (x2 < x1 && y1 === y2) {
    tmp = x2;
    x2 = x1;
    x1 = tmp;
  }

  y1 += this.ybase;
  y2 += this.ybase;
  
  for (y = y1; y <= y2; y++) {
    for (x = x1; x <= x2; x++) {
      ch = this.lines[y][x][1];
      if (ch === ' ') {
        buf += ch;
        continue;
      }
      if (buf) {
        out += buf;
        buf = '';
      }
      out += ch;
      if (isWide(ch)) x++;
    }
    buf = '';
    out += '\n';
  }

  return out;
};

//PJS start AccuTerm mod

Terminal.prototype.keyPrefix = function(ev, key) {
  if (key === 'k' || key === '&') {
    this.destroy();
  } else if (key === 'p' || key === ']') {
    this.emit('request paste');
  } else if (key === 'c') {
    this.emit('request create');
  } else if (key >= '0' && key <= '9') {
    key = +key - 1;
    if (!~key) key = 9;
    this.emit('request term', key);
  } else if (key === 'n') {
    this.emit('request term next');
  } else if (key === 'P') {
    this.emit('request term previous');
  } else if (key === ':') {
    this.emit('request command mode');
  } else if (key === '[') {
    this.enterSelect();
  }
};

Terminal.prototype.keySelect = function(ev, key) {
  this.showCursor();

  if (this.searchMode || key === 'n' || key === 'N') {
    return this.keySearch(ev, key);
  }

  if (key === '\x04') { // ctrl-d
    var y = this.ydisp + this.y;
    if (this.ydisp === this.ybase) {
      // Mimic vim behavior
      this.y = Math.min(this.y + (this.rows - 1) / 2 | 0, this.rows - 1);
      this.refresh(0, this.rows - 1);
    } else {
      this.scrollDisp((this.rows - 1) / 2 | 0);
    }
    if (this.visualMode) {
      this.selectText(this.x, this.x, y, this.ydisp + this.y);
    }
    return;
  }

  if (key === '\x15') { // ctrl-u
    var y = this.ydisp + this.y;
    if (this.ydisp === 0) {
      // Mimic vim behavior
      this.y = Math.max(this.y - (this.rows - 1) / 2 | 0, 0);
      this.refresh(0, this.rows - 1);
    } else {
      this.scrollDisp(-(this.rows - 1) / 2 | 0);
    }
    if (this.visualMode) {
      this.selectText(this.x, this.x, y, this.ydisp + this.y);
    }
    return;
  }

  if (key === '\x06') { // ctrl-f
    var y = this.ydisp + this.y;
    this.scrollDisp(this.rows - 1);
    if (this.visualMode) {
      this.selectText(this.x, this.x, y, this.ydisp + this.y);
    }
    return;
  }

  if (key === '\x02') { // ctrl-b
    var y = this.ydisp + this.y;
    this.scrollDisp(-(this.rows - 1));
    if (this.visualMode) {
      this.selectText(this.x, this.x, y, this.ydisp + this.y);
    }
    return;
  }

  if (key === 'k' || key === '\x1b[A') {
    var y = this.ydisp + this.y;
    this.y--;
    if (this.y < 0) {
      this.y = 0;
      this.scrollDisp(-1);
    }
    if (this.visualMode) {
      this.selectText(this.x, this.x, y, this.ydisp + this.y);
    } else {
      this.refresh(this.y, this.y + 1);
    }
    return;
  }

  if (key === 'j' || key === '\x1b[B') {
    var y = this.ydisp + this.y;
    this.y++;
    if (this.y >= this.rows) {
      this.y = this.rows - 1;
      this.scrollDisp(1);
    }
    if (this.visualMode) {
      this.selectText(this.x, this.x, y, this.ydisp + this.y);
    } else {
      this.refresh(this.y - 1, this.y);
    }
    return;
  }

  if (key === 'h' || key === '\x1b[D') {
    var x = this.x;
    this.x--;
    if (this.x < 0) {
      this.x = 0;
    }
    if (this.visualMode) {
      this.selectText(x, this.x, this.ydisp + this.y, this.ydisp + this.y);
    } else {
      this.refresh(this.y, this.y);
    }
    return;
  }

  if (key === 'l' || key === '\x1b[C') {
    var x = this.x;
    this.x++;
    if (this.x >= this.cols) {
      this.x = this.cols - 1;
    }
    if (this.visualMode) {
      this.selectText(x, this.x, this.ydisp + this.y, this.ydisp + this.y);
    } else {
      this.refresh(this.y, this.y);
    }
    return;
  }

  if (key === 'v' || key === ' ') {
    if (!this.visualMode) {
      this.enterVisual();
    } else {
      this.leaveVisual();
    }
    return;
  }

  if (key === 'y') {
    if (this.visualMode) {
      var text = this.grabText(
        this._selected.x1, this._selected.x2,
        this._selected.y1, this._selected.y2);
      this.copyText(text);
      this.leaveVisual();
      // this.leaveSelect();
    }
    return;
  }

  if (key === 'q' || key === '\x1b') {
    if (this.visualMode) {
      this.leaveVisual();
    } else {
      this.leaveSelect();
    }
    return;
  }

  if (key === 'w' || key === 'W') {
    var ox = this.x;
    var oy = this.y;
    var oyd = this.ydisp;

    var x = this.x;
    var y = this.y;
    var yb = this.ydisp;
    var saw_space = false;

    for (;;) {
      var line = this.lines[yb + y];
      while (x < this.cols) {
        if (line[x][1] <= ' ') {
          saw_space = true;
        } else if (saw_space) {
          break;
        }
        x++;
      }
      if (x >= this.cols) x = this.cols - 1;
      if (x === this.cols - 1 && line[x][1] <= ' ') {
        x = 0;
        if (++y >= this.rows) {
          y--;
          if (++yb > this.ybase) {
            yb = this.ybase;
            x = this.x;
            break;
          }
        }
        continue;
      }
      break;
    }

    this.x = x, this.y = y;
    this.scrollDisp(-this.ydisp + yb);

    if (this.visualMode) {
      this.selectText(ox, this.x, oy + oyd, this.ydisp + this.y);
    }
    return;
  }

  if (key === 'b' || key === 'B') {
    var ox = this.x;
    var oy = this.y;
    var oyd = this.ydisp;

    var x = this.x;
    var y = this.y;
    var yb = this.ydisp;

    for (;;) {
      var line = this.lines[yb + y];
      var saw_space = x > 0 && line[x][1] > ' ' && line[x - 1][1] > ' ';
      while (x >= 0) {
        if (line[x][1] <= ' ') {
          if (saw_space && (x + 1 < this.cols && line[x + 1][1] > ' ')) {
            x++;
            break;
          } else {
            saw_space = true;
          }
        }
        x--;
      }
      if (x < 0) x = 0;
      if (x === 0 && (line[x][1] <= ' ' || !saw_space)) {
        x = this.cols - 1;
        if (--y < 0) {
          y++;
          if (--yb < 0) {
            yb++;
            x = 0;
            break;
          }
        }
        continue;
      }
      break;
    }

    this.x = x, this.y = y;
    this.scrollDisp(-this.ydisp + yb);

    if (this.visualMode) {
      this.selectText(ox, this.x, oy + oyd, this.ydisp + this.y);
    }
    return;
  }

  if (key === 'e' || key === 'E') {
    var x = this.x + 1;
    var y = this.y;
    var yb = this.ydisp;
    if (x >= this.cols) x--;

    for (;;) {
      var line = this.lines[yb + y];
      while (x < this.cols) {
        if (line[x][1] <= ' ') {
          x++;
        } else {
          break;
        }
      }
      while (x < this.cols) {
        if (line[x][1] <= ' ') {
          if (x - 1 >= 0 && line[x - 1][1] > ' ') {
            x--;
            break;
          }
        }
        x++;
      }
      if (x >= this.cols) x = this.cols - 1;
      if (x === this.cols - 1 && line[x][1] <= ' ') {
        x = 0;
        if (++y >= this.rows) {
          y--;
          if (++yb > this.ybase) {
            yb = this.ybase;
            break;
          }
        }
        continue;
      }
      break;
    }

    this.x = x, this.y = y;
    this.scrollDisp(-this.ydisp + yb);

    if (this.visualMode) {
      this.selectText(ox, this.x, oy + oyd, this.ydisp + this.y);
    }
    return;
  }

  if (key === '^' || key === '0') {
    var ox = this.x;

    if (key === '0') {
      this.x = 0;
    } else if (key === '^') {
      var line = this.lines[this.ydisp + this.y];
      var x = 0;
      while (x < this.cols) {
        if (line[x][1] > ' ') {
          break;
        }
        x++;
      }
      if (x >= this.cols) x = this.cols - 1;
      this.x = x;
    }

    if (this.visualMode) {
      this.selectText(ox, this.x, this.ydisp + this.y, this.ydisp + this.y);
    } else {
      this.refresh(this.y, this.y);
    }
    return;
  }

  if (key === '$') {
    var ox = this.x;
    var line = this.lines[this.ydisp + this.y];
    var x = this.cols - 1;
    while (x >= 0) {
      if (line[x][1] > ' ') {
        if (this.visualMode && x < this.cols - 1) x++;
        break;
      }
      x--;
    }
    if (x < 0) x = 0;
    this.x = x;
    if (this.visualMode) {
      this.selectText(ox, this.x, this.ydisp + this.y, this.ydisp + this.y);
    } else {
      this.refresh(this.y, this.y);
    }
    return;
  }

  if (key === 'g' || key === 'G') {
    var ox = this.x;
    var oy = this.y;
    var oyd = this.ydisp;
    if (key === 'g') {
      this.x = 0, this.y = 0;
      this.scrollDisp(-this.ydisp);
    } else if (key === 'G') {
      this.x = 0, this.y = this.rows - 1;
      this.scrollDisp(this.ybase);
    }
    if (this.visualMode) {
      this.selectText(ox, this.x, oy + oyd, this.ydisp + this.y);
    }
    return;
  }

  if (key === 'H' || key === 'M' || key === 'L') {
    var ox = this.x;
    var oy = this.y;
    if (key === 'H') {
      this.x = 0, this.y = 0;
    } else if (key === 'M') {
      this.x = 0, this.y = this.rows / 2 | 0;
    } else if (key === 'L') {
      this.x = 0, this.y = this.rows - 1;
    }
    if (this.visualMode) {
      this.selectText(ox, this.x, this.ydisp + oy, this.ydisp + this.y);
    } else {
      this.refresh(oy, oy);
      this.refresh(this.y, this.y);
    }
    return;
  }

  if (key === '{' || key === '}') {
    var ox = this.x;
    var oy = this.y;
    var oyd = this.ydisp;

    var line;
    var saw_full = false;
    var found = false;
    var first_is_space = -1;
    var y = this.y + (key === '{' ? -1 : 1);
    var yb = this.ydisp;
    var i;

    if (key === '{') {
      if (y < 0) {
        y++;
        if (yb > 0) yb--;
      }
    } else if (key === '}') {
      if (y >= this.rows) {
        y--;
        if (yb < this.ybase) yb++;
      }
    }

    for (;;) {
      line = this.lines[yb + y];

      for (i = 0; i < this.cols; i++) {
        if (line[i][1] > ' ') {
          if (first_is_space === -1) {
            first_is_space = 0;
          }
          saw_full = true;
          break;
        } else if (i === this.cols - 1) {
          if (first_is_space === -1) {
            first_is_space = 1;
          } else if (first_is_space === 0) {
            found = true;
          } else if (first_is_space === 1) {
            if (saw_full) found = true;
          }
          break;
        }
      }

      if (found) break;

      if (key === '{') {
        y--;
        if (y < 0) {
          y++;
          if (yb > 0) yb--;
          else break;
        }
      } else if (key === '}') {
        y++;
        if (y >= this.rows) {
          y--;
          if (yb < this.ybase) yb++;
          else break;
        }
      }
    }

    if (!found) {
      if (key === '{') {
        y = 0;
        yb = 0;
      } else if (key === '}') {
        y = this.rows - 1;
        yb = this.ybase;
      }
    }

    this.x = 0, this.y = y;
    this.scrollDisp(-this.ydisp + yb);

    if (this.visualMode) {
      this.selectText(ox, this.x, oy + oyd, this.ydisp + this.y);
    }
    return;
  }

  if (key === '/' || key === '?') {
    if (!this.visualMode) {
      this.enterSearch(key === '/');
    }
    return;
  }

  return false;
};

Terminal.prototype.keySearch = function(ev, key) {
  if (key === '\x1b') {
    this.leaveSearch();
    return;
  }

  if (key === '\r' || (!this.searchMode && (key === 'n' || key === 'N'))) {
    this.leaveSearch();

    var entry = this.entry;

    if (!entry) {
      this.refresh(0, this.rows - 1);
      return;
    }

    var ox = this.x;
    var oy = this.y;
    var oyd = this.ydisp;

    var line;
    var found = false;
    var wrapped = false;
    var x = this.x + 1;
    var y = this.ydisp + this.y;
    var yb, i;
    var up = key === 'N'
      ? this.searchDown
      : !this.searchDown;

    for (;;) {
      line = this.lines[y];

      while (x < this.cols) {
        for (i = 0; i < entry.length; i++) {
          if (x + i >= this.cols) break;
          if (line[x + i][1] !== entry[i]) {
            break;
          } else if (line[x + i][1] === entry[i] && i === entry.length - 1) {
            found = true;
            break;
          }
        }
        if (found) break;
        x += i + 1;
      }
      if (found) break;

      x = 0;

      if (!up) {
        y++;
        if (y > this.ybase + this.rows - 1) {
          if (wrapped) break;
          // this.setMessage('Search wrapped. Continuing at TOP.');
          wrapped = true;
          y = 0;
        }
      } else {
        y--;
        if (y < 0) {
          if (wrapped) break;
          // this.setMessage('Search wrapped. Continuing at BOTTOM.');
          wrapped = true;
          y = this.ybase + this.rows - 1;
        }
      }
    }

    if (found) {
      if (y - this.ybase < 0) {
        yb = y;
        y = 0;
        if (yb > this.ybase) {
          y = yb - this.ybase;
          yb = this.ybase;
        }
      } else {
        yb = this.ybase;
        y -= this.ybase;
      }

      this.x = x, this.y = y;
      this.scrollDisp(-this.ydisp + yb);

      if (this.visualMode) {
        this.selectText(ox, this.x, oy + oyd, this.ydisp + this.y);
      }
      return;
    }

    // this.setMessage("No matches found.");
    this.refresh(0, this.rows - 1);

    return;
  }

  if (key === '\b' || key === '\x7f') {
    if (this.entry.length === 0) return;
    var bottom = this.ydisp + this.rows - 1;
    this.entry = this.entry.slice(0, -1);
    var i = this.entryPrefix.length + this.entry.length;
    //this.lines[bottom][i][1] = ' ';
    this.lines[bottom][i] = [
      this.lines[bottom][i][0],
      ' '
    ];
    this.x--;
    this.refresh(this.rows - 1, this.rows - 1);
    this.refresh(this.y, this.y);
    return;
  }

  if (key.length === 1 && key >= ' ' && key <= '~') {
    var bottom = this.ydisp + this.rows - 1;
    this.entry += key;
    var i = this.entryPrefix.length + this.entry.length - 1;
    //this.lines[bottom][i][0] = (this.defAttr & ~0x1ff) | 4;
    //this.lines[bottom][i][1] = key;
    this.lines[bottom][i] = [
      (this.defAttr & ~0x1ff) | 4,
      key
    ];
    this.x++;
    this.refresh(this.rows - 1, this.rows - 1);
    this.refresh(this.y, this.y);
    return;
  }

  return false;
};

//PJS - new function to calculate last column taking into account double-wide line attribute
Terminal.prototype.lastCol = function(y) {
	var lastCol = this.cols - 1;	
	if (this.lines[this.ybase + y].lineAttr & 1) {
		// double-wide line attribute
		lastCol = Math.floor(lastCol / 2);
	}
	return lastCol;
};

//PJS - new function to advance cursor which properly handles scrolling region, auto wrap, delay wrap
Terminal.prototype.advanceCursor = function() {
	var y = this.y
	  , lastCol = this.lastCol(y);
	if (this.delayWrap) {
		this.delayWrap = false;
		if (this.x >= lastCol) {
			if (this.wraparoundMode) {
				// handle delayed wrap
				this.x = 0;
				if (y < this.scrollBottom) {
					this.y++; // cursor above bottom of scrolling region
				} else if (y === this.scrollBottom) {
					this.scroll(); // cursor on last row of scrolling region (or last row of screen)
				} else if (this.scrollBottom < this.rows - 1) {
					this.y++; // cursor below bottom of scrolling region and above last row of screen
				} else {
					; // cursor on last row of screen
				}
			}
			return;
		}
	}
	
	this.x++;
	if (this.x > lastCol) {
		this.x = lastCol;
		this.delayWrap = this.wraparoundMode; // delay the line wrap until next character
	}
};

// PJS: new function to switch screen page
//  If direction < 0, then move back from current page.
//  If direction > 0 then move forward from current page.
//  Else move to specified page. Cursor position and scrolling
//  region are saved with the page. If restore_cursor = 1, the
//  cursor is restored to the position saved with the switched-to
//  page. If restore_cursor = 2, the cursor position is set to
//  home (0,0).
Terminal.prototype.switchPage = function(page, direction, restore_cursor) {
	
	// sort out the target page
	if (this.screenPages < 2) {
		return;
	}
	if (direction < 0) {
		page = this.curPage - page;
	} else if (direction > 0) {
		page = this.curPage + page;
	}
	if (page >= this.screenPages) {
		page = this.screenPages - 1;
	} else if (page < 0) {
		page = 0;
	}
	if (page === this.curPage) {
		return;
	}

	var i = 0
	  , x = this.x
	  , y = this.y
	  , cols = this.cols
	  , rows = this.rows
	  , line
	  , newPage
	  , savePage;
		  
	// save the current page
	savePage = {
		cols: this.cols,
		rows: this.rows,
		x: this.x,
		y: this.y,
		scrollTop: this.scrollTop,
		scrollBottom: this.scrollBottom,
		lines: this.lines.slice(this.ybase)		
	};
	
	// get the target page
	newPage = this.screenPage[page] || {scrollTop: 0, scrollBottom: this.rows - 1, lines: []};
	this.cols = newPage.cols || cols;
	this.rows = newPage.rows || rows;
	this.x = restore_cursor ? newPage.x || 0 : x;
	this.y = restore_cursor ? newPage.y || 0 : y;
	this.scrollTop = newPage.scrollTop;
	this.scrollBottom = newPage.scrollBottom;
	for (i = 0; i < this.rows; i++) {
		line = newPage.lines[i] || this.blankLine();
		this.lines[this.ybase + i] = line;
	}	
	this.screenPage[this.curPage] = savePage;	
	this.curPage = page;
	
	// resize the screen in case switched-to page geometry is different from current geometry
	this.resize(cols, rows); // ensure switched-to page is correct geometry

	// home the cursor?
	if (restore_cursor === 2) {
		this.cursorPos([1,1]);
	}
	
	// fixup scrolling region (resize has reset it)
	if (newPage.scrollTop > 0 && newPage.scrollTop < this.rows) {
		this.scrollTop = newPage.scrollTop;		
	}
	if (newPage.scrollBottom >= this.scrollTop && newPage.scrollBottom < this.rows) {
		this.scrollBottom = newPage.scrollBottom;
	}
	
	this.maxRange(); // refresh the new page
	
    this.handleCursor(this.x, this.y, this.curPage); // fire cursor event
};

//PJS: new function - refresh screen after colors updated
Terminal.prototype.updateColors = function() {
  if (this.useStyle && this.document) {
    Terminal.insertStyle(this.document, this.colors[256], this.colors[257], this.fontName, this.fontProps.fontSize, this.fontProps.scaleX, this.fontProps.scaleY, this.fontProps.fontHeight, this.bkgndPict, this.bkgndAlpha, this.bkgndMode);
  }	
  this.refresh(0, this.rows - 1);
  this.resetRange(); //PJS: reset refresh range after refresh
};

//PJS: new function - update font when font size or screen size changes
Terminal.prototype.updateFont = function() {
	if (this.useStyle && this.document) {
	  var oldFontProps = this.fontProps;
	  var newFontProps = this.getFontProps(this.fontName, this.fontPoints, this.cols, this.rows);
	  if (newFontProps) {
	    if (!oldFontProps ||
			newFontProps.fontSize !== oldFontProps.fontSize ||
			newFontProps.fontWidth !== oldFontProps.fontWidth ||
			newFontProps.fontHeight !== oldFontProps.fontHeight ||
			newFontProps.scaleX !== oldFontProps.scaleX ||
			newFontProps.scaleY !== oldFontProps.scaleY) {
		  this.fontProps = newFontProps;
          Terminal.insertStyle(this.document, this.colors[256], this.colors[257], this.fontName, this.fontProps.fontSize, this.fontProps.scaleX, this.fontProps.scaleY, this.fontProps.fontHeight, this.bkgndPict, this.bkgndAlpha, this.bkgndMode);
          this.refresh(0, this.rows - 1);  
          this.resetRange(); //PJS: reset refresh range after refresh
	    }
	  } else {
	    // likely failed because container is currently hidden - watch for
	  }
	}
 };
  
//PJS: new function - calculate font size for scalable font
Terminal.prototype.getFontProps = function(fontName, points, cols, rows) {

  var fontSize
  , fontWidth
  , fontHeight
  , minFontSize = _pointsToPixels(this.document, 4.5) // 6px
  , maxFontSize = _pointsToPixels(this.document, 20); // 27px

  try {
    if (points === 'auto') {
      var sbwd = this.viewport.offsetWidth - this.viewport.clientWidth;
      //var width = _getContainerWidth(this.parent) - 10; // subtract the padding included in the .terminal class
      //var width = this.parent.offsetWidth; // get the container width
      var width = this.viewport.clientWidth; // get the viewport width
      if (!width) {
        return null; // if offsetWidth is zero, it probably means that our container is hidden!		  
      } else {
        width -= 10; // subtract padding defined in the .terminal class
      }
      width -= sbwd; // subtract scrollbar width if there is a scrollbar (history)
      var height = this.viewport.clientHeight;
      var aspect = _fontAspect(this.document, fontName, '12pt', false);
      var aspect_with_leading = _fontAspect(this.document, fontName, '12pt', true);
      var aspect_min = (cols === 132 ? aspect * 0.6 : aspect * 0.8); // 0.6 = 80/132 (approx), so toggling screen between normal & extended should keep same line height
      var aspect_max = aspect * 1.2; // allow slightly obeese characters
      var charWd = width / cols; // calculate the font width (pixels) that fills entire terminal width using specifed number of columns	 
      var lineHt = height / rows; // calculate the font height (pixels) that fills the entire terminal height using specified number of rows
      var aspect_test = charWd / lineHt;
      if (aspect_test < aspect_min) {
        lineHt = charWd / aspect_min; // shorten line height to conform to minimum aspect ratio
        height = lineHt * rows;
      } else if (aspect_test > aspect_max) {
        charWd = lineHt * aspect_max; // narrow char width to conform to maximum aspect ratio
        width = charWd * cols;
      }
      fontSize = Math.round(lineHt * aspect_with_leading / aspect); // font size without leading
      // auto-size font if size is in acceptable range
      if (fontSize < minFontSize) {
        fontSize = minFontSize;
      } else if (fontSize > maxFontSize) {
        fontSize = maxFontSize;
      } else {
        // font size is in acceptable range - compute any extra scaling
        var sx = 1;
        var sy = 1;
        var str = Array(cols).join('X');
        var swh = _stringSize(this.document, fontName, fontSize + 'px', str);
        sx = Math.round((width / swh[0]) * 1000) / 1000; // X scale, rounded to 3 decimal places
        fontWidth = Math.round((swh[0] / cols) * sx * 1000) / 1000; // round to 3 decimal places
        fontHeight = Math.round(swh[1]); // integral value for height works better
        DEBUG&&console.log('getFontProps: auto font size=' + fontSize + '; cols=' + cols + '; width=' + width + '; scaleX=' + sx + '; scaleY=' + sy + '; charWidth=' + fontWidth + '; charHeight=' + fontHeight);
        return {fontSize: fontSize, scaleX: sx, scaleY: sy, fontWidth: fontWidth, fontHeight: fontHeight};
      }
    } else {
      fontSize = Math.min(Math.max(minFontSize, _pointsToPixels(this.document, points)), maxFontSize);
    }
  } catch(e) {
    DEBUG&&console.log('getFontProps: exception(1)=' + e);
  }
  try {
    str = Array(cols).join('X');
    swh = _stringSize(this.document, fontName, fontSize + 'px', str); // calculate using full line, in case of rounding errors
    fontHeight = Math.round(swh[1]); // round to nearest pixel
    fontWidth = Math.round((swh[0] / cols) * 1000) / 1000; // round to 1/1000 pixel
    DEBUG&&console.log('getFontProps: fixed font size=' + fontSize + '; charWidth=' + fontWidth + '; charHeight=' + fontHeight);
  } catch(e) {
    DEBUG&&console.log('getFontProps: exception(2)=' + e);
  }
  return {fontSize: fontSize, fontWidth: fontWidth, fontHeight: fontHeight};
};

//PJS: helper functions for font scaling

/*
// Return the width of a containing element in pixels
function _getContainerWidth(element) {
  var width = 0;
  if (element) {
    width = element.offsetWidth;
    if (!width) return _getContainerWidth(element.parentNode);
	DEBUG&&console.log('getContainerWidth: nodeName=' + element.nodeName + ' id=' + element.id + ' width=' + width);
  }
  return width;
}
*/

// Calculate font aspect ratio. Typically this is around 0.625. This does not include leading.
function _fontAspect(document, fontName, fontSize, include_leading) {
  var body = document.getElementsByTagName('body')[0];
  var div = document.createElement('div');
  var span = document.createElement('span');
  div.appendChild(span);
  div.style.visibility = 'hidden';
  div.style.whiteSpace = 'pre';
  div.style.fontFamily = fontName;
  div.style.fontSize = fontSize;
  div.style.lineHeight = fontSize;
  span.innerHTML = 'X';
  body.appendChild(div);
  var w = span.offsetWidth;
  if (include_leading)
    var h = span.offsetHeight; // this includes leading
  else
    var h = div.offsetHeight; // this does not include leading
  body.removeChild(div); 
  //DEBUG&&console.log('Font aspect for ' + fontSize + ' ' + fontName + ' = ' + (w / h) + '; PPI =  ' + _PPI(document));  
  return w / h;
}

// Measure rendered size of string in given font & size. The dimensions are
// returned as an array [width, height], and the height includes "leading".
function _stringSize(document, fontName, fontSize, str) {
  var body = document.getElementsByTagName('body')[0];
  var el = document.createElement('span');
  el.style.visibility = 'hidden';
  el.style.whiteSpace = 'pre';
  el.style.fontFamily = fontName;
  el.style.fontSize = fontSize;
  el.innerHTML = str;
  body.appendChild(el);
  var w = el.offsetWidth;
  var h = el.offsetHeight; // this value includes leading
  body.removeChild(el);
  return [w, h];
}

// Convert points to pixels
function _pointsToPixels(document, pts) {
	// bias the pixels up a bit so we get 14px for 10pt @ 96px/in
	return Math.round((pts * _PPI(document) / 72) + 0.49);
}

// Return pixels/inch calculated using stringSize, specifying font size in points & pixels
//   note: on Android & iOS devices this allways seems to be 96!
function _PPI(document) {
  if (!_PPI.val) {
    var str = Array(81).join('X');
    var n = _stringSize(document, 'monospace', '12pt', str)[0]; // pixel width of 12pt string
    var m = _stringSize(document, 'monospace', '12px', str)[0]; // pixel width of 12px string
    _PPI.val = Math.round(72 * n / m); // cache the size
  }
  return _PPI.val; // return number of css pixels per inch
}

// PJS: utilitiy functions to return line height / char width from terminal font properties
Terminal.prototype.getLineHeight = function() {
    var height = (this.fontProps && this.fontProps.fontHeight) || 0;
    if (!height) {
        try {
            height = Math.round(this.viewport.parentNode.style.lineHeight).replace(/[^-\d\.]/g, '');
        } catch(e) {}
    }
    return height;
};
 
Terminal.prototype.getCharWidth = function() {
    var width = (this.fontProps && this.fontProps.fontWidth) || 0;
    if (!width) {
        try {
            var fontSize = Math.round(this.viewport.parentNode.style.fontSize).replace(/[^-\d\.]/g, '');
            width = Math.round(fontSize * 0.55);
        } catch(e) {}
     }		
     return width;
};

// PJS: new function - ensure the character cell containing the cursor is scrolled into view
Terminal.prototype.scrollViewToCursorPos = function() {
	if (!this.viewport) return;
    var lineHeight = this.getLineHeight();
    var mainHeight = this.rows * lineHeight;
    var historyRows = this.historyRows;
    var historyBot = historyRows * lineHeight;
    if (historyBot > 0) 
        historyBot += 5; // add 5px for padding
    var cursorRow = historyRows + this.y;
    var cursorRowTop = (cursorRow * lineHeight) + 5; // add 5px for padding
    var cursorRowBot = cursorRowTop + lineHeight;
    var curScrollTop = this.viewport.scrollTop;
    var newScrollTop = historyBot + this.curScrollPos; // maintain same scroll position (scroll position zero shows terminal row zero at top of viewport)
    if (newScrollTop < 0) 
        newScrollTop = 0; // in case terminal was reset, clearing history
    if (newScrollTop > historyBot + mainHeight - lineHeight) 
        newScrollTop = historyBot + mainHeight - lineHeight;
    var viewportHeight = this.viewport.clientHeight;

    // make sure we can see the cursor line
    if (cursorRowBot > newScrollTop + viewportHeight) {
        newScrollTop = cursorRowBot - viewportHeight;
        if (newScrollTop - curScrollTop > lineHeight) {
            // scrolling more than one line?			
            if (newScrollTop < historyBot) {
                // if any history is visible, shift scroll position to hide the history				
                newScrollTop = historyBot;
            }
        }
    } else if (cursorRowTop < newScrollTop) {
        newScrollTop = cursorRowTop;
    }

    // save new scroll position
    this.curScrollPos = newScrollTop - historyBot;	

    // scroll the viewport if needed
    if (newScrollTop !== curScrollTop) {
        this.viewport.scrollTop = newScrollTop;
        //DEBUG&&console.log('scrollViewToCursorPos: cur scroll top=' + curScrollTop + ' new scroll top=' + newScrollTop + ' y=' + this.y + ' cursor row=' + cursorRow + ' hist=' + historyRows + ' line height=' + lineHeight + ' viewport height=' + viewportHeight + ' pos=' + this.curScrollPos);
    } else {
        //DEBUG&&console.log('scrollViewToCursorPos: cur scroll top=' + curScrollTop + ' y=' + this.y + ' cursor row=' + cursorRow + ' hist=' + historyRows + ' line height=' + lineHeight + ' viewport height=' + viewportHeight + ' pos=' + this.curScrollPos);
    }		
	       
};

// PJS: new function - resize font & adjust padding when viewport is resized
Terminal.prototype.resizeTerminalViewport = function() {
	if (!this.viewport) return;
	this.updateFont(); // resize auto-sized font to fit new viewport size
  this.updateImages(); // make sure images are scaled & positioned
  var lineHeight = this.getLineHeight();
  var mainHeight = this.rows * lineHeight;
  var viewportHeight = this.viewport.parentNode.clientHeight; // note: we need the container height, not the viewport height!
	var containerPadding = (viewportHeight > mainHeight) ?
    // add padding so that we can fill display with live screen without showing any history
    (viewportHeight - mainHeight) :		
    // remove padding because we can fill entire screen without history
	  0;
  this.mainterminal.style.paddingBottom = containerPadding + 'px';
  DEBUG&&console.log('resizeTerminalViewport: viewport height=' + viewportHeight + ' padding=' + containerPadding);
};

// PJS: display image above terminal screen
Terminal.prototype.addImage = function(filename, col, row, wd, ht, aspect, border) {	
	try {
		
		var imgdiv = this.document.createElement('div');
		imgdiv.className = 'terminal-image';
		
		var style = 'position:absolute;left:' + (col * this.getCharWidth()) + 'px;top:' + (row * this.getLineHeight()) + 'px;';
		if(wd && ht) 
		  style += 'width:' + (wd * this.getCharWidth()) + 'px;height:' + (ht * this.getLineHeight()) + 'px;';
	    else
		  style += 'width:50px;height:50px;';
		style += 'z-index:16;';
		style += 'background-color:gray;';
		imgdiv.setAttribute('style', style);
		
		var img = this.document.createElement('img');
		img.setAttribute('src', filename);
		switch(border) {
		  case 'B': img.setAttribute('class', 'simple'); break;
		  case 'R': img.setAttribute('class', 'raised'); break;
		  case 'I': img.setAttribute('class', 'inset'); break;
        }
		      
        style = 'position:absolute;left:0px;top:0px;';
        if(wd && ht) {
          style += 'width:' + (wd * this.getCharWidth()) + 'px;height:' + (ht * this.getLineHeight()) + 'px;';
        } else {
          try {
            var self = this;
            img.addEventListener('load', function() {
				try {
					if (img.naturalWidth && img.naturalHeight) {
						imgdiv.style.width = img.naturalWidth + 'px';				
						imgdiv.style.height = img.naturalHeight + 'px';
						img.style.width = img.naturalWidth + 'px';				
						img.style.height = img.naturalHeight + 'px';
					}
				} catch(e) {}
            });    	  
		  } catch(e) {}
	    }
		img.setAttribute('style', style);

		// stash the original terminal coordinates & size to the imgdiv element for resizing the image when the viewport changes
		var props = '{"col":'+col+',"row":'+row+',"wd":'+wd+',"ht":'+ht+'}';
		imgdiv.setAttribute('atprops', props);
		
		// Append img to imgdiv, then imgdiv to image container div
		imgdiv.appendChild(img);
		this.terminalImageContainer.appendChild(imgdiv);
	} catch(e) {
		DEBUG&&console.log('addImage exception: ' + e);
	}
};

Terminal.prototype.deleteImage = function(filename) {
	if (!filename) {
		// delete all images
		this.terminalImageContainer.innerHTML = '';
	} else {
		var imgs = this.terminalImageContainer.children;
		var i;
		for(i = 0; i < imgs.length; i++) {
			try {
				if (imgs[i].firstElementChild && imgs[i].firstElementChild.getAttribute('src') === filename) {
					imgs[i].parentNode.removeChild(imgs[i]);
				}
			} catch(e) {}
		}
		
	}
};

Terminal.prototype.updateImages = function() {
	var imgs = this.terminalImageContainer.children;
	var img;
	var i;
	for(i = 0; i < imgs.length; i++) {
		try {
			var props = imgs[i].getAttribute('atprops');
			if (props) {
				var obj = JSON.parse(props);
				if (obj && 'col' in obj && 'row' in obj && 'wd' in obj && 'ht' in obj) {
					imgs[i].left = (obj.col * this.getCharWidth()) + 'px';
					imgs[i].top = (obj.row * this.getLineHeight()) + 'px';
					if(obj.wd && obj.ht) {
						imgs[i].width = (obj.wd * this.getCharWidth()) + 'px';
					    imgs[i].height = (obj.ht * this.getLineHeight()) + 'px';										
						img = imgs[i].firstElementChild;
						if (img) {
							img.width = (obj.wd * this.getCharWidth()) + 'px';
							img.height = (obj.ht * this.getLineHeight()) + 'px';
						}
					}
				}
			}
		} catch(e) {
			DEBUG&&console.log('updateImages exception: ' + e);			
		}	
	}
};

// PJS: moved from tdmain.js to term.js cause we use it in clearScreen()
Terminal.prototype.hasScrollingRegion = function () {
    return (this.scrollTop > 0 || this.scrollBottom < this.rows - 1);
};

/**
 * Character Sets
 */

Terminal.charsets = {};

// DEC Special Character and Line Drawing Set.
// http://vt100.net/docs/vt102-ug/table5-13.html
// A lot of curses apps use this if they see TERM=xterm.
// testing: echo -e '\e(0a\e(B'
// The xterm output sometimes seems to conflict with the
// reference above. xterm seems in line with the reference
// when running vttest however.
// The table below now uses xterm's output from vttest.
Terminal.charsets.SCLD = { // (0
  '`': '\u25c6', // '◆'
  'a': '\u2592', // '▒'
  'b': '\u0009', // '\t'
  'c': '\u000c', // '\f'
  'd': '\u000d', // '\r'
  'e': '\u000a', // '\n'
  'f': '\u00b0', // '°'
  'g': '\u00b1', // '±'
  'h': '\u2424', // '\u2424' (NL)
  'i': '\u000b', // '\v'
  'j': '\u2518', // '┘'
  'k': '\u2510', // '┐'
  'l': '\u250c', // '┌'
  'm': '\u2514', // '└'
  'n': '\u253c', // '┼'
  'o': '\u23ba', // '⎺'
  'p': '\u23bb', // '⎻'
  'q': '\u2500', // '─'
  'r': '\u23bc', // '⎼'
  's': '\u23bd', // '⎽'
  't': '\u251c', // '├'
  'u': '\u2524', // '┤'
  'v': '\u2534', // '┴'
  'w': '\u252c', // '┬'
  'x': '\u2502', // '│'
  'y': '\u2264', // '≤'
  'z': '\u2265', // '≥'
  '{': '\u03c0', // 'π'
  '|': '\u2260', // '≠'
  '}': '\u00a3', // '£'
  '~': '\u00b7'  // '·'
};

Terminal.charsets.UK = null; // (A
Terminal.charsets.US = null; // (B (USASCII)
Terminal.charsets.Dutch = null; // (4
Terminal.charsets.Finnish = null; // (C or (5
Terminal.charsets.French = null; // (R
Terminal.charsets.FrenchCanadian = null; // (Q
Terminal.charsets.German = null; // (K
Terminal.charsets.Italian = null; // (Y
Terminal.charsets.NorwegianDanish = null; // (E or (6
Terminal.charsets.Spanish = null; // (Z
Terminal.charsets.Swedish = null; // (H or (7
Terminal.charsets.Swiss = null; // (=
Terminal.charsets.ISOLatin = null; // /A
Terminal.charsets.MULTINATIONAL = null; // %5
Terminal.charsets.UPSS = null; // <  (PJS)
Terminal.charsets.DEFAULT = null; // ESC % @ (PJS)
Terminal.charsets.UTF8 = null; // ESC % G (PJS)

/**
 * Helpers
 */

function on(el, type, handler, capture) {
  el.addEventListener(type, handler, capture || false);
}

function off(el, type, handler, capture) {
  el.removeEventListener(type, handler, capture || false);
}

function cancel(ev) {
  if (ev.preventDefault) ev.preventDefault();
  ev.returnValue = false;
  if (ev.stopPropagation) ev.stopPropagation();
  ev.cancelBubble = true;
  return false;
}

function inherits(child, parent) {
  function f() {
    this.constructor = child;
  }
  f.prototype = parent.prototype;
  child.prototype = new f;
}

// if bold is broken, we can't
// use it in the terminal.
function isBoldBroken(document, fontName) {
  var body = document.getElementsByTagName('body')[0];
  var el = document.createElement('span');
  //PJS: added extra style properties: visibility & whiteSpace. Use offsetWidth instead of scrollWidth which returns zero when this function is called!
  el.style.visibility = 'hidden';
  el.style.whiteSpace = 'pre';
  el.style.fontFamily = fontName; //PJS: use same font as terminal when checking for broken bold!
  el.innerHTML = 'hello world';
  body.appendChild(el);
  //var w1 = el.scrollWidth;
  var w1 = el.offsetWidth;
  el.style.fontWeight = 'bold';
  //var w2 = el.scrollWidth;
  var w2 = el.offsetWidth;
  body.removeChild(el);
  return w1 !== w2;
}

var String = this.String;
var setTimeout = this.setTimeout;
var setInterval = this.setInterval;

function indexOf(obj, el) {
  var i = obj.length;
  while (i--) {
    if (obj[i] === el) return i;
  }
  return -1;
}

//PJS start AccuTerm mod

// A new version of isWide based on Marcus Kuhn's mk_wcwidth
function isWide(ch) {
  if (!ch || (ch < '\u1100')) {
    return false;
  }
  try {  
    var ucs = ch.charCodeAt(0);
    if (
        (ucs >= 0x1100 &&
         (ucs <= 0x115f ||                       // Hangul Jamo init. consonants
          ucs === 0x2329 || ucs === 0x232a ||
          (ucs >= 0x2e80 && ucs <= 0xa4cf &&
           ucs !== 0x303f) ||                     // CJK ... Yi
          (ucs >= 0xac00 && ucs <= 0xd7a3) ||    // Hangul Syllables
          (ucs >= 0xf900 && ucs <= 0xfaff) ||    // CJK Compatibility Ideographs
          (ucs >= 0xfe10 && ucs <= 0xfe19) ||    // Vertical forms
          (ucs >= 0xfe30 && ucs <= 0xfe6f) ||    // CJK Compatibility Forms
          (ucs >= 0xff00 && ucs <= 0xff60) ||    // Fullwidth Forms
          (ucs >= 0xffe0 && ucs <= 0xffe6) ||
          (ucs >= 0x20000 && ucs <= 0x2fffd) ||
          (ucs >= 0x30000 && ucs <= 0x3fffd)))
	  )
	  return true;
  } catch(e) {
  }
  return false;
}
//PJS - begin comment out original isWide function
/*
function isWide(ch) {
  if (ch <= '\uff00') return false;
  return (ch >= '\uff01' && ch <= '\uffbe')
      || (ch >= '\uffc2' && ch <= '\uffc7')
      || (ch >= '\uffca' && ch <= '\uffcf')
      || (ch >= '\uffd2' && ch <= '\uffd7')
      || (ch >= '\uffda' && ch <= '\uffdc')
      || (ch >= '\uffe0' && ch <= '\uffe6')
      || (ch >= '\uffe8' && ch <= '\uffee');
}
*/
//PJS - end comment out original isWide function
//PJS end AccuTerm mod


function matchColor(r1, g1, b1) {
  var hash = (r1 << 16) | (g1 << 8) | b1;

  if (matchColor._cache[hash] != null) {
    return matchColor._cache[hash];
  }

  var ldiff = Infinity
    , li = -1
    , i = 0
    , c
    , r2
    , g2
    , b2
    , diff;

  for (; i < Terminal.vcolors.length; i++) {
    c = Terminal.vcolors[i];
    r2 = c[0];
    g2 = c[1];
    b2 = c[2];

    diff = matchColor.distance(r1, g1, b1, r2, g2, b2);

    if (diff === 0) {
      li = i;
      break;
    }

    if (diff < ldiff) {
      ldiff = diff;
      li = i;
    }
  }

  return matchColor._cache[hash] = li;
}

matchColor._cache = {};

// http://stackoverflow.com/questions/1633828
matchColor.distance = function(r1, g1, b1, r2, g2, b2) {
  return Math.pow(30 * (r1 - r2), 2)
    + Math.pow(59 * (g1 - g2), 2)
    + Math.pow(11 * (b1 - b2), 2);
};

function each(obj, iter, con) {
  if (obj.forEach) return obj.forEach(iter, con);
  for (var i = 0; i < obj.length; i++) {
    iter.call(con, obj[i], i, obj);
  }
}

function keys(obj) {
  if (Object.keys) return Object.keys(obj);
  var key, keys = [];
  for (key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      keys.push(key);
    }
  }
  return keys;
}

// PJS: added debounce & throttle utility functions
function debounce(func, delay) {
  var inDebounce = undefined;
  return function() {
    var context = this, args = arguments;
	if (inDebounce)
      clearTimeout(inDebounce);
    return inDebounce = setTimeout(function() {
      return func.apply(context, args);
    }, delay);
  };
}

function throttle(func, limit) {
  var inThrottle = false;
  return function() {
    var context = this, args = arguments;
    if (!inThrottle) {
      func.apply(context, args);
      inThrottle = true;
      return setTimeout(function() {
        return inThrottle = false;
      }, limit);
    }
  };
}

/**
 * Expose
 */

Terminal.EventEmitter = EventEmitter;
Terminal.inherits = inherits;
Terminal.on = on;
Terminal.off = off;
Terminal.cancel = cancel;
Terminal.isWide = isWide; //PJS

if (typeof module !== 'undefined') {
  //module.exports = Terminal;
  TerminalExport = Terminal;
} else {
  this.Terminal = Terminal;
}

if (window) {
  TerminalExport = Terminal;
}

}).call(function() {
  return this || (typeof window !== 'undefined' ? window : global);
}());

////////////////////////////////////////////////////////////
// Exports!
////////////////////////////////////////////////////////////

export { TerminalExport };